import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { uploadQuestionMedia, uploadQuestionAudio } from "../lib/mediaUpload";
import { canonicalCategory, categoryKey, dedupeCategories, loadLocalCategoryPrefs } from "../lib/categories";
import { ensureLiveGame, fetchLivePlayers, subscribeLivePlayers, upsertLivePlayer, removeLivePlayer, resetTestGame, endLiveGame } from "../lib/liveGame";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  Bot,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  Image,
  Link,
  List,
  Loader2,
  Lock,
  Maximize2,
  MessageSquare,
  MonitorPlay,
  MoreHorizontal,
  MoreVertical,
  Music,
  Palette,
  Pause,
  Pencil,
  Play,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Save,
  SlidersHorizontal,
  Sparkles,
  Tags,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Trash2,
  Trophy,
  Unlock,
  Upload,
  UserX,
  Users,
  Wifi,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { hostToolsStorageKey, loadHostSetupSettings, loadHostToolsSessionState, loadProfileValue, profileKeys, readCurrentProjectSessionId, resetHostToolsSessionState, saveHostSetupSettings, saveHostToolsSessionState, updateUserMetadata, writeCurrentProjectSessionId } from "../lib/profileState";
import { readHostStyleProfile, rememberStyleFeedback } from "../lib/hostStyleMemory";

const STORAGE_BASE = process.env.REACT_APP_SUPABASE_URL ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/` : "";
const PRODUCTION_HOSTNAMES = new Set(["quizcrafter.fun", "www.quizcrafter.fun", "quiz-crafter-foreverfun-bits-projects.vercel.app"]);
const POINTS_BY_TYPE = { true_false: 25, multiple_choice: 50, written: 100 };
const DEFAULT_BRANDING = { name: "Forever Fun Events", logoUrl: "/quiz-crafter-logo.svg", primaryColor: "#71E0DC", accentColor: "#AEB2EF", correctColor: "", optionColor: "#7C8496", lobbyTagline: "Let's get quizzical.", pausedMessage: "Going old-school tonight — hold onto your answer, the host's on the way!" };

const typeMeta = {
  true_false: { label: "True/False", short: "T/F", icon: CheckCircle, color: "text-[#71E0DC]" },
  multiple_choice: { label: "Multiple Choice", short: "MC", icon: List, color: "text-[#AEB2EF]" },
  written: { label: "Written", short: "Written", icon: MessageSquare, color: "text-emerald-400" },
};

const arrayConfig = [
  { key: "true_false_questions", type: "true_false" },
  { key: "multiple_choice_questions", type: "multiple_choice" },
  { key: "written_questions", type: "written" },
  { key: "picture_questions", type: "written" },
];

// CRA bakes REACT_APP_PUBLIC_SITE_URL in at build time from .env.production,
// which loads for every production-mode build -- and Vercel always builds in
// production mode, preview deployments included. So blindly preferring that
// env var here meant every join link/QR code ever generated, even from a
// branch preview, pointed players at the real quizcrafter.fun production
// site instead of back to wherever the host was actually running -- players
// were silently testing whatever code was live on production, never the
// preview build the host was on. Only use the canonical public URL when
// we're actually being served from production; otherwise stay on the
// current origin so a preview's players land back on that same preview.
const getPublicOrigin = () => {
  if (typeof window === "undefined") return process.env.REACT_APP_PUBLIC_SITE_URL ? process.env.REACT_APP_PUBLIC_SITE_URL.replace(/\/$/, "") : "";
  if (PRODUCTION_HOSTNAMES.has(window.location.hostname) && process.env.REACT_APP_PUBLIC_SITE_URL) {
    return process.env.REACT_APP_PUBLIC_SITE_URL.replace(/\/$/, "");
  }
  return window.location.origin;
};

// Preview deployments sit behind Vercel's Deployment Protection login wall,
// which blocks anonymous players scanning a QR code cold. Vercel's
// "Protection Bypass for Automation" secret, appended as a query param, lets
// a first request through and (with x-vercel-set-bypass-cookie) sets a
// cookie so the rest of the SPA session stays unlocked. Only relevant on a
// protected preview -- never applied on production, which isn't walled off.
const getProtectionBypassQuery = () => {
  const secret = process.env.REACT_APP_VERCEL_PROTECTION_BYPASS;
  if (!secret || typeof window === "undefined" || PRODUCTION_HOSTNAMES.has(window.location.hostname)) return "";
  return `&x-vercel-protection-bypass=${encodeURIComponent(secret)}&x-vercel-set-bypass-cookie=true`;
};

const MEDIA_PLACEHOLDERS = new Set(["question", "image", "picture", "photo", "media", "n/a", "na", "none", "null", "undefined"]);

const cleanMediaValue = (value) => {
  const text = String(value || "").trim();
  if (!text || MEDIA_PLACEHOLDERS.has(text.toLowerCase())) return "";
  return text;
};

const looksLikeMediaValue = (value) => {
  const text = cleanMediaValue(value);
  if (!text) return false;
  if (/^(data:image\/|https?:\/\/|\/)/i.test(text)) return true;
  if (/\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(text)) return true;
  if (/storage\/v1\/object|supabase|cloudinary|canva|drive\.google|dropbox|images?|photos?/i.test(text)) return true;
  return false;
};

const firstQuestionImageUrl = (question = {}) => {
  const candidates = [
    question.image_url,
    question.imageUrl,
    question.media_url,
    question.mediaUrl,
    question.question_image_url,
    question.questionImageUrl,
    question.picture_url,
    question.pictureUrl,
    question.photo_url,
    question.photoUrl,
    question.correct_answer_image,
    question.correctAnswerImage,
    question.answer_image,
    question.answerImage,
    question.reveal_image_url,
    question.revealImageUrl,
  ];
  return candidates.map(cleanMediaValue).find(looksLikeMediaValue) || "";
};

const buildStorageUrl = (path) => {
  const value = cleanMediaValue(path);
  if (!value) return "";
  if (value.startsWith("data:") || value.startsWith("http://") || value.startsWith("https://")) return value;
  if (!STORAGE_BASE) return value;
  return `${STORAGE_BASE}${value.replace(/^\/+/, "")}`;
};
const HOST_DEFAULT_BRANDING_KEY = "quiz-crafter-host-branding-defaults";
const metadataBrandingKey = "quiz_crafter_host_branding_defaults_v1";
const hostBrandingKey = (sessionId) => `quiz-crafter-host-branding-${sessionId}`;
const sanitizeHexColor = (value, fallback) => /^#[0-9a-f]{6}$/i.test(String(value || "")) ? value : fallback;
const normalizeBranding = (branding = {}) => {
  const source = branding && typeof branding === "object" ? branding : {};
  const logoUrl = String(source.logoUrl || "").trim();
  return {
    name: String(source.name || "").trim() || DEFAULT_BRANDING.name,
    logoUrl: logoUrl === "/forever-fun-logo.png" ? DEFAULT_BRANDING.logoUrl : logoUrl,
    primaryColor: sanitizeHexColor(source.primaryColor, DEFAULT_BRANDING.primaryColor),
    accentColor: sanitizeHexColor(source.accentColor, DEFAULT_BRANDING.accentColor),
    // Empty means "match the brand primary color" -- correctColor only holds a
    // value once a host explicitly picks something other than the default.
    correctColor: /^#[0-9a-f]{6}$/i.test(String(source.correctColor || "")) ? source.correctColor : "",
    optionColor: sanitizeHexColor(source.optionColor, DEFAULT_BRANDING.optionColor),
    lobbyTagline: String(source.lobbyTagline || "").trim() || DEFAULT_BRANDING.lobbyTagline,
    pausedMessage: String(source.pausedMessage || "").trim() || DEFAULT_BRANDING.pausedMessage,
  };
};
const readDefaultBranding = () => {
  try {
    return normalizeBranding({ ...DEFAULT_BRANDING, ...JSON.parse(localStorage.getItem(HOST_DEFAULT_BRANDING_KEY) || "null") });
  } catch {
    return DEFAULT_BRANDING;
  }
};
const readSavedDefaultBranding = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(HOST_DEFAULT_BRANDING_KEY) || "null");
    return parsed && typeof parsed === "object" ? normalizeBranding(parsed) : null;
  } catch {
    return null;
  }
};
const writeDefaultBranding = (branding) => { try { localStorage.setItem(HOST_DEFAULT_BRANDING_KEY, JSON.stringify(normalizeBranding(branding))); } catch { /* Ignore storage failures so branding never crashes hosting. */ } };
const brandingChanged = (left, right) => JSON.stringify(normalizeBranding(left || {})) !== JSON.stringify(normalizeBranding(right || {}));
const loadDefaultBrandingFromProfile = async () => {
  const setupSettings = await loadHostSetupSettings().catch(() => ({}));
  if (setupSettings.branding && typeof setupSettings.branding === "object") return normalizeBranding(setupSettings.branding);
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  const remoteBranding = data?.session?.user?.user_metadata?.[metadataBrandingKey];
  return remoteBranding && typeof remoteBranding === "object" ? normalizeBranding(remoteBranding) : null;
};
const saveDefaultBrandingToProfile = async (branding) => {
  const cleanBranding = normalizeBranding(branding);
  await updateUserMetadata({ [metadataBrandingKey]: cleanBranding });
  await saveHostSetupSettings({ branding: cleanBranding });
  return cleanBranding;
};
const getSessionBranding = (session) => {
  const defaults = readDefaultBranding();
  return {
    ...defaults,
    name: session?.host_brand_name || session?.brand_name || session?.company_name || session?.venue_name || defaults.name,
    logoUrl: session?.host_logo_url || session?.brand_logo_url || session?.logo_url || defaults.logoUrl,
    primaryColor: sanitizeHexColor(session?.host_primary_color || session?.primary_color, defaults.primaryColor),
    accentColor: sanitizeHexColor(session?.host_accent_color || session?.accent_color, defaults.accentColor),
  };
};
const readStoredBranding = (sessionId, session = null) => {
  try {
    const stored = JSON.parse(localStorage.getItem(hostBrandingKey(sessionId)) || "null");
    return stored ? normalizeBranding({ ...getSessionBranding(session), ...stored }) : getSessionBranding(session);
  } catch {
    return getSessionBranding(session);
  }
};
const writeStoredBranding = (sessionId, branding) => { try { localStorage.setItem(hostBrandingKey(sessionId), JSON.stringify(normalizeBranding(branding))); } catch { /* Ignore storage failures so branding never crashes hosting. */ } };

const parseOptions = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => (typeof item === "string" ? item : item?.text)).filter(Boolean);
  const raw = String(value).trim();
  if (!raw) return [];
  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((item) => (typeof item === "string" ? item : item?.text)).filter(Boolean);
    } catch {
      // Fall through to semicolon parsing.
    }
  }
  return raw.split(";").map((item) => item.trim()).filter(Boolean);
};

const optionKey = (value) => String(value || "").trim().toLowerCase();
const seededSortValue = (seed, value) => {
  const text = `${seed}|${value}`;
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  return Math.abs(hash);
};
const buildAnswerOptions = (question, questionType) => {
  if (questionType === "true_false") return ["True", "False"];
  if (questionType !== "multiple_choice") return parseOptions(question.incorrect_answers || question.options);
  const answer = question.correct_answer || question.answer || "";
  const options = [answer, ...parseOptions(question.incorrect_answers || question.options)].filter(Boolean);
  const seen = new Set();
  const uniqueOptions = options.filter((option) => {
    const key = optionKey(option);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const seed = `${question.question_text || question.question || ""}|${answer}`;
  return uniqueOptions.sort((a, b) => seededSortValue(seed, a) - seededSortValue(seed, b));
};

const normalizeType = (question, fallbackType = "written") => {
  const type = question?.question_type || fallbackType;
  if (type === "true_false" || type === "multiple_choice" || type === "written") return type;
  return "written";
};

const getRoundOrder = (question, fallbackOrder = 1) => Number(question?.round_order || question?.round_number || question?.round || fallbackOrder) || fallbackOrder;
const getSourceOrder = (question, fallbackOrder = 1) => Number(question?.import_order || question?.source_order || question?.question_order || question?.order || fallbackOrder) || fallbackOrder;
// After-answer is the default going forward -- only a question explicitly
// saved with "before_answer" keeps that behavior; anything unset (including
// every question saved before this default flip) normalizes to after_answer.
const normalizeWagerTiming = (value) => value === "before_answer" || value === "before" ? "before_answer" : "after_answer";
// Headcounts used to be a single number; treat an old plain number as the
// adults count so a session tracked before the adults/kids split isn't lost.
const normalizeHeadcount = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return { adults: value.adults ?? "", kids: value.kids ?? "" };
  if (value === undefined || value === null || value === "") return { adults: "", kids: "" };
  return { adults: value, kids: "" };
};
const normalizeImageTiming = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (["after", "after_answer", "post", "reveal"].includes(normalized)) return "after_answer";
  return "initial";
};
const getRoundName = (question, fallbackOrder = 1) => {
  if (question?.round_name) return question.round_name;
  if (question?.round_title) return question.round_title;
  if (question?.round && Number.isNaN(Number(question.round))) return question.round;
  return `Round ${getRoundOrder(question, fallbackOrder)}`;
};

const getRoundMetadata = (session) => {
  const raw = session?.round_descriptions || session?.rounds_metadata || session?.rounds || [];
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return Object.values(raw);
  return [];
};

const getRoundDescription = (session, roundOrder, roundName) => {
  const metadata = getRoundMetadata(session);
  const match = metadata.find((round) => Number(round.order || round.round_order) === Number(roundOrder) || String(round.name || round.round_name || "").toLowerCase() === String(roundName || "").toLowerCase());
  return match?.description || match?.round_description || "";
};

const flattenSession = (session) => {
  const entries = arrayConfig.flatMap(({ key, type }) => {
    const questions = Array.isArray(session?.[key]) ? session[key] : [];
    return questions.map((question, index) => {
      const questionType = normalizeType(question, type);
      const roundOrder = getRoundOrder(question, 1);
      return {
        id: `${key}-${index}`,
        category: question.category || "General",
        questionText: question.question_text || question.question || "",
        answer: question.correct_answer || question.answer || "",
        funFact: question.fun_fact || "",
        imageUrl: firstQuestionImageUrl(question),
        imageTiming: normalizeImageTiming(question.image_timing || question.image_display_timing || question.media_timing || question.mediaTiming),
        audioUrl: question.audio_url || question.audioUrl || "",
        options: buildAnswerOptions(question, questionType),
        type: questionType,
        roundName: getRoundName(question, roundOrder),
        roundOrder,
        sourceOrder: getSourceOrder(question, index + 1),
        roundDescription: question.round_description || getRoundDescription(session, roundOrder, getRoundName(question, roundOrder)),
        points: question.points ?? question.question_points ?? null,
        timerSeconds: Number(question.timer_seconds ?? question.time_limit ?? 30) || 30,
        wagerLimit: Number(question.wager_limit ?? question.free_wager_limit ?? 0) || 0,
        wagerTiming: normalizeWagerTiming(question.wager_timing || question.wagerTiming),
      };
    });
  });

  return entries.sort((a, b) => a.roundOrder !== b.roundOrder ? a.roundOrder - b.roundOrder : a.sourceOrder - b.sourceOrder);
};

const makeRounds = (questions) => {
  const groups = new Map();
  questions.forEach((question, index) => {
    const key = `${question.roundOrder}-${question.roundName}`;
    if (!groups.has(key)) groups.set(key, { key, name: question.roundName, description: question.roundDescription || "", startIndex: index, questions: [], roundOrder: question.roundOrder });
    groups.get(key).questions.push(question);
  });
  return [...groups.values()];
};

// "I don't like to use the same category more than once in a round" -- shared
// by the Write Question/Add Round forms (QuestionDraftFields) and the
// existing-question Edit form (QuestionEditFields) so both a manually-typed
// category and an AI draft's category can be checked against what's already
// on the board. `inRound` gates the hard stuff (AI generation exclusion,
// cycleCategory avoidance); `elsewhere` is purely informational, since a
// category repeating in a *different* round is fine -- the host only asked
// about repeats within the same round, but said they want to see any-round
// usage while writing.
// inRound maps categoryKey -> the category's actual display text (not just
// the key) so callers that feed it to the AI (excludeCategories) can show
// the model a readable category name rather than a stripped-down key.
const buildCategoryUsage = (questions, { roundKey = null, excludeQuestionId = null } = {}) => {
  const inRound = new Map();
  const elsewhere = new Map();
  questions.forEach((question) => {
    if (excludeQuestionId && question.id === excludeQuestionId) return;
    const key = categoryKey(question.category);
    if (!key) return;
    const questionRoundKey = `${question.roundOrder}-${question.roundName}`;
    if (roundKey && questionRoundKey === roundKey) {
      if (!inRound.has(key)) inRound.set(key, question.category);
    } else {
      if (!elsewhere.has(key)) elsewhere.set(key, new Set());
      elsewhere.get(key).add(question.roundName);
    }
  });
  return { inRound, elsewhere };
};

// Small inline hint under a Category input -- an amber warning when the
// typed/drafted category is already used in the round this question belongs
// to (what the host said they want to avoid), a quieter note when it's only
// used in a different round (informational, not a problem).
const CategoryUsageHint = ({ category, usage }) => {
  const key = categoryKey(category);
  if (!key || !usage) return null;
  if (usage.inRound?.has(key)) return <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-400"><AlertTriangle size={12} />Already used in this round</p>;
  const elsewhereRounds = usage.elsewhere?.get(key);
  if (elsewhereRounds?.size) return <p className="mt-1 text-xs text-zinc-500">Already used in {[...elsewhereRounds].join(", ")}</p>;
  return null;
};

const readStoredLeaderboard = (sessionId) => {
  try {
    return JSON.parse(localStorage.getItem(`quiz-crafter-leaderboard-${sessionId}`) || "[]");
  } catch {
    return [];
  }
};
const readStoredList = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const readStoredObject = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};
const writeStoredList = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Live hosting should keep running even if browser storage is unavailable.
  }
};
const writeStoredObject = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value && typeof value === "object" ? value : {}));
  } catch {
    // Live hosting should keep running even if browser storage is unavailable.
  }
};
const persistLiveVote = (sessionId, name, payload, makeKey, isTestRun) => {
  const key = hostToolsStorageKey(sessionId, name);
  const current = readStoredList(key);
  const next = [...current.filter((item) => makeKey(item) !== makeKey(payload)), payload];
  writeStoredList(key, next);
  if (!isTestRun) saveHostToolsSessionState(sessionId, { [name === "category-feedback" ? "categoryFeedback" : name]: next }).catch((error) => console.warn("Host tools profile save unavailable:", error));
  return next;
};
const persistLiveIdea = (sessionId, payload, isTestRun) => {
  const key = hostToolsStorageKey(sessionId, "ideas");
  const current = readStoredList(key);
  const next = [payload, ...current.filter((item) => !(item.playerId === payload.playerId && item.submittedAt === payload.submittedAt))].slice(0, 200);
  writeStoredList(key, next);
  if (!isTestRun) saveHostToolsSessionState(sessionId, { ideas: next }).catch((error) => console.warn("Host tools profile save unavailable:", error));
  return next;
};
const liveEventKey = (event, payload = {}) => payload.eventId || payload.id || [
  event,
  payload.playerId || "player",
  payload.questionIndex ?? payload.roundKey ?? payload.category ?? "session",
  payload.questionId || "",
  payload.submittedAt || payload.updatedAt || payload.joinedAt || "",
].join(":");

const getDefaultPoints = (question) => POINTS_BY_TYPE[question?.type] || 100;
const getQuestionPoints = (question) => Number(question?.points ?? 0) > 0 ? Number(question.points) : getDefaultPoints(question);
const isBonusQuestion = (question) => String(question?.category || "").trim().toUpperCase() === "BONUS";
const answerKey = (answer) => `${answer.playerId}-${answer.questionIndex}`;
const normalizeLivePlayerRow = (row) => ({ id: row.id, name: row.name || "Team", score: Number(row.score || 0), joinedAt: row.joined_at || row.joinedAt });
// live_game_players rows load/arrive asynchronously and independently of the
// local players/leaderboard state -- overwriting wholesale on every fetch or
// realtime tick would erase host-only fields (updatePreference, etc.) and
// race with a host's just-made edit. Merge by id instead.
const mergeLeaderboardTeams = (current, incoming) => {
  if (!incoming.length) return current;
  const byId = new Map(current.map((team) => [team.id, team]));
  incoming.forEach((team) => {
    const existing = byId.get(team.id);
    byId.set(team.id, { ...existing, ...team, score: Number(team.score ?? existing?.score ?? 0) });
  });
  return [...byId.values()];
};
const mergePlayerProfiles = (current, incoming) => {
  if (!incoming.length) return current;
  const byId = new Map(current.map((player) => [player.id, player]));
  incoming.forEach((player) => {
    const existing = byId.get(player.id);
    byId.set(player.id, { ...existing, ...player, updatePreference: existing?.updatePreference || "none", updateContact: existing?.updateContact || "" });
  });
  return [...byId.values()];
};
const normalizeAnswerText = (value) => String(value || "").trim().toLowerCase().replace(/[’']/g, "'").replace(/[^a-z0-9]+/g, " ").trim();
const isCorrectSubmission = (answer, question) => Boolean(question?.answer) && normalizeAnswerText(answer?.answer) === normalizeAnswerText(question.answer);
const serializeRoundIntro = (round) => {
  if (!round) return null;
  const firstQuestion = round.questions?.[0];
  // An empty round (createEmptyRound) has no first question to read type/
  // points/timer off of -- fall back to the defaults stored with the round
  // itself, so its Round Intro still carries them.
  const uniformType = firstQuestion
    ? (round.questions.every((question) => question.type === firstQuestion.type) ? firstQuestion.type : null)
    : (round.questionType && round.questionType !== "mixed" ? round.questionType : null);
  return {
    key: round.key,
    name: round.name,
    description: round.description || "",
    categories: [...new Set((round.questions || []).map((question) => question.category).filter(Boolean))],
    questionCount: round.questions?.length || 0,
    startIndex: round.startIndex,
    questionType: uniformType,
    points: firstQuestion ? getQuestionPoints(firstQuestion) : (round.points ?? null),
    timerSeconds: firstQuestion?.timerSeconds || round.timerSeconds || null,
  };
};
const getTeamScore = (leaderboard, teamId) => Number(leaderboard.find((team) => team.id === teamId)?.score || 0);
const isLateSubmission = (answer) => answer?.secondsRemainingAtSubmit !== null && answer?.secondsRemainingAtSubmit !== undefined && Number(answer.secondsRemainingAtSubmit) <= 5;
const hasSubmittedWager = (answer) => Boolean(answer?.wagerMode) && (answer?.wagerSubmitted === true || answer?.wagerTiming !== "after_answer" || Number(answer?.wagerAmount || 0) > 0);
// There is no host-configured wager ceiling anymore -- wagering is a plain
// on/off switch per question (wagerLimit > 0 just means "on"), and the only
// real cap is whatever the team actually had to stake, snapshotted at
// submit time (wagerCap/scoreAtWager) and re-derived here from the
// leaderboard as a fallback.
const getWagerAward = (answer, leaderboard, previousPoints = 0) => {
  const requested = Math.max(0, Number(answer?.wagerAmount || 0));
  if (!requested) return 0;
  const currentScore = getTeamScore(leaderboard, answer.playerId);
  const scoreAtWager = Number.isFinite(Number(answer?.scoreAtWager))
    ? Math.max(0, Number(answer.scoreAtWager))
    : Math.max(0, currentScore - Number(previousPoints || 0));
  const submittedCap = Number.isFinite(Number(answer?.wagerCap)) ? Number(answer.wagerCap) : Number.POSITIVE_INFINITY;
  return Math.min(requested, submittedCap, scoreAtWager);
};
const buildFairPlayStats = (players, answers, gradedAnswers) => {
  const teamIds = new Set([...players.map((player) => player.id), ...answers.map((answer) => answer.playerId)]);
  const byTeam = new Map([...teamIds].map((teamId) => [teamId, { lateCorrect: 0, correctStreak: 0, longestCorrectStreak: 0, correct: 0, graded: 0, flags: [] }]));
  [...byTeam.keys()].forEach((teamId) => {
    const teamAnswers = answers.filter((answer) => answer.playerId === teamId).sort((a, b) => Number(a.questionIndex || 0) - Number(b.questionIndex || 0));
    let streak = 0;
    teamAnswers.forEach((answer) => {
      const graded = gradedAnswers[answerKey(answer)];
      if (!graded?.status) return;
      const stats = byTeam.get(teamId);
      stats.graded += 1;
      if (graded.status === "correct") {
        stats.correct += 1;
        streak += 1;
        stats.correctStreak = streak;
        stats.longestCorrectStreak = Math.max(stats.longestCorrectStreak, streak);
        if (isLateSubmission(answer)) stats.lateCorrect += 1;
      } else {
        streak = 0;
        stats.correctStreak = 0;
      }
    });
  });
  byTeam.forEach((stats) => {
    if (stats.lateCorrect >= 2) stats.flags.push(`${stats.lateCorrect} late correct`);
    if (stats.correctStreak >= 4) stats.flags.push(`${stats.correctStreak} correct in a row`);
  });
  return byTeam;
};

// Realtime broadcast.send() resolves "ok"/"timed out"/"error" -- previously
// nothing checked that, so a transient Supabase realtime blip (seen live:
// a ~30s window of broadcast 400s during an event) silently dropped a
// question/reveal/timer update with no retry. The presentation screen and
// phones would then sit on stale state until the 8s REST fallback poll
// caught up, surfacing as a jarring jump/flash rather than a smooth update.
const sendLiveBroadcast = (channel, message, attempt = 0) => {
  if (!channel) return;
  channel.send(message).then((status) => {
    if (status !== "ok" && attempt < 2) {
      window.setTimeout(() => sendLiveBroadcast(channel, message, attempt + 1), 300 * (attempt + 1));
    }
  });
};

// sessionIdProp/onEditBuild let this render embedded inside EventWorkspace.jsx's
// merged popout (id passed as a prop, the Emergency panel's "editBuild" link
// flips the popout's own mode back to the editor instead of navigating to a
// different page) while leaving the standalone /host-session/:id route's
// behavior -- reading the id from the URL, navigating to /build/:id --
// completely unchanged.
const HostSession = ({ sessionIdProp, onEditBuild, initialEventOpen = true } = {}) => {
  const { id: idParam } = useParams();
  const id = sessionIdProp || idParam;
  const embedded = Boolean(sessionIdProp);
  // Whether the host has actually opened this event yet. Defaults to true
  // (today's always-on behavior) at the standalone /host-session/:id route;
  // EventWorkspace passes an explicit value for the embedded, unified view,
  // where this component now mounts before the host is ready to go live so
  // they can review/arrange questions first. Gates ensureLiveGame and the
  // live-state broadcast/persist effect below -- without that gate, simply
  // opening the workspace to look at questions would silently create a
  // live_games row and start broadcasting live state before "Open Event".
  const [eventOpen, setEventOpen] = useState(initialEventOpen);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTestRun = searchParams.get("mode") === "test";
  // Test Run promises "teams and scores here won't count," so none of a
  // rehearsal's players/answers/feedback/etc. may reach the account-wide
  // profile cache (read by Host Hub from any device/session) or the
  // sessions row itself -- only this browser's own reload-resilience
  // localStorage mirror still updates, exactly as before.
  const persistHostToolsPatch = (patch, label) => {
    if (isTestRun) return;
    saveHostToolsSessionState(id, patch).catch((error) => console.warn(`${label} profile save unavailable:`, error));
  };
  const liveChannelRef = useRef(null);
  const liveStateRef = useRef(null);
  const liveStateSequenceRef = useRef(0);
  const presentReadyDebounceRef = useRef(null);
  const processedLiveEventsRef = useRef(new Set());
  const hostedResultsRef = useRef({});
  const liveStateSaveRef = useRef(0);
  const liveStateSaveKeyRef = useRef("");
  const liveRosterSyncKeyRef = useRef("");
  const [session, setSession] = useState(null);
  const [approvedCategories, setApprovedCategories] = useState([]);
  const [liveGameId, setLiveGameId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(null);
  const [emptyStateAddRoundOpen, setEmptyStateAddRoundOpen] = useState(false);
  const [emptyStateWriteRoundKey, setEmptyStateWriteRoundKey] = useState(null);
  const [emptyStateGenerateRoundKey, setEmptyStateGenerateRoundKey] = useState(null);
  const [emptyStateManageRoundsOpen, setEmptyStateManageRoundsOpen] = useState(false);
  const [emergencyOverride, setEmergencyOverride] = useState(null);
  const [generatedEmergency, setGeneratedEmergency] = useState(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [disputeNotes, setDisputeNotes] = useState(() => readStoredList(hostToolsStorageKey(id, "disputes")));
  const [showAnswer, setShowAnswer] = useState(false);
  const [showFunFact, setShowFunFact] = useState(false);
  // Audio clips play from the host's own device (speakers/PA hooked up to
  // this machine), not broadcast to the presentation screen or players --
  // so this is purely local playback state, no live-state/broadcast plumbing.
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef(null);
  // Session-wide switch, not tied to any one question -- lets the host
  // collect answers manually (verbally, on paper) for as long as they want
  // instead of via phone, without touching join, feedback, or idea
  // submission on phones, which don't gate on this at all. Restored from
  // hosted_results.liveState on load so it survives a host page reload.
  const [answersPaused, setAnswersPaused] = useState(false);
  const [presentMode, setPresentMode] = useState("categories");
  const [introRoundKey, setIntroRoundKey] = useState(null);
  const [pendingBonusIndex, setPendingBonusIndex] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [leaderboard, setLeaderboard] = useState(() => readStoredLeaderboard(id));
  const [teamName, setTeamName] = useState("");
  const [teamScore, setTeamScore] = useState("");
  const [players, setPlayers] = useState(() => readStoredList(hostToolsStorageKey(id, "players")));
  const [answers, setAnswers] = useState(() => readStoredList(hostToolsStorageKey(id, "answers")));
  const [playerActivity, setPlayerActivity] = useState(() => readStoredList(hostToolsStorageKey(id, "activity")));
  const [feedback, setFeedback] = useState(() => readStoredList(hostToolsStorageKey(id, "feedback")));
  const [categoryFeedback, setCategoryFeedback] = useState(() => readStoredList(hostToolsStorageKey(id, "category-feedback")));
  const [playerIdeas, setPlayerIdeas] = useState(() => readStoredList(hostToolsStorageKey(id, "ideas")));
  const [gradedAnswers, setGradedAnswers] = useState(() => readStoredObject(hostToolsStorageKey(id, "graded-answers")));
  const [scoreModal, setScoreModal] = useState(null);
  const [liveStatus, setLiveStatus] = useState("connecting");
  const [hostStateLoaded, setHostStateLoaded] = useState(false);
  const [pointsPerQuestion, setPointsPerQuestion] = useState(25);
  const [wagerMode, setWagerMode] = useState(false);
  const [wagerLimit, setWagerLimit] = useState(0);
  const [wagerTiming, setWagerTiming] = useState("after_answer");
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [timerEndAt, setTimerEndAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [branding, setBranding] = useState(() => readStoredBranding(id));
  const [attendance, setAttendance] = useState({ nonPlayers: { adults: "", kids: "" }, teamHeadcounts: {} });
  const attendanceLoadedRef = useRef(false);
  const [topbarCollapsed, setTopbarCollapsed] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null);

  // player_join/answer_submit broadcasts are the only place a brand-new
  // team's local state gets created -- previously they only touched
  // players/leaderboard, so if the live_games row wasn't ready yet (or the
  // roster's own realtime sync lagged), that team's live_game_players row
  // never got created and the leaderboard/player device never saw them.
  // Route both events through this so the server-side roster gets the same
  // upsert the local state does.
  const ensureTeamFromPayload = useCallback((payload) => {
    if (!payload?.playerId) return;
    const name = payload.playerName || "Team";
    const joinedAt = payload.joinedAt || payload.submittedAt || new Date().toISOString();
    setPlayers((current) => {
      const nextPlayer = {
        id: payload.playerId,
        name,
        updatePreference: payload.updatePreference || current.find((player) => player.id === payload.playerId)?.updatePreference || "none",
        updateContact: payload.updateContact || current.find((player) => player.id === payload.playerId)?.updateContact || "",
        joinedAt,
      };
      const next = current.some((player) => player.id === payload.playerId)
        ? current.map((player) => player.id === payload.playerId ? { ...player, ...nextPlayer } : player)
        : [...current, nextPlayer];
      persistHostToolsPatch({ players: next }, "Players");
      return next;
    });
    setLeaderboard((teams) => teams.some((team) => team.id === payload.playerId)
      ? teams.map((team) => team.id === payload.playerId ? { ...team, name: name || team.name || "Team" } : team)
      : [...teams, { id: payload.playerId, name, score: 0 }]);
    if (liveGameId) upsertLivePlayer(liveGameId, { id: payload.playerId, name }).catch((error) => console.warn("Live roster save unavailable:", error));
  }, [id, liveGameId]);

  const applyLivePlayerEvent = useCallback((event, payload) => {
    if (!payload || typeof payload !== "object") return;
    const key = liveEventKey(event, payload);
    if (processedLiveEventsRef.current.has(key)) return;
    processedLiveEventsRef.current.add(key);
    if (processedLiveEventsRef.current.size > 1600) processedLiveEventsRef.current = new Set([...processedLiveEventsRef.current].slice(-1000));

    if (event === "player_join") {
      if (!payload.playerId) return;
      ensureTeamFromPayload(payload);
      return;
    }

    if (event === "player_rename") {
      if (!payload.playerId || !payload.playerName) return;
      setPlayers((current) => {
        const next = current.map((player) => player.id === payload.playerId ? { ...player, name: payload.playerName } : player);
        persistHostToolsPatch({ players: next }, "Players");
        return next;
      });
      setLeaderboard((teams) => teams.map((team) => team.id === payload.playerId ? { ...team, name: payload.playerName } : team));
      setAnswers((current) => {
        const next = current.map((answer) => answer.playerId === payload.playerId ? { ...answer, playerName: payload.playerName } : answer);
        writeStoredList(hostToolsStorageKey(id, "answers"), next);
        persistHostToolsPatch({ answers: next }, "Answers");
        return next;
      });
      return;
    }

    if (event === "answer_submit") {
      if (!payload.playerId) return;
      ensureTeamFromPayload(payload);
      setAnswers((current) => {
        const filtered = current.filter((answer) => !(answer.playerId === payload.playerId && answer.questionIndex === payload.questionIndex));
        const next = [...filtered, payload].slice(-800);
        writeStoredList(hostToolsStorageKey(id, "answers"), next);
        persistHostToolsPatch({ answers: next }, "Answers");
        return next;
      });
      return;
    }

    if (event === "player_activity") {
      if (!payload.playerId || payload.questionIndex === undefined) return;
      setPlayerActivity((current) => {
        const next = [...current, payload].slice(-800);
        writeStoredList(hostToolsStorageKey(id, "activity"), next);
        persistHostToolsPatch({ activity: next }, "Activity");
        return next;
      });
      return;
    }

    if (event === "feedback_submit") {
      if (!payload.playerId || payload.questionIndex === undefined) return;
      setFeedback(() => persistLiveVote(id, "feedback", payload, (item) => `${item.playerId}-${item.questionIndex}`, isTestRun));
      supabase.from("session_question_feedback").upsert({
        session_id: id,
        player_id: payload.playerId,
        player_name: payload.playerName || "",
        sentiment: payload.sentiment,
        question_index: payload.questionIndex,
        question_id: payload.questionId || null,
        question_text: payload.questionText || "",
        category: payload.category || "",
        round_name: payload.roundName || "",
        submitted_at: payload.submittedAt || new Date().toISOString(),
      }, { onConflict: "session_id,player_id,question_index" }).then(({ error }) => { if (error) console.warn("Question feedback save unavailable:", error); });
      return;
    }

    if (event === "category_feedback_submit") {
      if (!payload.playerId || !payload.category) return;
      setCategoryFeedback(() => persistLiveVote(id, "category-feedback", payload, (item) => `${item.playerId}-${item.roundKey || item.roundName}-${item.category}`, isTestRun));
      supabase.from("session_category_feedback").upsert({
        session_id: id,
        player_id: payload.playerId,
        player_name: payload.playerName || "",
        sentiment: payload.sentiment,
        category: payload.category,
        round_key: payload.roundKey || payload.roundName || "",
        round_name: payload.roundName || "",
        submitted_at: payload.submittedAt || new Date().toISOString(),
      }, { onConflict: "session_id,player_id,round_key,category" }).then(({ error }) => { if (error) console.warn("Category feedback save unavailable:", error); });
      return;
    }

    if (event === "idea_submit") {
      if (!payload.playerId) return;
      setPlayerIdeas(() => persistLiveIdea(id, payload, isTestRun));
      supabase.from("session_player_ideas").insert({
        session_id: id,
        player_id: payload.playerId,
        player_name: payload.playerName || "",
        category: payload.category || "",
        question: payload.question || "",
        submitted_at: payload.submittedAt || new Date().toISOString(),
      }).then(({ error }) => { if (error) console.warn("Player idea save unavailable:", error); });
    }
  }, [ensureTeamFromPayload, id]);
  const applyStoredLiveEvents = useCallback((results) => {
    const events = Array.isArray(results?.liveEvents) ? results.liveEvents : [];
    events.forEach((record) => applyLivePlayerEvent(record?.event, record?.payload));
  }, [applyLivePlayerEvent]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(interval);
  }, []);

  // Same "approved categories" the standalone builder page cycles through --
  // the host's saved approve/reject prefs (quiz-crafter-category-preferences,
  // shared via lib/categories so approving one from either screen shows up
  // in both) layered onto every category actually seen in the question
  // library, minus anything rejected or removed (a removed category is a
  // deleted stray duplicate, not one the host dislikes, but it still
  // shouldn't resurface here). Fetched once; a category approved
  // mid-event elsewhere won't retroactively appear here without a reload,
  // same tradeoff BuildSession already accepts.
  useEffect(() => {
    let cancelled = false;
    supabase.from("questions").select("category").then(({ data, error }) => {
      if (cancelled) return;
      if (error) { console.warn("Category list unavailable:", error); return; }
      const prefs = loadLocalCategoryPrefs();
      const excludedKeys = new Set([...prefs.rejected, ...prefs.removed].map(categoryKey));
      const approved = new Set(prefs.approved.filter((category) => !excludedKeys.has(categoryKey(category))));
      (data || []).forEach((row) => {
        const category = canonicalCategory(row.category, [...approved]);
        if (category && !excludedKeys.has(categoryKey(category))) approved.add(category);
      });
      setApprovedCategories(dedupeCategories([...approved]).sort((a, b) => a.localeCompare(b)));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const loadSession = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.from("sessions").select("*").eq("id", id).single();
        if (error) throw error;
        setSession(data);
        hostedResultsRef.current = data?.hosted_results && typeof data.hosted_results === "object" && !Array.isArray(data.hosted_results) ? data.hosted_results : {};
        liveStateSequenceRef.current = Math.max(liveStateSequenceRef.current, Number(hostedResultsRef.current?.liveState?.liveSequence || 0));
        // A session-wide setting, not tied to any one question -- restore it
        // here so a host reload mid-event doesn't silently flip phones back
        // on (or off) behind the host's back.
        if (typeof hostedResultsRef.current?.liveState?.answersPaused === "boolean") setAnswersPaused(hostedResultsRef.current.liveState.answersPaused);
        // Also restored here, not just in the endedAt-only branch below --
        // without this, a host reloading mid-event (still on question 1,
        // say) would see gameStarted reset to false and that question would
        // wrongly render as "not yet asked" again instead of picking the
        // live hosting view back up where the reload left off.
        if (typeof hostedResultsRef.current?.liveState?.gameStarted === "boolean") setGameStarted(hostedResultsRef.current.liveState.gameStarted);
        applyStoredLiveEvents(hostedResultsRef.current);
        setBranding(readStoredBranding(id, data));
        const savedTeamHeadcounts = data?.attendance?.teamHeadcounts && typeof data.attendance.teamHeadcounts === "object" ? data.attendance.teamHeadcounts : {};
        setAttendance({
          nonPlayers: normalizeHeadcount(data?.attendance?.nonPlayers ?? data?.attendance?.barHeadcount),
          teamHeadcounts: Object.fromEntries(Object.entries(savedTeamHeadcounts).map(([teamId, value]) => [teamId, normalizeHeadcount(value)])),
        });
        attendanceLoadedRef.current = true;
      } catch (error) {
        console.error("Host session load error:", error);
        toast.error(error.message || "Failed to load session");
        navigate("/past-sessions");
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [applyStoredLiveEvents, id, navigate]);

  useEffect(() => {
    const loadDurablePlayerEvents = async () => {
      try {
        const { data, error } = await supabase.from("sessions").select("hosted_results").eq("id", id).single();
        if (error) throw error;
        const results = data?.hosted_results && typeof data.hosted_results === "object" && !Array.isArray(data.hosted_results) ? data.hosted_results : {};
        hostedResultsRef.current = { ...hostedResultsRef.current, ...results };
        applyStoredLiveEvents(results);
      } catch (error) {
        console.warn("Live player event recovery unavailable:", error);
      }
    };
    loadDurablePlayerEvents();
    const interval = window.setInterval(loadDurablePlayerEvents, 2500);
    return () => window.clearInterval(interval);
  }, [applyStoredLiveEvents, id]);

  // A one-shot attempt here used to fail silently and permanently on any
  // transient error (a console.warn nobody sees) -- with no live_games row,
  // every downstream leaderboard/score write for the rest of the page's
  // lifetime silently no-ops, with no user-facing sign anything is wrong.
  // Retries with backoff, and surfaces a toast if it's still failing after
  // a few attempts instead of staying silent forever.
  useEffect(() => {
    if (!session || !eventOpen) return undefined;
    let cancelled = false;
    let timeoutId = null;
    let attempt = 0;
    const tryEnsure = () => {
      attempt += 1;
      ensureLiveGame(id, { sessionName: session?.name || session?.session_name || "Trivia Night", isTest: isTestRun })
        .then((game) => { if (!cancelled) setLiveGameId(game.id); })
        .catch((error) => {
          console.warn("Live game setup unavailable:", error);
          if (cancelled) return;
          if (attempt >= 4) { toast.error("Couldn't set up live scoring for this session -- reload the page to try again."); return; }
          timeoutId = window.setTimeout(tryEnsure, Math.min(2000 * 2 ** (attempt - 1), 15000));
        });
    };
    tryEnsure();
    return () => { cancelled = true; window.clearTimeout(timeoutId); };
  }, [id, session, isTestRun, eventOpen]);

  useEffect(() => {
    if (!liveGameId) return undefined;
    let cancelled = false;
    fetchLivePlayers(liveGameId)
      .then((rows) => {
        if (cancelled) return;
        const teams = rows.map(normalizeLivePlayerRow);
        setLeaderboard((current) => mergeLeaderboardTeams(current, teams));
        setPlayers((current) => mergePlayerProfiles(current, teams));
      })
      .catch((error) => console.warn("Live roster load unavailable:", error));
    const unsubscribe = subscribeLivePlayers(liveGameId, ({ eventType, new: newRow, old: oldRow }) => {
      setLeaderboard((current) => {
        if (eventType === "DELETE") return current.filter((team) => team.id !== oldRow.id);
        const nextTeam = normalizeLivePlayerRow(newRow);
        const exists = current.some((team) => team.id === nextTeam.id);
        return exists ? current.map((team) => (team.id === nextTeam.id ? nextTeam : team)) : [...current, nextTeam];
      });
      if (eventType !== "DELETE") setPlayers((current) => mergePlayerProfiles(current, [normalizeLivePlayerRow(newRow)]));
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [liveGameId]);

  // Bulk grading (mark-all-correct, undo, etc.) changes scores in local
  // leaderboard state without going through adjustScore/setScore, so it
  // never reached live_game_players -- the host screen showed the right
  // score but players' devices and the presentation leaderboard didn't.
  // Keyed by a content hash so this doesn't refire (and re-upsert every
  // row) on every unrelated render, only when a name or score actually
  // changes.
  useEffect(() => {
    if (!liveGameId || !leaderboard.length) return;
    const syncKey = leaderboard
      .map((team) => `${team.id}:${team.name || ""}:${Number(team.score || 0)}`)
      .sort()
      .join("|");
    if (syncKey === liveRosterSyncKeyRef.current) return;
    liveRosterSyncKeyRef.current = syncKey;
    leaderboard.forEach((team) => {
      const name = team.name || players.find((player) => player.id === team.id)?.name || "Team";
      upsertLivePlayer(liveGameId, { id: team.id, name, score: Number(team.score || 0) })
        .catch((error) => console.warn("Live roster score sync unavailable:", error));
    });
  }, [leaderboard, liveGameId, players]);

  useEffect(() => {
    const loadProfileHostState = async () => {
      try {
        const state = await loadHostToolsSessionState(id);
        const snapshot = state.results && typeof state.results === "object" ? { ...state, ...state.results } : state;
        if (Array.isArray(snapshot.players)) setPlayers(snapshot.players);
        if (Array.isArray(snapshot.answers)) setAnswers(snapshot.answers);
        if (Array.isArray(snapshot.activity)) setPlayerActivity(snapshot.activity);
        if (Array.isArray(snapshot.feedback)) setFeedback(snapshot.feedback);
        if (Array.isArray(snapshot.categoryFeedback)) setCategoryFeedback(snapshot.categoryFeedback);
        if (Array.isArray(snapshot.ideas)) setPlayerIdeas(snapshot.ideas);
        if (Array.isArray(snapshot.disputes)) setDisputeNotes(snapshot.disputes);
        if (snapshot.gradedAnswers && typeof snapshot.gradedAnswers === "object") setGradedAnswers(snapshot.gradedAnswers);
        if (Number.isFinite(Number(snapshot.currentIndex))) setCurrentIndex(Math.max(0, Number(snapshot.currentIndex)));
        if (snapshot.endedAt) {
          setGameStarted(true);
          setPresentMode(snapshot.presentMode || "winners");
        }
      } catch (error) {
        console.warn("Live host profile state unavailable:", error);
      } finally {
        setHostStateLoaded(true);
      }
    };
    loadProfileHostState();
  }, [id]);

  useEffect(() => {
    const loadProfileBranding = async () => {
      try {
        const defaultBranding = await loadDefaultBrandingFromProfile();
        const savedLocalBranding = readSavedDefaultBranding();
        if (defaultBranding) {
          writeDefaultBranding(defaultBranding);
        } else if (savedLocalBranding && brandingChanged(savedLocalBranding, DEFAULT_BRANDING)) {
          await saveDefaultBrandingToProfile(savedLocalBranding);
        } else {
          return;
        }
        const hasSessionOverride = !!localStorage.getItem(hostBrandingKey(id));
        if (!hasSessionOverride) setBranding(readStoredBranding(id, session));
      } catch (error) {
        console.warn("Host default branding profile sync unavailable:", error);
      }
    };
    loadProfileBranding();
  }, [id, session]);

  useEffect(() => {
    const channel = supabase.channel(`quiz-crafter-live-${id}`, { config: { broadcast: { self: false } } });
    liveChannelRef.current = channel;
    channel
      .on("broadcast", { event: "player_join" }, ({ payload }) => {
        applyLivePlayerEvent("player_join", payload);
      })
      .on("broadcast", { event: "player_rename" }, ({ payload }) => {
        applyLivePlayerEvent("player_rename", payload);
      })
      .on("broadcast", { event: "answer_submit" }, ({ payload }) => {
        applyLivePlayerEvent("answer_submit", payload);
      })
      .on("broadcast", { event: "player_activity" }, ({ payload }) => {
        applyLivePlayerEvent("player_activity", payload);
      })
      .on("broadcast", { event: "feedback_submit" }, ({ payload }) => {
        applyLivePlayerEvent("feedback_submit", payload);
      })
      .on("broadcast", { event: "category_feedback_submit" }, ({ payload }) => {
        applyLivePlayerEvent("category_feedback_submit", payload);
      })
      .on("broadcast", { event: "idea_submit" }, ({ payload }) => {
        applyLivePlayerEvent("idea_submit", payload);
      })
      .on("broadcast", { event: "present_ready" }, ({ payload }) => {
        // A presentation screen claiming itself as active (see PresentSession) --
        // the most recent claim always wins, which is what actually kicks an
        // older screen (a stray tab, a duplicate "Open Presentation" click) out
        // of the running instead of it continuing to redraw in parallel. Forcing
        // through persistLiveState bumps the sequence/timestamp so already-synced
        // screens don't ignore the change as a no-newer-than-current update.
        const claimedInstanceId = payload?.instanceId ? String(payload.instanceId) : null;
        if (claimedInstanceId && liveStateRef.current && claimedInstanceId !== liveStateRef.current.presentInstanceId) {
          persistLiveState({ ...liveStateRef.current, presentInstanceId: claimedInstanceId, updatedAt: new Date().toISOString() }, true);
          return;
        }
        // Every open presentation screen pings this on mount/reconnect with
        // no coalescing -- two screens open (or one screen reconnect-looping
        // from a throttled background tab) used to mean two-plus independent
        // full-state broadcasts fired back to back, right when the channel
        // was already unstable. Debounce so a burst of pings gets one reply.
        window.clearTimeout(presentReadyDebounceRef.current);
        presentReadyDebounceRef.current = window.setTimeout(() => {
          const state = liveStateRef.current;
          if (state) sendLiveBroadcast(liveChannelRef.current, { type: "broadcast", event: "host_state", payload: state });
        }, 400);
      })
      .subscribe((status) => {
        setLiveStatus(status === "SUBSCRIBED" ? "live" : "connecting");
        if (status === "SUBSCRIBED" && liveStateRef.current) {
          sendLiveBroadcast(liveChannelRef.current, { type: "broadcast", event: "host_state", payload: liveStateRef.current });
        }
      });
    return () => {
      window.clearTimeout(presentReadyDebounceRef.current);
      supabase.removeChannel(channel);
      liveChannelRef.current = null;
    };
  // persistLiveState is intentionally omitted -- it's redefined every render,
  // and this effect must only re-subscribe the channel on id changes, not on
  // every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyLivePlayerEvent, id]);

  const questions = useMemo(() => flattenSession(session), [session]);
  // A round can now exist before it has any questions -- "Create Round"
  // without writing a first question yet (createEmptyRound below) stores a
  // shell in session.round_descriptions (name/description/order plus the
  // question-type/points/timer defaults for whatever gets added to it
  // later). Once it has real questions, its identity moves onto them
  // (round_name/round_order, same as always) and this shell is just ignored
  // -- filtered out here because a real group with the same order+name
  // already exists, never deleted, since the fallback description lookup
  // still reads it (see getRoundDescription).
  const rounds = useMemo(() => {
    const built = makeRounds(questions);
    // A round_descriptions entry can go stale against a real round (e.g. an
    // older save wrote it, then the round was renamed or reordered here --
    // renameRound/moveRound only patch it for a round that's still empty).
    // Matching on order-or-name against every real round, not just the exact
    // pair, keeps a stale entry from resurfacing as a bogus duplicate round.
    const realNames = new Set(built.map((round) => round.name));
    const realOrders = new Set(built.map((round) => round.roundOrder));
    const emptyRounds = getRoundMetadata(session)
      .map((entry) => ({
        name: entry.name || entry.round_name || "",
        description: entry.description || entry.round_description || "",
        order: Number(entry.order ?? entry.round_order) || 0,
        questionType: entry.question_type || "mixed",
        points: entry.points ?? null,
        timerSeconds: Number(entry.timer_seconds) || 30,
      }))
      .filter((entry) => entry.name && !realNames.has(entry.name) && !realOrders.has(entry.order))
      .map((entry) => ({ key: `${entry.order}-${entry.name}`, name: entry.name, description: entry.description, startIndex: questions.length, questions: [], roundOrder: entry.order, isEmpty: true, questionType: entry.questionType, points: entry.points, timerSeconds: entry.timerSeconds }));
    return [...built, ...emptyRounds].sort((a, b) => a.roundOrder - b.roundOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, session?.round_descriptions]);
  const isReviewing = reviewIndex !== null;
  const hostIndex = isReviewing ? reviewIndex : currentIndex;
  // Stop and rewind whenever the displayed question changes -- there are
  // several separate navigation paths (next/prev, jump to question, review
  // mode) that all end up changing hostIndex, so hooking that one derived
  // value instead of each navigation function covers all of them without
  // leaving a clip silently playing in the background after the host has
  // moved on to a different question.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlayingAudio(false);
  }, [hostIndex]);
  const currentQuestion = questions[currentIndex] || null;
  const liveDisplayedQuestion = emergencyOverride || currentQuestion;
  const displayedQuestion = isReviewing ? questions[hostIndex] : liveDisplayedQuestion;
  const currentRound = rounds.find((round) => hostIndex >= round.startIndex && hostIndex < round.startIndex + round.questions.length);
  const liveRound = rounds.find((round) => currentIndex >= round.startIndex && currentIndex < round.startIndex + round.questions.length);
  const nextRound = rounds.find((round) => round.startIndex > currentIndex);
  const defaultIntroRound = liveRound && currentIndex >= liveRound.startIndex + liveRound.questions.length - 1 && nextRound ? nextRound : liveRound;
  const introRound = rounds.find((round) => round.key === (introRoundKey || defaultIntroRound?.key)) || defaultIntroRound || liveRound;
  const currentAnswers = answers.filter((answer) => answer.questionIndex === hostIndex).sort((a, b) => String(a.submittedAt).localeCompare(String(b.submittedAt)));
  const fairPlayStats = useMemo(() => buildFairPlayStats(players, answers, gradedAnswers), [players, answers, gradedAnswers]);
  const progress = questions.length ? Math.round(((currentIndex + 1) / questions.length) * 100) : 0;
  const sessionName = session?.name || session?.session_name || "Trivia Session";
  const joinUrl = `${getPublicOrigin()}/join?session=${id}${getProtectionBypassQuery()}`;
  const timeRemaining = timerEndAt ? Math.max(0, Math.ceil((timerEndAt - now) / 1000)) : null;
  const acceptingAnswers = !answersPaused && (timeRemaining === null || timeRemaining > 0);

  const persistLiveState = (state, force = false) => {
    liveStateSequenceRef.current = Math.max(liveStateSequenceRef.current + 1, Number(state.liveSequence || 0));
    const orderedState = { ...state, liveSequence: liveStateSequenceRef.current };
    liveStateRef.current = orderedState;
    localStorage.setItem(`quiz-crafter-present-state-${id}`, JSON.stringify(orderedState));
    sendLiveBroadcast(liveChannelRef.current, { type: "broadcast", event: "host_state", payload: orderedState });
    const nowMs = Date.now();
    const durableKey = [
      orderedState.liveSequence,
      orderedState.currentIndex,
      orderedState.mode,
      orderedState.pendingBonusIndex ?? "",
      orderedState.pointsPerQuestion,
      orderedState.timerSeconds,
      orderedState.wagerLimit,
      orderedState.wagerTiming,
      orderedState.timerEndAt || "",
      orderedState.showAnswer ? "answer" : "",
      orderedState.showFunFact ? "fact" : "",
      orderedState.answersPaused ? "paused" : "",
    ].join("|");
    if (force || durableKey !== liveStateSaveKeyRef.current || nowMs - liveStateSaveRef.current > 1200) {
      liveStateSaveKeyRef.current = durableKey;
      liveStateSaveRef.current = nowMs;
      // Keep the local ref current for the host's own bookkeeping (e.g. endSession
      // reads it), but DON'T write it back to the DB as a full-column overwrite --
      // hostedResultsRef.current is only refreshed every 2.5s by a polling interval,
      // so a naive `update({ hosted_results: hostedResults })` here (this fires on
      // nearly every host action) could clobber a player's liveEvents entry that
      // landed in the DB after this stale snapshot was taken, and could even revert
      // hosted_results.liveState itself if a stale write raced in after this one --
      // the actual mechanism behind presentation-screen flicker/desync. The
      // merge_host_live_state RPC does a single atomic UPDATE scoped to only
      // liveState/liveStateUpdatedAt, so it can never step on liveEvents.
      hostedResultsRef.current = { ...hostedResultsRef.current, liveState: orderedState, liveStateUpdatedAt: orderedState.updatedAt };
      if (!isTestRun) {
        supabase.rpc("merge_host_live_state", { p_session_id: id, p_live_state: orderedState }).then(({ error }) => {
          if (error) console.warn("Live presentation state save unavailable:", error);
        }).catch((error) => console.warn("Live presentation state save unavailable:", error));
      }
    }
  };

  const buildLiveState = ({
    question,
    index,
    mode,
    pendingIndex = pendingBonusIndex,
    showAnswerValue = showAnswer,
    showFunFactValue = showFunFact,
    timerEndAtValue = timerEndAt,
    gameStartedValue = gameStarted,
    pointsValue = pointsPerQuestion,
    timerValue = timerSeconds,
    wagerLimitValue = wagerLimit,
    wagerModeValue = wagerMode,
    wagerTimingValue = wagerTiming,
    answersPausedValue = answersPaused,
  }) => {
    if (!question) return null;
    const activePoints = Number(pointsValue) || getQuestionPoints(question);
    const activeTimer = Number(timerValue) || Number(question.timerSeconds || 0) || 30;
    const activeWagerLimit = Number(wagerLimitValue || 0);
    const activeRound = rounds.find((round) => index >= round.startIndex && index < round.startIndex + round.questions.length);
    return {
      sessionId: id,
      liveSequence: liveStateSequenceRef.current + 1,
      sessionName,
      mode,
      gameStarted: gameStartedValue,
      joinUrl,
      currentIndex: index,
      pendingBonusIndex: pendingIndex,
      pendingBonusRound: pendingIndex !== null ? serializeRoundIntro(rounds.find((round) => pendingIndex >= round.startIndex && pendingIndex < round.startIndex + round.questions.length)) : null,
      currentQuestion: {
        ...question,
        points: activePoints,
        questionPoints: activePoints,
        timerSeconds: activeTimer,
        wagerLimit: activeWagerLimit,
        wagerTiming: wagerTimingValue,
        isBonus: isBonusQuestion(question),
        answer: showAnswerValue ? question.answer : "",
      },
      introRound: serializeRoundIntro(mode === "categories" ? introRound : activeRound || introRound),
      showAnswer: showAnswerValue,
      revealedAnswer: showAnswerValue ? question.answer : "",
      showFunFact: showFunFactValue,
      pointsPerQuestion: activePoints,
      wagerMode: wagerModeValue,
      wagerLimit: activeWagerLimit,
      wagerTiming: wagerTimingValue,
      timerSeconds: activeTimer,
      timerEndAt: timerEndAtValue,
      answersPaused: answersPausedValue,
      acceptingAnswers: !answersPausedValue && (timerEndAtValue === null || timerEndAtValue > Date.now()),
      // Carried in the broadcast snapshot itself so player devices and the
      // presentation screen have a roster/score fallback that rides the
      // realtime WebSocket channel -- the same channel that kept working
      // throughout the live_game_players REST-path incidents -- instead of
      // depending solely on the live_game_players table being reachable.
      players,
      leaderboard: leaderboard.map((team) => ({ id: team.id, name: team.name || players.find((player) => player.id === team.id)?.name || "Team", score: Number(team.score || 0) })),
      branding,
      // Carried forward rather than dropped -- this is whichever presentation
      // screen last claimed itself active (see the present_ready handler
      // below); every ordinary state update needs to preserve it or the next
      // question advance would silently un-claim the current screen.
      presentInstanceId: liveStateRef.current?.presentInstanceId ?? null,
      updatedAt: new Date().toISOString(),
    };
  };

  useEffect(() => {
    if (!liveDisplayedQuestion || isReviewing) return;
    setPointsPerQuestion(getQuestionPoints(liveDisplayedQuestion));
    setTimerSeconds(Number(liveDisplayedQuestion.timerSeconds || 30));
    setWagerLimit(Number(liveDisplayedQuestion.wagerLimit || 0));
    setWagerTiming(normalizeWagerTiming(liveDisplayedQuestion.wagerTiming));
    setWagerMode(Number(liveDisplayedQuestion.wagerLimit || 0) > 0);
  }, [liveDisplayedQuestion, isReviewing]);

  useEffect(() => {
    if (!hostStateLoaded) return;
    localStorage.setItem(`quiz-crafter-leaderboard-${id}`, JSON.stringify(leaderboard));
    persistHostToolsPatch({ leaderboard }, "Leaderboard");
  }, [id, leaderboard, hostStateLoaded]);

  useEffect(() => {
    if (!hostStateLoaded) return;
    writeStoredList(hostToolsStorageKey(id, "players"), players);
    persistHostToolsPatch({ players }, "Players");
  }, [id, players, hostStateLoaded]);

  useEffect(() => {
    if (!hostStateLoaded) return;
    writeStoredObject(hostToolsStorageKey(id, "graded-answers"), gradedAnswers);
    persistHostToolsPatch({ gradedAnswers }, "Graded answers");
  }, [id, gradedAnswers, hostStateLoaded]);

  // Same isTestRun guard as question settings -- a Test Run's headcounts
  // shouldn't overwrite the real session's attendance numbers.
  useEffect(() => {
    if (!attendanceLoadedRef.current || isTestRun) return undefined;
    const timeout = window.setTimeout(() => {
      supabase.from("sessions").update({ attendance }).eq("id", id).then(({ error }) => {
        if (error) console.warn("Attendance save unavailable:", error);
      }).catch((error) => console.warn("Attendance save unavailable:", error));
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [id, attendance, isTestRun]);

  useEffect(() => {
    // Pre-open, nothing should be broadcasting/persisting as "live" yet --
    // see the eventOpen comment near this component's state declarations.
    if (!eventOpen || !session || !questions.length || !liveDisplayedQuestion) return;
    const state = buildLiveState({
      question: liveDisplayedQuestion,
      index: currentIndex,
      mode: presentMode,
    });
    if (state) persistLiveState(state);
  // The live snapshot helpers intentionally read the latest host state in this render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, session, sessionName, questions.length, liveDisplayedQuestion, currentIndex, pendingBonusIndex, rounds, introRound, showAnswer, showFunFact, presentMode, gameStarted, joinUrl, pointsPerQuestion, wagerMode, wagerLimit, wagerTiming, timerSeconds, timerEndAt, acceptingAnswers, answersPaused, branding, players, leaderboard, eventOpen]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const state = liveStateRef.current;
      if (state) sendLiveBroadcast(liveChannelRef.current, { type: "broadcast", event: "host_state", payload: state });
    }, 2500);
    return () => window.clearInterval(interval);
  }, [id]);

  const goToQuestion = (index, options = {}) => {
    if (index < 0 || index >= questions.length) return;
    if (options.reviewOnly) {
      setReviewIndex(index);
      return;
    }
    const targetQuestion = questions[index];
    const targetRound = rounds.find((round) => index >= round.startIndex && index < round.startIndex + round.questions.length);
    const isRoundBonus = isBonusQuestion(targetQuestion) || (targetRound && targetRound.questions.length > 1 && index === targetRound.startIndex + targetRound.questions.length - 1);
    const shouldPauseForBonus = options.pauseBeforeBonus !== false && presentMode !== "leaderboard" && isRoundBonus && pendingBonusIndex !== index;
    if (shouldPauseForBonus) {
      setPendingBonusIndex(index);
      setShowAnswer(false);
      setShowFunFact(false);
      setTimerEndAt(null);
      setPresentMode("bonus_pause");
      setGameStarted(true);
      // Broadcast this transition immediately instead of waiting on the
      // reactive snapshot effect -- the host can click straight through to
      // the bonus question next, and that call's own broadcast shouldn't be
      // racing a still-pending one for this state.
      const pauseState = buildLiveState({
        question: currentQuestion,
        index: currentIndex,
        mode: "bonus_pause",
        pendingIndex: index,
        showAnswerValue: false,
        showFunFactValue: false,
        timerEndAtValue: null,
        gameStartedValue: true,
      });
      if (pauseState) persistLiveState(pauseState, true);
      return;
    }
    setReviewIndex(null);
    setPointsPerQuestion(getQuestionPoints(targetQuestion));
    setTimerSeconds(Number(targetQuestion?.timerSeconds || 30));
    setWagerLimit(Number(targetQuestion?.wagerLimit || 0));
    setWagerTiming(normalizeWagerTiming(targetQuestion?.wagerTiming));
    setWagerMode(Number(targetQuestion?.wagerLimit || 0) > 0);
    setCurrentIndex(index);
    setEmergencyOverride(null);
    setPendingBonusIndex(null);
    setShowAnswer(false);
    setShowFunFact(false);
    const nextTimerEndAt = options.startTimer ? Date.now() + Math.max(1, Number(targetQuestion?.timerSeconds || 30)) * 1000 : null;
    setTimerEndAt(nextTimerEndAt);
    setPresentMode("question");
    if (options.startTimer) setGameStarted(true);
    const nextState = buildLiveState({
      question: targetQuestion,
      index,
      mode: "question",
      pendingIndex: null,
      showAnswerValue: false,
      showFunFactValue: false,
      timerEndAtValue: nextTimerEndAt,
      gameStartedValue: options.startTimer ? true : gameStarted,
      pointsValue: getQuestionPoints(targetQuestion),
      timerValue: Number(targetQuestion?.timerSeconds || 30),
      wagerLimitValue: Number(targetQuestion?.wagerLimit || 0),
      wagerModeValue: Number(targetQuestion?.wagerLimit || 0) > 0,
      wagerTimingValue: normalizeWagerTiming(targetQuestion?.wagerTiming),
    });
    if (nextState) persistLiveState(nextState, true);
  };

  const reviewQuestion = (index) => goToQuestion(index, { reviewOnly: true });
  const returnToLiveQuestion = () => setReviewIndex(null);
  const releaseReviewedQuestion = () => {
    if (!isReviewing) return;
    goToQuestion(reviewIndex, { startTimer: false, pauseBeforeBonus: false });
  };
  // The one-time transition out of "still arranging questions" -- flips the
  // gate on ensureLiveGame/persistLiveState above. Nothing else needs to
  // happen here: goToQuestion (via each card's own Ask Question button)
  // still owns actually starting a question.
  const openEvent = () => setEventOpen(true);

  const releaseMode = (mode, roundKey = null) => {
    if (mode === "categories") setIntroRoundKey(roundKey || introRound?.key || currentRound?.key || null);
    setPresentMode(mode);
    const nextGameStarted = mode === "qr" ? gameStarted : true;
    if (mode !== "qr") setGameStarted(true);
    sendLiveBroadcast(liveChannelRef.current, {
      type: "broadcast",
      event: "host_mode",
      payload: { mode, gameStarted: nextGameStarted, introRoundKey: roundKey || null, updatedAt: new Date().toISOString() },
    });
  };

  const toggleAnswer = () => {
    if (isReviewing) return;
    setShowAnswer((value) => {
      const next = !value;
      // Revealing settles the question -- a countdown still ticking
      // underneath the revealed answer doesn't mean anything anymore, and
      // leaving it running was confusing (looked like more time to answer).
      if (next) setTimerEndAt(null);
      return next;
    });
    setGameStarted(true);
    setPresentMode("question");
  };

  const toggleFunFact = () => {
    if (isReviewing) return;
    setShowFunFact((value) => !value);
    setGameStarted(true);
    releaseMode("question");
  };

  const toggleAudioPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlayingAudio) {
      audio.pause();
      return;
    }
    audio.currentTime = 0;
    audio.play().catch((error) => {
      console.error("Audio clip playback error:", error);
      toast.error("Couldn't play that clip -- check this device's speaker/volume");
    });
  };

  const toggleAnswersPaused = () => setAnswersPaused((value) => !value);

  const startTimer = () => {
    if (isReviewing) return;
    setTimerEndAt(Date.now() + Math.max(1, Number(timerSeconds) || 30) * 1000);
    setGameStarted(true);
    setPresentMode("question");
  };
  const resetTimer = () => setTimerEndAt(null);
  // For the true "I clicked Ask Question by mistake" or "let's redo this one"
  // case -- clears this question's submissions and grading so it can be asked
  // fresh. Any points already awarded from grading must be reversed through
  // adjustScore (the normal scoring path), not just dropped, or the
  // leaderboard would silently keep points for answers that no longer exist.
  // Parameterized by index (defaulting to the live question) so this can
  // also run from a completed question's own overflow menu, not just the
  // currently-live one -- only the live UI flags (timer/reveal state) reset
  // when the target actually is the live question.
  const resetQuestion = (targetIndex = currentIndex) => {
    const existingAnswers = answers.filter((answer) => Number(answer.questionIndex) === targetIndex);
    if (existingAnswers.length > 0 && !window.confirm(`Reset this question? This clears ${existingAnswers.length} submitted answer${existingAnswers.length === 1 ? "" : "s"}, undoes any points already awarded for it, and lets you ask it fresh.`)) return;
    existingAnswers.forEach((answer) => {
      const grade = gradedAnswers[answerKey(answer)];
      if (grade?.scoreApplied && Number(grade.points || 0)) adjustScore(answer.playerId, -Number(grade.points));
    });
    const removedKeys = new Set(existingAnswers.map((answer) => answerKey(answer)));
    setAnswers((current) => {
      const next = current.filter((answer) => Number(answer.questionIndex) !== targetIndex);
      writeStoredList(hostToolsStorageKey(id, "answers"), next);
      persistHostToolsPatch({ answers: next }, "Reset question");
      return next;
    });
    setGradedAnswers((current) => {
      const next = Object.fromEntries(Object.entries(current).filter(([key]) => !removedKeys.has(key)));
      writeStoredObject(hostToolsStorageKey(id, "graded-answers"), next);
      persistHostToolsPatch({ gradedAnswers: next }, "Reset question");
      return next;
    });
    if (targetIndex === currentIndex) {
      setShowAnswer(false);
      setShowFunFact(false);
      setTimerEndAt(null);
    }
    toast.success("Question reset -- ready to ask again");
  };
  const startTimerOnly = (seconds = 60) => {
    setTimerSeconds(seconds);
    setTimerEndAt(Date.now() + Math.max(1, Number(seconds) || 60) * 1000);
    setGameStarted(true);
    setPresentMode("question");
  };
  const makeFallbackEmergency = (kind = "replacement") => ({
    id: `emergency-${Date.now()}`,
    category: kind === "tiebreaker" ? "Tiebreaker" : (displayedQuestion?.category || "Emergency"),
    questionText: kind === "tiebreaker" ? "What year did the first modern crossword puzzle appear in a newspaper?" : "What common kitchen spice comes from the dried inner bark of trees in the Cinnamomum family?",
    answer: kind === "tiebreaker" ? "1913" : "Cinnamon",
    funFact: kind === "tiebreaker" ? "Arthur Wynne's word-cross appeared in the New York World in December 1913." : "The curled sticks are called quills and form as strips of bark dry.",
    options: [],
    type: "written",
    roundName: currentRound?.name || "Emergency",
    roundOrder: currentRound?.questions?.[0]?.roundOrder || 1,
    sourceOrder: currentIndex + 1,
    points: pointsPerQuestion || 25,
    timerSeconds: timerSeconds || 30,
    wagerLimit: 0,
    wagerTiming: "after_answer",
  });
  const generateEmergencyQuestion = async (kind = "replacement") => {
    setEmergencyLoading(true);
    // This already falls back to a backup emergency question on any error --
    // but with no timeout, a hung request never reached that fallback at
    // all, it just left the host waiting on-stage with a spinner. Bounding
    // it lets the existing fallback actually do its job.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch("/api/generate-session-candidates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          sessionId: `emergency-${id}-${currentIndex}`,
          questionType: "written",
          count: 1,
          difficulty: kind === "tiebreaker" ? "hard" : "medium",
          theme: `${kind === "tiebreaker" ? "Create a fair numeric tiebreaker question." : "Create one fresh emergency replacement question."}\nCurrent category: ${displayedQuestion?.category || ""}\nCurrent question to avoid: ${displayedQuestion?.questionText || ""}`,
          excludeUsed: true,
          avoidDuplicates: true,
          rejectedQuestions: [displayedQuestion?.questionText].filter(Boolean),
          venueId: session?.venue_id || null,
        }),
      });
      const data = await response.json();
      const candidate = Array.isArray(data?.candidates) ? data.candidates[0] : null;
      if (!response.ok || !candidate) throw new Error(data?.error || "No emergency question returned");
      setGeneratedEmergency({
        ...makeFallbackEmergency(kind),
        category: candidate.category || (kind === "tiebreaker" ? "Tiebreaker" : "Emergency"),
        questionText: candidate.question_text || candidate.question || "",
        answer: candidate.correct_answer || candidate.answer || "",
        funFact: candidate.fun_fact || "",
        options: [],
        type: "written",
      });
      toast.success(kind === "tiebreaker" ? "Tiebreaker generated" : "Replacement generated");
    } catch (error) {
      setGeneratedEmergency(makeFallbackEmergency(kind));
      toast.info("Using backup emergency question");
    } finally {
      clearTimeout(timeoutId);
      setEmergencyLoading(false);
    }
  };
  const activateEmergencyQuestion = (question) => {
    if (!question?.questionText || !question?.answer) return toast.error("Generate or enter an emergency question first");
    setEmergencyOverride({ ...question, id: question.id || `emergency-${Date.now()}` });
    setAnswers((current) => current.filter((answer) => Number(answer.questionIndex) !== Number(currentIndex)));
    setShowAnswer(false);
    setShowFunFact(false);
    setTimerEndAt(null);
    setPresentMode("question");
    setGameStarted(true);
    toast.success("Emergency question is live");
  };
  const addDisputeNote = (note) => {
    const body = String(note || "").trim();
    if (!body) return;
    const next = [{ id: `dispute-${Date.now()}`, questionIndex: currentIndex, questionText: displayedQuestion?.questionText || "", note: body, createdAt: new Date().toISOString() }, ...disputeNotes].slice(0, 100);
    setDisputeNotes(next);
    writeStoredList(hostToolsStorageKey(id, "disputes"), next);
    persistHostToolsPatch({ disputes: next }, "Disputes");
    toast.success("Dispute note saved");
  };
  const endSession = async () => {
    // A Test Run promises nothing here counts -- ending one must not commit
    // rehearsal players/scores to this session's permanent hosted_results,
    // or mark it is_past, the way the real End Session below does. Reset it
    // the same way starting a fresh Test Run already does (see
    // SessionDetail.jsx's handleTestRun) and bail before any of that runs.
    if (isTestRun) {
      if (!window.confirm("End this test run? Nothing from it will be saved.")) return;
      try {
        await resetTestGame(id);
        await resetHostToolsSessionState(id);
        if (!session?.is_past) await supabase.from("sessions").update({ hosted_results: null }).eq("id", id);
      } catch (error) {
        console.warn("Test run cleanup unavailable:", error);
      }
      toast.success("Test run ended. Nothing was saved.");
      if (onEditBuild) onEditBuild();
      else navigate(`/session/${id}`);
      return;
    }
    if (!window.confirm("End this hosted session and save the live results?")) return;
    const endedAt = new Date().toISOString();
    const results = {
      endedAt,
      sessionId: id,
      sessionName,
      leaderboard: [...leaderboard].sort((a, b) => Number(b.score || 0) - Number(a.score || 0)),
      players,
      answers,
      gradedAnswers,
      feedback,
      categoryFeedback,
      ideas: playerIdeas,
      activity: playerActivity,
      disputes: disputeNotes,
      questionCount: questions.length,
      currentIndex,
      presentMode: "winners",
    };
    writeStoredObject(hostToolsStorageKey(id, "results"), results);
    writeStoredList(hostToolsStorageKey(id, "players"), players);
    writeStoredList(hostToolsStorageKey(id, "answers"), answers);
    writeStoredObject(hostToolsStorageKey(id, "graded-answers"), gradedAnswers);
    writeStoredList(hostToolsStorageKey(id, "feedback"), feedback);
    writeStoredList(hostToolsStorageKey(id, "category-feedback"), categoryFeedback);
    writeStoredList(hostToolsStorageKey(id, "ideas"), playerIdeas);
    writeStoredList(hostToolsStorageKey(id, "activity"), playerActivity);
    writeStoredList(hostToolsStorageKey(id, "disputes"), disputeNotes);
    setGameStarted(true);
    setPresentMode("winners");
    persistHostToolsPatch({ endedAt, currentIndex, presentMode: "winners" }, "End session marker");
    hostedResultsRef.current = { ...results, liveState: liveStateRef.current, liveStateUpdatedAt: new Date().toISOString() };
    const { error } = await supabase.from("sessions").update({ hosted_at: endedAt, hosted_results: hostedResultsRef.current, is_past: true }).eq("id", id);
    if (liveGameId) endLiveGame(liveGameId).catch((endError) => console.warn("Marking live game finished unavailable:", endError));
    if (error) {
      console.error("End session database save failed:", error);
      toast.error("Session ended, but saving the results failed. Results are kept on this device -- try again before closing this tab.");
    } else {
      setSession((current) => ({ ...current, is_past: true, hosted_at: endedAt, hosted_results: hostedResultsRef.current }));
      toast.success("Hosted session ended and results saved");
    }
    const cachedCurrentProject = readCurrentProjectSessionId();
    const activeCurrentProject = cachedCurrentProject !== undefined ? cachedCurrentProject : await loadProfileValue(profileKeys.currentProjectSessionId).catch(() => null);
    if (activeCurrentProject && String(activeCurrentProject) === String(id)) writeCurrentProjectSessionId(null);
    // Most hosts have already shown winners/feedback manually at their own pace
    // before clicking End Session -- staying on the host screen afterward (still
    // showing winners) is redundant. Leave host mode once the save is confirmed;
    // stay put on a failed save so the "try again before closing this tab" advice
    // above still makes sense.
    if (!error) { if (onEditBuild) onEditBuild(); else navigate(`/session/${id}`); }
  };
  const openPresentation = () => window.open(`/present-session/${id}`, "_blank", "noopener,noreferrer");
  const copyJoinLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success("Join link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };
  const saveBranding = async (nextBranding) => {
    const cleanBranding = normalizeBranding(nextBranding);
    setBranding(cleanBranding);
    writeStoredBranding(id, cleanBranding);
    try {
      const { error } = await supabase.from("sessions").update({
        host_brand_name: cleanBranding.name,
        host_logo_url: cleanBranding.logoUrl,
        host_primary_color: cleanBranding.primaryColor,
        host_accent_color: cleanBranding.accentColor,
      }).eq("id", id);
      if (error && !String(error.message || "").includes("column")) throw error;
      toast.success("Host branding saved");
    } catch (error) {
      console.error("Save host branding error:", error);
      toast.success("Host branding saved on this device");
    }
  };
  const saveBrandingAsDefault = async (nextBranding) => {
    const cleanBranding = normalizeBranding(nextBranding);
    writeDefaultBranding(cleanBranding);
    try {
      await saveDefaultBrandingToProfile(cleanBranding);
      toast.success("Default host branding saved to your profile");
    } catch (error) {
      console.warn("Host default branding profile save unavailable:", error);
      toast.success("Default host branding saved on this device");
    }
    saveBranding(cleanBranding);
  };
  const useDefaultBranding = () => {
    const defaults = readDefaultBranding();
    saveBranding(defaults);
    return defaults;
  };


  const addTeam = () => {
    const name = teamName.trim();
    if (!name) return;
    const nextTeam = { id: crypto.randomUUID(), name, score: Number(teamScore || 0) };
    setLeaderboard((teams) => [...teams, nextTeam]);
    // Manually-added teams also need to land in the players roster --
    // it's what drives the Manual Answer dropdown, waiting-on list, and
    // submitted counts, none of which read from the leaderboard.
    setPlayers((current) => {
      const next = [...current, { id: nextTeam.id, name, updatePreference: "none", updateContact: "", joinedAt: new Date().toISOString() }];
      persistHostToolsPatch({ players: next }, "Players");
      return next;
    });
    if (liveGameId) upsertLivePlayer(liveGameId, nextTeam).catch((error) => console.warn("Live roster save unavailable:", error));
    setTeamName("");
    setTeamScore("");
    releaseMode("leaderboard");
  };

  // Teams are looked up by id and, if the roster's realtime sync hasn't
  // caught up yet (e.g. a player answers the first question right after
  // joining), added rather than silently dropped -- and the score is
  // upserted, not just updated, so it lands even if the player's roster
  // row hasn't been written yet.
  // Both used to read `leaderboard` from the render-time closure to compute
  // nextScore, then hand that already-computed number to setLeaderboard's
  // updater instead of deriving it inside the updater. Two adjustments for
  // the same team landing before a re-render flushed between them (e.g. a
  // manual grade plus the auto-grade effect, or grading two teams back to
  // back) both read the same stale prior score, so the second call's write
  // silently clobbered the first instead of stacking on top of it -- a
  // team's score would end up missing exactly one grade's worth of points.
  // Computing nextScore inside the updater guarantees each call builds on
  // whatever the true latest state is, not a snapshot from when it was called.
  const adjustScore = (teamId, amount) => {
    setLeaderboard((teams) => {
      const existing = teams.find((team) => team.id === teamId);
      const name = existing?.name || players.find((player) => player.id === teamId)?.name || "Team";
      const nextScore = Number(existing?.score || 0) + amount;
      if (liveGameId) upsertLivePlayer(liveGameId, { id: teamId, name, score: nextScore }).catch((error) => console.warn("Live roster save unavailable:", error));
      return teams.some((team) => team.id === teamId)
        ? teams.map((team) => team.id === teamId ? { ...team, score: nextScore } : team)
        : [...teams, { id: teamId, name, score: nextScore }];
    });
  };
  const setScore = (teamId, score) => {
    setLeaderboard((teams) => {
      const existing = teams.find((team) => team.id === teamId);
      const name = existing?.name || players.find((player) => player.id === teamId)?.name || "Team";
      const nextScore = Number(score || 0);
      if (liveGameId) upsertLivePlayer(liveGameId, { id: teamId, name, score: nextScore }).catch((error) => console.warn("Live roster save unavailable:", error));
      return teams.some((team) => team.id === teamId)
        ? teams.map((team) => team.id === teamId ? { ...team, score: nextScore } : team)
        : [...teams, { id: teamId, name, score: nextScore }];
    });
  };
  const removeTeam = (teamId) => {
    setLeaderboard((teams) => teams.filter((team) => team.id !== teamId));
    setPlayers((current) => current.filter((player) => player.id !== teamId));
    removeLivePlayer(teamId).catch((error) => console.warn("Live roster removal unavailable:", error));
  };
  // Dropped teams keep their score and history (unlike Remove) but stop
  // counting toward "waiting on" and submitted totals for future questions.
  const toggleTeamDropped = (teamId) => {
    setLeaderboard((teams) => teams.map((team) => team.id === teamId ? { ...team, dropped: !team.dropped } : team));
  };
  const updateNonPlayers = (field, value) => setAttendance((current) => ({ ...current, nonPlayers: { ...normalizeHeadcount(current.nonPlayers), [field]: value } }));
  const updateTeamHeadcount = (teamId, field, value) => setAttendance((current) => ({ ...current, teamHeadcounts: { ...current.teamHeadcounts, [teamId]: { ...normalizeHeadcount(current.teamHeadcounts[teamId]), [field]: value } } }));
  const openScoreModal = (teamId, context = {}) => {
    const team = leaderboard.find((item) => item.id === teamId) || players.find((item) => item.id === teamId);
    setScoreModal({ teamId, teamName: team?.name || context.playerName || "Team", currentScore: Number(team?.score || 0), adjustment: "", setTo: String(Number(team?.score || 0)), ...context });
  };
  const addManualAnswer = ({ playerId, answer, wagerAmount = 0, silent = false }) => {
    const team = leaderboard.find((item) => item.id === playerId) || players.find((item) => item.id === playerId);
    if (!team || !displayedQuestion) return toast.error("Choose a team and answer");
    const finalAnswer = String(answer || "").trim();
    if (!finalAnswer) return toast.error("Enter the team's answer");
    const questionWagerMode = isReviewing ? Number(displayedQuestion.wagerLimit || 0) > 0 : wagerMode;
    const questionWagerLimit = isReviewing ? Number(displayedQuestion.wagerLimit || 0) : Number(wagerLimit || 0);
    const questionWagerTiming = isReviewing ? normalizeWagerTiming(displayedQuestion.wagerTiming) : wagerTiming;
    const questionPoints = isReviewing ? getQuestionPoints(displayedQuestion) : Number(pointsPerQuestion) || getDefaultPoints(displayedQuestion);
    const wager = questionWagerMode ? Math.max(0, Number(wagerAmount || 0)) : 0;
    const payload = {
      playerId,
      playerName: team.name || "Team",
      answer: finalAnswer,
      points: questionWagerMode ? wager : questionPoints,
      wagerAmount: wager,
      wagerSubmitted: true,
      wagerMode: questionWagerMode,
      wagerLimit: questionWagerLimit,
      wagerCap: questionWagerLimit,
      scoreAtWager: getTeamScore(leaderboard, playerId),
      wagerTiming: questionWagerTiming,
      questionIndex: hostIndex,
      questionId: displayedQuestion.id,
      questionText: displayedQuestion.questionText,
      manuallyEntered: true,
      submittedAt: new Date().toISOString(),
    };
    setAnswers((current) => {
      const filtered = current.filter((item) => !(item.playerId === payload.playerId && Number(item.questionIndex) === Number(payload.questionIndex)));
      const next = [...filtered, payload].slice(-800);
      writeStoredList(hostToolsStorageKey(id, "answers"), next);
      persistHostToolsPatch({ answers: next }, "Manual answer");
      return next;
    });
    if (showAnswer || isReviewing) markAnswer(payload, isCorrectSubmission(payload, displayedQuestion) ? "correct" : "incorrect", { applyScore: !isReviewing });
    if (!silent) toast.success(`Added answer for ${team.name || "team"}`);
  };
  // previous/previousPoints/delta used to read gradedAnswers from the
  // render-time closure, same bug as adjustScore above: two grades for the
  // same answer landing before a re-render flushed between them (the direct
  // call from addManualAnswer plus the reveal-effect's auto-grade pass is
  // the common way this happens) both saw the same stale "not yet graded"
  // previous state and both applied the full award as a delta -- double-
  // counting that answer's points. Reading gradedAnswers from inside the
  // setGradedAnswers updater guarantees each call sees whatever the true
  // latest grade is, not a snapshot from when it was called.
  const markAnswer = (answer, status, options = {}) => {
    const key = answerKey(answer);
    const answerQuestion = questions[Number(answer.questionIndex)] || displayedQuestion;
    const answerPoints = Number(answer.points || 0) || Number(answerQuestion?.points || 0) || getDefaultPoints(answerQuestion);
    const answerUsesWager = Boolean(answer.wagerMode) || Number(answerQuestion?.wagerLimit || 0) > 0 || hasSubmittedWager(answer) || Number(answer.wagerAmount || 0) > 0;
    let toastMessage = null;
    setGradedAnswers((current) => {
      const previous = current[key];
      const previousPoints = previous?.scoreApplied === false ? 0 : Number(previous?.points || 0);
      const award = answerUsesWager ? getWagerAward(answer, leaderboard, previousPoints) : answerPoints;
      const wagerPenalty = answerUsesWager ? getWagerAward(answer, leaderboard, previousPoints) : 0;
      const nextPoints = status === "correct" ? award : status === "incorrect" && wagerPenalty > 0 ? -wagerPenalty : 0;
      const scoreApplied = Boolean(showAnswer || options.applyScore || previous?.scoreApplied === true);
      const delta = scoreApplied ? nextPoints - previousPoints : 0;
      if (delta) adjustScore(answer.playerId, delta);
      toastMessage = !scoreApplied ? `Marked ${status}; score updates on reveal` : status === "correct" ? `Marked correct (+${delta || 0})` : delta ? `Marked incorrect (${delta})` : "Marked incorrect";
      return { ...current, [key]: { status, points: nextPoints, scoreApplied, gradedAt: new Date().toISOString() } };
    });
    if (!options.silent) toast.success(toastMessage);
  };
  // Corrects a wager amount after the fact (mis-heard team, fat-fingered
  // manual entry) without touching the answer text or resubmitting it as a
  // new answer. Re-runs it through markAnswer so an already-graded answer's
  // award/penalty recomputes off the corrected amount instead of the stale one.
  const editWager = (playerId, questionIndex, nextWagerAmount) => {
    const existing = answers.find((item) => item.playerId === playerId && Number(item.questionIndex) === Number(questionIndex));
    if (!existing) return toast.error("No submitted answer to edit for this team");
    const wager = Math.max(0, Number(nextWagerAmount) || 0);
    const updated = { ...existing, wagerAmount: wager, wagerSubmitted: true };
    setAnswers((current) => {
      const next = current.map((item) => (item.playerId === playerId && Number(item.questionIndex) === Number(questionIndex) ? updated : item));
      writeStoredList(hostToolsStorageKey(id, "answers"), next);
      persistHostToolsPatch({ answers: next }, "Wager edit");
      return next;
    });
    const grade = gradedAnswers[answerKey(existing)];
    if (grade?.status) markAnswer(updated, grade.status, { silent: true });
    toast.success(`Wager set to ${wager} for ${existing.playerName || "team"}`);
  };

  // Points/timer/wager edits used to only touch the live pointsPerQuestion/
  // timerSeconds/wagerLimit state, which goToQuestion resets from the
  // question's own stored fields on every navigation -- so an edit "stuck"
  // only until you left and came back, which read as "doesn't save". This
  // writes the change back into the question itself (in `session`, and to
  // Supabase), so it survives navigation and reloads.
  const updateQuestionSettings = async (patch, scope, baseQuestion) => {
    const target = baseQuestion || displayedQuestion;
    if (!target || !session) return;
    const match = String(target.id || "").match(/^(.+)-(\d+)$/);
    if (!match) return;
    const baseIndex = questions.findIndex((question) => question.id === target.id);
    if (baseIndex === -1) return;
    const targetRound = rounds.find((round) => baseIndex >= round.startIndex && baseIndex < round.startIndex + round.questions.length);
    const scopedQuestions = scope === "round" && targetRound ? targetRound.questions
      : scope === "all" ? questions.slice(baseIndex)
      : [target];
    const dbPatch = {};
    if (patch.points !== undefined) dbPatch.points = patch.points;
    if (patch.timerSeconds !== undefined) dbPatch.timer_seconds = patch.timerSeconds;
    if (patch.wagerLimit !== undefined) dbPatch.wager_limit = patch.wagerLimit;
    if (patch.wagerTiming !== undefined) dbPatch.wager_timing = patch.wagerTiming;
    if (patch.imageUrl !== undefined) dbPatch.image_url = patch.imageUrl;
    if (patch.imageTiming !== undefined) dbPatch.image_timing = patch.imageTiming;
    if (patch.audioUrl !== undefined) dbPatch.audio_url = patch.audioUrl;

    const affectedIndicesByKey = new Map();
    scopedQuestions.forEach((question) => {
      const questionMatch = String(question.id || "").match(/^(.+)-(\d+)$/);
      if (!questionMatch) return;
      const [, key, indexStr] = questionMatch;
      if (!affectedIndicesByKey.has(key)) affectedIndicesByKey.set(key, new Set());
      affectedIndicesByKey.get(key).add(Number(indexStr));
    });

    const updatedArrays = {};
    affectedIndicesByKey.forEach((indices, key) => {
      const current = Array.isArray(session[key]) ? session[key] : [];
      updatedArrays[key] = current.map((question, index) => (indices.has(index) ? { ...question, ...dbPatch } : question));
    });

    setSession((current) => ({ ...current, ...updatedArrays }));
    if (scopedQuestions.some((question) => question.id === displayedQuestion?.id)) {
      if (patch.points !== undefined) setPointsPerQuestion(patch.points);
      if (patch.timerSeconds !== undefined) setTimerSeconds(patch.timerSeconds);
      if (patch.wagerLimit !== undefined) { setWagerLimit(patch.wagerLimit); setWagerMode(patch.wagerLimit > 0); }
      if (patch.wagerTiming !== undefined) setWagerTiming(patch.wagerTiming);
    }
    // A Test Run promises nothing here counts -- points/timer/wager edits
    // made during one must stay local to this run, or they'd overwrite the
    // real question data and show up when the actual event starts.
    if (isTestRun) return;
    try {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) throw error;
    } catch (error) {
      console.warn("Question settings save unavailable:", error);
      toast.error("Saved for this session, but couldn't sync to the database");
    }
  };

  // Inline content editing, right on the card a question is hosted from --
  // this is what replaces needing to jump to Advanced Edit just to fix a
  // typo or tighten an answer. Unlike updateQuestionSettings there's no
  // scope selector (editing one question's own text/answer never applies to
  // a whole round) and no index remap needed (content edits never change
  // round_order/source_order, so the question's position in the flattened
  // list is untouched). Changing question TYPE stays out of scope here --
  // that would mean moving the question to a different array entirely.
  const updateQuestionContent = async (question, patch) => {
    const match = String(question.id || "").match(/^(.+)-(\d+)$/);
    if (!match) return false;
    const [, key, indexStr] = match;
    const index = Number(indexStr);
    const dbPatch = {};
    if (patch.category !== undefined) dbPatch.category = patch.category;
    if (patch.questionText !== undefined) dbPatch.question_text = patch.questionText;
    if (patch.answer !== undefined) dbPatch.correct_answer = patch.answer;
    if (patch.incorrectAnswers !== undefined) dbPatch.incorrect_answers = patch.incorrectAnswers;
    if (patch.funFact !== undefined) dbPatch.fun_fact = patch.funFact;
    if (patch.imageUrl !== undefined) dbPatch.image_url = patch.imageUrl;
    if (patch.imageTiming !== undefined) dbPatch.image_timing = patch.imageTiming;
    if (patch.audioUrl !== undefined) dbPatch.audio_url = patch.audioUrl;
    const current = Array.isArray(session[key]) ? session[key] : [];
    const updatedArrays = { [key]: current.map((item, i) => (i === index ? { ...item, ...dbPatch } : item)) };
    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    if (isTestRun) return true;
    try {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) throw error;
    } catch (error) {
      console.warn("Question edit save unavailable:", error);
      toast.error("Saved for this session, but couldn't sync to the database");
    }
    return true;
  };

  // Reuses BuildSession's host-assistant.js "question_edit" mode unchanged --
  // one question in, one rewritten candidate back, never auto-committed.
  // AiEditQuestionModal shows the result and only calls updateQuestionContent
  // (via onApply) once the host explicitly clicks Apply.
  const editQuestionWithAi = async (question, instruction) => {
    const request = instruction.trim();
    if (!request) { toast.error("Tell the AI what to change"); return null; }
    try {
      const response = await fetch("/api/host-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "question_edit",
          request,
          context: {
            session: { name: sessionName },
            questions: [{
              category: question.category,
              question_text: question.questionText,
              correct_answer: question.answer,
              question_type: question.type,
              incorrect_answers: question.type === "multiple_choice" ? question.options.filter((option) => option !== question.answer) : [],
              fun_fact: question.funFact,
            }],
          },
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.candidate) throw new Error(data?.error || "AI did not return a usable edit");
      return { answer: data.answer, candidate: data.candidate };
    } catch (error) {
      console.error("AI question edit error:", error);
      toast.error(error.message || "Failed to edit question with AI");
      return null;
    }
  };

  // Third piece of AI tooling, after per-question edit and one-shot Generate
  // -- a multi-turn chat, reusing host-assistant.js's "build_cohost" mode.
  // That mode already existed and was fully tested server-side, but its only
  // caller in BuildSession.jsx (CoHostModal) was dead code, never actually
  // rendered -- this is its first real, live caller. Conversation history is
  // round-scoped and lives in CoHostChatModal's own state, not persisted, the
  // same as BuildSession's version.
  const askCoHost = async (round, conversation, request, rejectedQuestions = []) => {
    try {
      const response = await fetch("/api/host-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "build_cohost",
          request,
          context: {
            session: { name: sessionName, activeRound: round.name },
            conversation,
            questions: round.questions.map((question) => ({
              category: question.category,
              question_text: question.questionText,
              correct_answer: question.answer,
              question_type: question.type,
            })),
            // Drafts the host explicitly X'd out of the Usable Drafts list --
            // never resuggest these, not even reworded (see the X button in
            // CoHostChatModal, which also teaches rememberStyleFeedback so
            // this preference outlives the chat/round too).
            rejectedQuestions: rejectedQuestions.map((candidate) => ({
              category: candidate.category,
              question_text: candidate.question_text,
              correct_answer: candidate.correct_answer,
            })),
            hostStyleProfile: readHostStyleProfile(),
          },
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "The co-host couldn't finish that request");
      return { answer: data.answer, candidates: Array.isArray(data.candidates) ? data.candidates : [] };
    } catch (error) {
      console.error("AI co-host error:", error);
      toast.error(error.message || "Failed to ask the co-host");
      return null;
    }
  };

  // "I like this question but don't want to use it this round" -- saves a
  // copy into the user's permanent question library (the `questions` table),
  // completely independent of this session/round. Accepts either a flattened
  // session question (questionText/answer/type/options/funFact/imageUrl) or a
  // raw AI candidate (question_text/correct_answer/question_type/
  // incorrect_answers/fun_fact/image_url), so the same action works from an
  // existing round card and from an unreviewed Generate/Co-Host candidate.
  const saveQuestionToLibrary = async (question) => {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (sessionError || !userId) { toast.error("You must be signed in to save to your library"); return false; }
    const answer = question.answer || question.correct_answer || "";
    const questionText = question.questionText || question.question_text || question.question || "";
    if (!questionText.trim() || !answer.trim()) { toast.error("Question and answer can't be empty"); return false; }
    const incorrectAnswers = Array.isArray(question.options) && question.options.length
      ? question.options.filter((option) => option !== answer).join("; ")
      : Array.isArray(question.incorrect_answers) ? question.incorrect_answers.join("; ") : (question.incorrect_answers || null);
    const payload = {
      user_id: userId,
      question_text: questionText,
      correct_answer: answer,
      question_type: question.type || question.question_type || "written",
      category: question.category || "",
      incorrect_answers: incorrectAnswers,
      fun_fact: question.funFact || question.fun_fact || null,
      image_url: question.imageUrl || question.image_url || null,
    };
    try {
      const { error } = await supabase.from("questions").insert(payload);
      if (error) throw error;
      toast.success("Saved to library");
      return true;
    } catch (error) {
      console.error("Save to library error:", error);
      toast.error(error.message || "Failed to save to library");
      return false;
    }
  };

  // Shared by every round-header action below: writes the same patch onto
  // every question in `roundQuestions`, across whichever real arrays they
  // live in, mirroring updateQuestionSettings's per-array patch/commit.
  const commitQuestionPatch = async (roundQuestions, dbPatch) => {
    const affectedIndicesByKey = new Map();
    roundQuestions.forEach((question) => {
      const match = String(question.id || "").match(/^(.+)-(\d+)$/);
      if (!match) return;
      const [, key, indexStr] = match;
      if (!affectedIndicesByKey.has(key)) affectedIndicesByKey.set(key, new Set());
      affectedIndicesByKey.get(key).add(Number(indexStr));
    });
    const updatedArrays = {};
    affectedIndicesByKey.forEach((indices, key) => {
      const current = Array.isArray(session[key]) ? session[key] : [];
      updatedArrays[key] = current.map((question, index) => (indices.has(index) ? { ...question, ...dbPatch } : question));
    });
    setSession((current) => ({ ...current, ...updatedArrays }));
    if (isTestRun) return;
    try {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) throw error;
    } catch (error) {
      console.warn("Round update save unavailable:", error);
      toast.error("Saved for this session, but couldn't sync to the database");
    }
  };

  // Empty rounds (createEmptyRound below) have no questions to patch -- their
  // whole identity lives in this one round_descriptions entry instead.
  const matchesRoundMetadata = (entry, round) => Number(entry.order ?? entry.round_order) === round.roundOrder && (entry.name || entry.round_name) === round.name;
  const writeRoundMetadata = async (entries) => {
    const updatedArrays = { round_descriptions: entries };
    setSession((current) => ({ ...current, ...updatedArrays }));
    if (isTestRun) return;
    const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
    if (error) {
      console.warn("Round metadata save unavailable:", error);
      toast.error("Saved for this session, but couldn't sync to the database");
    }
  };

  const renameRound = (round, name) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === round.name) return;
    if (round.isEmpty) {
      writeRoundMetadata(getRoundMetadata(session).map((entry) => (matchesRoundMetadata(entry, round) ? { ...entry, name: trimmed } : entry)));
      return;
    }
    commitQuestionPatch(round.questions, { round_name: trimmed });
  };

  const describeRound = (round, description) => {
    if ((round.description || "") === description) return;
    if (round.isEmpty) {
      writeRoundMetadata(getRoundMetadata(session).map((entry) => (matchesRoundMetadata(entry, round) ? { ...entry, description } : entry)));
      return;
    }
    commitQuestionPatch(round.questions, { round_description: description });
  };

  const setEmptyRoundSettings = (round, settings) => {
    writeRoundMetadata(getRoundMetadata(session).map((entry) => (matchesRoundMetadata(entry, round) ? { ...entry, points: settings.points, timer_seconds: settings.timerSeconds, question_type: settings.questionType || entry.question_type } : entry)));
  };

  // Adjacent-swap only (mirrors BuildSession's handleMoveRound). Renaming a
  // round doesn't change its position in `questions` (sort key is roundOrder,
  // not name) so it's inherently safe, but swapping round_order does move
  // every one of that round's questions to new absolute indices -- and
  // currentIndex/reviewIndex are raw positions into that same array. Without
  // remapping them here, a live or reviewed question would silently become a
  // *different* question the instant its round moved.
  const moveRound = (round, direction) => {
    const index = rounds.findIndex((item) => item.key === round.key);
    const targetIndex = index + direction;
    if (index === -1 || targetIndex < 0 || targetIndex >= rounds.length) return;
    const other = rounds[targetIndex];
    const roundOrderValue = round.roundOrder;
    const otherOrderValue = other.roundOrder;
    if (roundOrderValue === undefined || otherOrderValue === undefined || roundOrderValue === otherOrderValue) return;

    const affectedIndicesByKey = new Map();
    const queueOrder = (roundQuestions, order) => {
      roundQuestions.forEach((question) => {
        const match = String(question.id || "").match(/^(.+)-(\d+)$/);
        if (!match) return;
        const [, key, indexStr] = match;
        if (!affectedIndicesByKey.has(key)) affectedIndicesByKey.set(key, new Map());
        affectedIndicesByKey.get(key).set(Number(indexStr), order);
      });
    };
    queueOrder(round.questions, otherOrderValue);
    queueOrder(other.questions, roundOrderValue);

    const updatedArrays = {};
    affectedIndicesByKey.forEach((indexOrderMap, key) => {
      const current = Array.isArray(session[key]) ? session[key] : [];
      updatedArrays[key] = current.map((question, i) => (indexOrderMap.has(i) ? { ...question, round_order: indexOrderMap.get(i) } : question));
    });

    // An empty side has no questions for queueOrder to have touched above --
    // its new position only exists in the round_descriptions shell, so patch
    // that entry's order directly instead.
    let metadataEntries = getRoundMetadata(session);
    let metadataChanged = false;
    const patchEmptySide = (side, newOrder) => {
      if (!side.isEmpty) return;
      metadataEntries = metadataEntries.map((entry) => {
        if (!matchesRoundMetadata(entry, side)) return entry;
        metadataChanged = true;
        return { ...entry, order: newOrder };
      });
    };
    patchEmptySide(round, otherOrderValue);
    patchEmptySide(other, roundOrderValue);
    if (metadataChanged) updatedArrays.round_descriptions = metadataEntries;

    const [first, second] = round.startIndex < other.startIndex ? [round, other] : [other, round];
    const remap = (position) => {
      const firstLen = first.questions.length;
      const secondLen = second.questions.length;
      if (position >= first.startIndex && position < first.startIndex + firstLen) return second.startIndex + (position - first.startIndex);
      if (position >= second.startIndex && position < second.startIndex + secondLen) return first.startIndex + (position - second.startIndex);
      return position;
    };

    setSession((current) => ({ ...current, ...updatedArrays }));
    setCurrentIndex((value) => remap(value));
    setReviewIndex((value) => (value === null ? value : remap(value)));

    if (isTestRun) return;
    supabase.from("sessions").update(updatedArrays).eq("id", id).then(({ error }) => {
      if (error) {
        console.warn("Round reorder save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    });
  };

  const deleteRound = (round) => {
    if (rounds.length <= 1) return toast.error("Keep at least one round");
    if (round.isEmpty) {
      if (!window.confirm(`Delete "${round.name}"? This can't be undone.`)) return;
      writeRoundMetadata(getRoundMetadata(session).filter((entry) => !matchesRoundMetadata(entry, round)));
      return;
    }
    const removedCount = round.questions.length;
    if (eventOpen && hostIndex >= round.startIndex && hostIndex < round.startIndex + removedCount) {
      return toast.error("Can't delete the round that's currently live or being reviewed");
    }
    if (!window.confirm(`Delete "${round.name}" and its ${removedCount} question${removedCount === 1 ? "" : "s"}? This can't be undone.`)) return;

    const affectedIndicesByKey = new Map();
    round.questions.forEach((question) => {
      const match = String(question.id || "").match(/^(.+)-(\d+)$/);
      if (!match) return;
      const [, key, indexStr] = match;
      if (!affectedIndicesByKey.has(key)) affectedIndicesByKey.set(key, new Set());
      affectedIndicesByKey.get(key).add(Number(indexStr));
    });
    const updatedArrays = {};
    affectedIndicesByKey.forEach((indices, key) => {
      const current = Array.isArray(session[key]) ? session[key] : [];
      updatedArrays[key] = current.filter((_, index) => !indices.has(index));
    });

    const newLength = questions.length - removedCount;
    const remap = (position) => {
      if (position >= round.startIndex + removedCount) return position - removedCount;
      if (position >= round.startIndex) return Math.min(round.startIndex, Math.max(0, newLength - 1));
      return position;
    };

    setSession((current) => ({ ...current, ...updatedArrays }));
    setCurrentIndex((value) => remap(value));
    setReviewIndex((value) => (value === null ? value : newLength > 0 ? remap(value) : null));

    if (isTestRun) return;
    supabase.from("sessions").update(updatedArrays).eq("id", id).then(({ error }) => {
      if (error) {
        console.warn("Round delete save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    });
  };

  // Shared validation/shaping for the cut-down manual write-question form --
  // used both to seed a brand new round (createRound) and to add a question
  // to one that already exists (addQuestionToRound). toast.error(...) returns
  // a truthy id, not a success flag, so every caller here returns an
  // explicit boolean rather than the toast call itself.
  const buildQuestionFromDraft = (draft) => {
    const questionText = draft.question_text.trim();
    const correctAnswer = draft.correct_answer.trim();
    if (!questionText || !correctAnswer) { toast.error("Write the question and its answer"); return null; }
    const wrongAnswers = draft.incorrect_answers.map((answer) => answer.trim()).filter(Boolean);
    if (draft.question_type === "multiple_choice" && wrongAnswers.length < 2) { toast.error("Add at least two wrong answers for multiple choice"); return null; }
    const key = draft.question_type === "true_false" ? "true_false_questions" : draft.question_type === "multiple_choice" ? "multiple_choice_questions" : "written_questions";
    const question = {
      question_type: draft.question_type,
      category: draft.category.trim() || "General",
      question_text: questionText,
      correct_answer: draft.question_type === "true_false" ? (correctAnswer.toLowerCase() === "false" ? "False" : "True") : correctAnswer,
      incorrect_answers: draft.question_type === "multiple_choice" ? wrongAnswers.join("; ") : null,
      fun_fact: draft.fun_fact.trim(),
      timer_seconds: Number(draft.timer_seconds) || 30,
      points: draft.points === "" || draft.points === null || draft.points === undefined ? null : Number(draft.points) || null,
      image_url: (draft.image_url || "").trim(),
      image_timing: draft.image_timing || "initial",
      audio_url: (draft.audio_url || "").trim(),
      wager_limit: 0,
      wager_timing: "after_answer",
    };
    return { key, question };
  };

  // A round with real questions gets its identity from them (round_name/
  // round_order on each one, via makeRounds); an empty round gets it from a
  // session.round_descriptions shell instead (see createEmptyRound). This is
  // the "write a first question right away" path -- a fresh, always-highest
  // round_order guarantees the new round -- and its one question -- lands at
  // the very end of the sorted `questions` list, after every existing
  // currentIndex/reviewIndex position, so nothing needs remapping the way
  // move/delete do.
  const createRound = async (name, draft, description = "") => {
    const roundName = name.trim();
    if (!roundName) { toast.error("Give the round a name"); return false; }
    const built = buildQuestionFromDraft(draft);
    if (!built) return false;
    const { key, question } = built;
    const roundOrder = rounds.reduce((max, round) => Math.max(max, Number(round.roundOrder) || 0), 0) + 1;
    question.round_name = roundName;
    question.round_order = roundOrder;
    question.round_description = description.trim();
    const current = Array.isArray(session[key]) ? session[key] : [];
    const updatedArrays = { [key]: [...current, question] };
    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    if (!isTestRun) {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) {
        console.warn("Round create save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    }
    return true;
  };

  // The no-question path: just a name (plus optional description/type/points/
  // timer defaults) saved into session.round_descriptions -- see the `rounds`
  // useMemo above for how this shell surfaces as a real, selectable round with
  // zero questions until something gets written or added to it.
  const createEmptyRound = async (name, description, settings) => {
    const roundName = name.trim();
    if (!roundName) { toast.error("Give the round a name"); return false; }
    const roundOrder = rounds.reduce((max, round) => Math.max(max, Number(round.roundOrder) || 0), 0) + 1;
    const entries = [...getRoundMetadata(session), {
      name: roundName,
      description: (description || "").trim(),
      order: roundOrder,
      question_type: settings?.questionType && settings.questionType !== "mixed" ? settings.questionType : "mixed",
      points: settings?.points === "" || settings?.points == null ? null : Number(settings.points) || null,
      timer_seconds: Number(settings?.timerSeconds) || 30,
    }];
    await writeRoundMetadata(entries);
    return true;
  };

  // Appending to an existing round, unlike createRound, can't rely on a
  // fresh top-level round_order to land safely at the very end of the whole
  // list -- it only needs to land at the end of *this* round, ahead of
  // every later round, which does shift every question after this round's
  // insertion point (including a live/reviewed one in a later round). Same
  // "flatten the hypothetical, look up by id" fix as moveQuestionToRound.
  // Shared by addQuestionToRound and addLibraryQuestionToRound (Phase 4):
  // stamps a fully-built question onto the end of `round`, remaps
  // currentIndex/reviewIndex by id the same way moveQuestionToRound does
  // (appending inside an *existing* round shifts every later round by one),
  // and saves.
  const appendQuestionToRound = async (round, key, question) => {
    const roundOrder = round.roundOrder;
    const sourceOrder = round.questions.reduce((max, item) => Math.max(max, Number(item.sourceOrder) || 0), 0) + 1;
    question.round_name = round.name;
    question.round_order = roundOrder;
    question.source_order = sourceOrder;
    const current = Array.isArray(session[key]) ? session[key] : [];
    const updatedArrays = { [key]: [...current, question] };

    const pinnedLiveId = questions[currentIndex]?.id;
    const pinnedReviewId = isReviewing ? questions[reviewIndex]?.id : null;
    const remappedQuestions = flattenSession({ ...session, ...updatedArrays });
    const newCurrentIndex = remappedQuestions.findIndex((item) => item.id === pinnedLiveId);
    const newReviewIndex = pinnedReviewId ? remappedQuestions.findIndex((item) => item.id === pinnedReviewId) : -1;

    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    if (newCurrentIndex !== -1) setCurrentIndex(newCurrentIndex);
    if (isReviewing) setReviewIndex(newReviewIndex !== -1 ? newReviewIndex : null);

    if (!isTestRun) {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) {
        console.warn("Add question save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    }
    return true;
  };

  const addQuestionToRound = async (round, draft) => {
    const built = buildQuestionFromDraft(draft);
    if (!built) return false;
    return appendQuestionToRound(round, built.key, built.question);
  };

  // Library questions come from the questions table already valid/saved, so
  // this skips buildQuestionFromDraft's manual-form validation and just
  // reshapes the row into the same embedded-question shape createRound/
  // addQuestionToRound write, media fields included (the manual write form
  // doesn't support media yet, but a library question often already has it).
  const addLibraryQuestionToRound = async (round, libraryQuestion) => {
    const type = libraryQuestion.question_type === "true_false" || libraryQuestion.question_type === "multiple_choice" ? libraryQuestion.question_type : "written";
    const key = type === "true_false" ? "true_false_questions" : type === "multiple_choice" ? "multiple_choice_questions" : "written_questions";
    const question = {
      question_type: type,
      category: libraryQuestion.category || "General",
      question_text: libraryQuestion.question_text || libraryQuestion.question || "",
      correct_answer: libraryQuestion.correct_answer || libraryQuestion.answer || "",
      incorrect_answers: libraryQuestion.incorrect_answers ?? null,
      fun_fact: libraryQuestion.fun_fact || "",
      image_url: libraryQuestion.image_url || "",
      image_timing: libraryQuestion.image_timing || libraryQuestion.image_display_timing || "initial",
      timer_seconds: 30,
      wager_limit: 0,
      wager_timing: "after_answer",
    };
    return appendQuestionToRound(round, key, question);
  };

  // Shapes one AI-generated candidate (from /api/generate-session-candidates)
  // into the same embedded-question form every other add path writes,
  // inheriting the round's points/timer defaults the same way
  // WriteQuestionModal's prefill does.
  const buildQuestionFromCandidate = (candidate, roundDefaults) => {
    const type = candidate.question_type === "true_false" || candidate.question_type === "multiple_choice" ? candidate.question_type : "written";
    const key = type === "true_false" ? "true_false_questions" : type === "multiple_choice" ? "multiple_choice_questions" : "written_questions";
    const question = {
      question_type: type,
      category: candidate.category || "General",
      question_text: candidate.question_text || candidate.question || "",
      correct_answer: type === "true_false" ? (String(candidate.correct_answer || "").toLowerCase() === "false" ? "False" : "True") : (candidate.correct_answer || candidate.answer || ""),
      incorrect_answers: type === "multiple_choice" ? (Array.isArray(candidate.incorrect_answers) ? candidate.incorrect_answers.join("; ") : candidate.incorrect_answers || "") : null,
      fun_fact: candidate.fun_fact || "",
      timer_seconds: Number(roundDefaults?.timerSeconds) || 30,
      points: roundDefaults?.points ?? null,
      wager_limit: 0,
      wager_timing: "after_answer",
    };
    return { key, question };
  };

  // Batched sibling of appendQuestionToRound for "Add All"/"Add Selected"
  // from AI generation -- one supabase.update for the whole batch instead of
  // N sequential ones (per the workspace-merge plan's explicit call-out for
  // this exact path), but the same "flatten the hypothetical session, look
  // up the pinned live/reviewed question by id" remap safety.
  const addGeneratedQuestionsToRound = async (round, candidates) => {
    if (!candidates.length) return false;
    const roundDefaults = round.questions[0] || { timerSeconds: round.timerSeconds, points: round.points };
    let sourceOrderCounter = round.questions.reduce((max, item) => Math.max(max, Number(item.sourceOrder) || 0), 0);
    const built = candidates.map((candidate) => {
      const { key, question } = buildQuestionFromCandidate(candidate, roundDefaults);
      sourceOrderCounter += 1;
      question.round_name = round.name;
      question.round_order = round.roundOrder;
      question.source_order = sourceOrderCounter;
      return { key, question };
    });

    const updatedArrays = {};
    built.forEach(({ key, question }) => {
      if (!updatedArrays[key]) updatedArrays[key] = Array.isArray(session[key]) ? [...session[key]] : [];
      updatedArrays[key].push(question);
    });

    const pinnedLiveId = questions[currentIndex]?.id;
    const pinnedReviewId = isReviewing ? questions[reviewIndex]?.id : null;
    const remappedQuestions = flattenSession({ ...session, ...updatedArrays });
    const newCurrentIndex = remappedQuestions.findIndex((item) => item.id === pinnedLiveId);
    const newReviewIndex = pinnedReviewId ? remappedQuestions.findIndex((item) => item.id === pinnedReviewId) : -1;

    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    if (newCurrentIndex !== -1) setCurrentIndex(newCurrentIndex);
    if (isReviewing) setReviewIndex(newReviewIndex !== -1 ? newReviewIndex : null);

    if (!isTestRun) {
      const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
      if (error) {
        console.warn("Generated questions save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    }
    return true;
  };

  // Unlike moveRound's whole-round swap, moving a single question can shift
  // an arbitrary number of other questions' positions in between its old and
  // new spot. Rather than hand-deriving that arithmetic, just flatten the
  // hypothetical post-move session the same way the real one gets flattened,
  // then look up where the previously-live/-reviewed question ended up by id.
  const moveQuestionToRound = async (question, targetRound) => {
    const match = String(question.id || "").match(/^(.+)-(\d+)$/);
    if (!match) return;
    const [, key, indexStr] = match;
    const targetRoundOrder = targetRound.roundOrder;
    if (targetRoundOrder === undefined) return;
    const index = Number(indexStr);
    const current = Array.isArray(session[key]) ? session[key] : [];
    const updatedArrays = { [key]: current.map((item, i) => (i === index ? { ...item, round_name: targetRound.name, round_order: targetRoundOrder } : item)) };

    const pinnedLiveId = questions[currentIndex]?.id;
    const pinnedReviewId = isReviewing ? questions[reviewIndex]?.id : null;
    const remappedQuestions = flattenSession({ ...session, ...updatedArrays });
    const newCurrentIndex = remappedQuestions.findIndex((item) => item.id === pinnedLiveId);
    const newReviewIndex = pinnedReviewId ? remappedQuestions.findIndex((item) => item.id === pinnedReviewId) : -1;

    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    if (newCurrentIndex !== -1) setCurrentIndex(newCurrentIndex);
    if (isReviewing) setReviewIndex(newReviewIndex !== -1 ? newReviewIndex : null);

    if (isTestRun) return;
    const { error } = await supabase.from("sessions").update(updatedArrays).eq("id", id);
    if (error) {
      console.warn("Move question save unavailable:", error);
      toast.error("Saved for this session, but couldn't sync to the database");
    }
  };

  // A straight copy, appended to the end of the same round it was
  // duplicated from -- reuses appendQuestionToRound, so it lands after
  // everything else in that round with the same index-remap safety as
  // Write Question/Add from Library.
  const duplicateQuestion = async (question) => {
    const round = rounds.find((item) => item.questions.some((q) => q.id === question.id));
    if (!round) return;
    const key = question.type === "true_false" ? "true_false_questions" : question.type === "multiple_choice" ? "multiple_choice_questions" : "written_questions";
    const wrongAnswers = (question.options || []).filter((option) => option !== question.answer);
    const dup = {
      question_type: question.type,
      category: question.category,
      question_text: question.questionText,
      correct_answer: question.answer,
      incorrect_answers: question.type === "multiple_choice" ? wrongAnswers.join("; ") : null,
      fun_fact: question.funFact || "",
      image_url: question.imageUrl || "",
      image_timing: question.imageTiming || "initial",
      audio_url: question.audioUrl || "",
      points: question.points,
      timer_seconds: Number(question.timerSeconds || 30),
      wager_limit: Number(question.wagerLimit || 0),
      wager_timing: question.wagerTiming || "after_answer",
    };
    await appendQuestionToRound(round, key, dup);
    toast.success("Question duplicated");
  };

  // Removes exactly one question -- unlike deleteRound, everything else in
  // its round (and every later round) keeps its round, so this is a plain
  // single-position removal + remap, no per-round batching needed.
  const discardQuestion = (question) => {
    const match = String(question.id || "").match(/^(.+)-(\d+)$/);
    if (!match) return;
    const [, key, indexStr] = match;
    const index = Number(indexStr);
    const baseIndex = questions.findIndex((item) => item.id === question.id);
    if (baseIndex === -1) return;
    if (eventOpen && baseIndex === hostIndex) return toast.error("Can't discard the question that's currently live or being reviewed");
    const round = rounds.find((item) => item.questions.some((q) => q.id === question.id));
    if (round && round.questions.length <= 1) return toast.error("A round needs at least one question -- delete the round instead");
    if (!window.confirm("Discard this question? This can't be undone.")) return;

    const current = Array.isArray(session[key]) ? session[key] : [];
    const updatedArrays = { [key]: current.filter((_, i) => i !== index) };
    const remap = (position) => {
      if (position > baseIndex) return position - 1;
      if (position === baseIndex) return Math.max(0, baseIndex - 1);
      return position;
    };

    setSession((prevSession) => ({ ...prevSession, ...updatedArrays }));
    setCurrentIndex((value) => remap(value));
    setReviewIndex((value) => (value === null ? value : remap(value)));

    if (isTestRun) return;
    supabase.from("sessions").update(updatedArrays).eq("id", id).then(({ error }) => {
      if (error) {
        console.warn("Discard question save unavailable:", error);
        toast.error("Saved for this session, but couldn't sync to the database");
      }
    });
  };

  useEffect(() => {
    if (!displayedQuestion || !showAnswer) return;
    currentAnswers.forEach((answer) => {
      const key = answerKey(answer);
      const grade = gradedAnswers[key];
      const submittedWager = hasSubmittedWager(answer);
      if (wagerMode && wagerTiming === "after_answer" && !submittedWager) return;
      if (grade?.scoreApplied === false) {
        markAnswer(answer, grade.status, { silent: true, applyScore: true });
        return;
      }
      if (wagerMode && submittedWager && grade && Number(answer.wagerAmount || 0) > 0 && Number(grade.points || 0) === 0) {
        markAnswer(answer, grade.status, { silent: true, applyScore: true });
        return;
      }
      if (grade) return;
      // Auto-grade against the accepted answer so nothing sits ungraded once
      // it's revealed -- the host corrects mistakes by clicking the answer.
      const correct = isCorrectSubmission(answer, displayedQuestion);
      markAnswer(answer, correct ? "correct" : "incorrect", { silent: true });
    });
  // markAnswer intentionally stays outside the deps so this effect only reacts to answer/game state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAnswers, displayedQuestion, gradedAnswers, wagerMode, wagerTiming, showAnswer]);

  if (loading) return <div className={`${embedded ? "h-full" : "min-h-screen"} bg-[#09090B] flex items-center justify-center`}><Loader2 className="text-[#71E0DC] animate-spin" size={34} /></div>;
  if (!session || (!displayedQuestion && !rounds.length)) {
    // A brand-new event (see NewEventModal) lands here with zero questions
    // and zero rounds -- this used to be a dead end pointing back to the old
    // builder page; now Add Round works from an empty session too.
    return <div className={`${embedded ? "h-full" : "min-h-screen"} bg-[#09090B] flex items-center justify-center p-6 text-center`}>
      <div>
        <p className="text-white text-2xl font-bold mb-2">{session ? "No questions yet" : "Session not found"}</p>
        <p className="text-zinc-500 mb-4">{session ? "Add a round to get this event started." : "This session may have been removed."}</p>
        {session && <Button onClick={() => setEmptyStateAddRoundOpen(true)} className="gradient-btn"><Plus size={16} className="mr-2" />Add Round</Button>}
        {!embedded && <Button variant="outline" onClick={() => navigate(`/session/${id}`)} className="ml-2 border-white/10 text-zinc-300 hover:text-white">Back to Session</Button>}
      </div>
      {session && emptyStateAddRoundOpen && <AddRoundModal onCreate={createRound} onCreateEmpty={createEmptyRound} onSaveToLibrary={saveQuestionToLibrary} approvedCategories={approvedCategories} categoryUsage={buildCategoryUsage(questions)} onClose={() => setEmptyStateAddRoundOpen(false)} />}
    </div>;
  }
  if (!displayedQuestion) {
    // Round(s) exist (createEmptyRound) but none has a real question yet --
    // the rest of this page is built around a live/displayed real question
    // (goToQuestion, QuestionStage, the live-hosting toolbar...), so this is
    // a minimal bootstrap screen rather than trying to make all of that
    // tolerate "no question exists anywhere." The moment any round gets its
    // first question, currentIndex (already 0) points at it and the normal
    // workspace below takes over on the next render.
    const emptyStateWriteRound = rounds.find((round) => round.key === emptyStateWriteRoundKey);
    const emptyStateGenerateRound = rounds.find((round) => round.key === emptyStateGenerateRoundKey);
    return <div className={`${embedded ? "h-full" : "min-h-screen"} bg-[#09090B] p-6`}>
      <div className="mx-auto max-w-2xl">
        <p className="mb-1 text-center text-2xl font-bold text-white">Let's build this event</p>
        <p className="mb-6 text-center text-zinc-500">Add a question to a round to get it started.</p>
        <div className="space-y-3">
          {rounds.map((round) => <div key={round.key} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-zinc-950/40 p-4">
            <div>
              <p className="font-bold text-white">{round.name}</p>
              <p className="text-xs text-zinc-500">{round.questions.length} question{round.questions.length === 1 ? "" : "s"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setEmptyStateGenerateRoundKey(round.key)} className="border-white/10 text-zinc-300 hover:text-white"><Sparkles size={13} className="mr-1.5" />Generate</Button>
              <Button size="sm" onClick={() => setEmptyStateWriteRoundKey(round.key)} className="gradient-btn"><Pencil size={13} className="mr-1.5" />Write Question</Button>
            </div>
          </div>)}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" onClick={() => setEmptyStateAddRoundOpen(true)} className="border-white/10 text-zinc-300 hover:text-white"><Plus size={14} className="mr-1.5" />Add Round</Button>
          <Button variant="outline" onClick={() => setEmptyStateManageRoundsOpen(true)} className="border-white/10 text-zinc-300 hover:text-white">Manage Rounds</Button>
          {!embedded && <Button variant="outline" onClick={() => navigate(`/session/${id}`)} className="border-white/10 text-zinc-300 hover:text-white">Back to Session</Button>}
        </div>
      </div>
      {emptyStateAddRoundOpen && <AddRoundModal onCreate={createRound} onCreateEmpty={createEmptyRound} onSaveToLibrary={saveQuestionToLibrary} approvedCategories={approvedCategories} categoryUsage={buildCategoryUsage(questions)} onClose={() => setEmptyStateAddRoundOpen(false)} />}
      {emptyStateWriteRound && <WriteQuestionModal round={emptyStateWriteRound} onCreate={(draft) => addQuestionToRound(emptyStateWriteRound, draft)} onSaveToLibrary={saveQuestionToLibrary} approvedCategories={approvedCategories} categoryUsage={buildCategoryUsage(questions, { roundKey: emptyStateWriteRound.key })} onClose={() => setEmptyStateWriteRoundKey(null)} />}
      {emptyStateGenerateRound && <GenerateRoundModal round={emptyStateGenerateRound} venueId={session?.venue_id} existingQuestionTexts={new Set()} onAddAll={(candidates) => addGeneratedQuestionsToRound(emptyStateGenerateRound, candidates)} onSaveToLibrary={saveQuestionToLibrary} onClose={() => setEmptyStateGenerateRoundKey(null)} />}
      {emptyStateManageRoundsOpen && <RoundManagerModal
        rounds={rounds}
        onRename={renameRound}
        onDescribe={describeRound}
        onSetRoundSettings={(round, settings) => (round.isEmpty ? setEmptyRoundSettings(round, settings) : updateQuestionSettings({ points: settings.points, timerSeconds: settings.timerSeconds }, "round", round.questions[0]))}
        onMoveRound={moveRound}
        onDelete={deleteRound}
        onAddRound={() => setEmptyStateAddRoundOpen(true)}
        onClose={() => setEmptyStateManageRoundsOpen(false)}
      />}
    </div>;
  }

  const viewPointsPerQuestion = isReviewing ? getQuestionPoints(displayedQuestion) : pointsPerQuestion;
  const viewTimerSeconds = isReviewing ? Number(displayedQuestion.timerSeconds || 30) : timerSeconds;
  const viewWagerLimit = isReviewing ? Number(displayedQuestion.wagerLimit || 0) : wagerLimit;
  const viewWagerMode = isReviewing ? Number(displayedQuestion.wagerLimit || 0) > 0 : wagerMode;
  const viewWagerTiming = isReviewing ? normalizeWagerTiming(displayedQuestion.wagerTiming) : wagerTiming;
  const hasRevealMedia = Boolean(displayedQuestion.imageUrl || displayedQuestion.image_url) && normalizeImageTiming(displayedQuestion.imageTiming || displayedQuestion.image_timing) === "after_answer";
  const hasRevealExtra = Boolean(displayedQuestion.funFact || hasRevealMedia);
  const brandStyle = { "--host-primary": branding.primaryColor, "--host-accent": branding.accentColor };
  const flaggedTeamCount = [...fairPlayStats.values()].filter((stats) => stats.flags.length > 0).length;
  // Union, not intersection: leaderboard is authoritative for "is this team
  // still active" (removeTeam clears both lists together), but a team can
  // legitimately exist in one list without the other yet -- a manually
  // added team never got a player_join broadcast, and a just-joined player
  // may not have synced to the leaderboard yet. Intersecting the two used
  // to silently drop manually-added teams from the Manual Answer dropdown,
  // the waiting-on list, and the submitted count.
  const activeTeamIds = new Set([...leaderboard.map((team) => team.id), ...players.map((player) => player.id)]);
  const activePlayers = [
    ...leaderboard.map((team) => {
      const player = players.find((item) => item.id === team.id);
      return player ? { ...player, name: team.name } : { id: team.id, name: team.name };
    }),
    ...players.filter((player) => !activeTeamIds.has(player.id)),
  ];
  // Dropped teams stay in activePlayers (history, answer review) but drop
  // out of the "still playing" roster used for waiting-on/submitted counts
  // and the manual-answer dropdown.
  const droppedTeamIds = new Set(leaderboard.filter((team) => team.dropped).map((team) => team.id));
  const playingPlayers = activePlayers.filter((player) => !droppedTeamIds.has(player.id));
  const activeCurrentAnswers = currentAnswers.filter((answer) => activeTeamIds.has(answer.playerId));
  // Same shape as activeCurrentAnswers above, parameterized by question index
  // instead of hardcoded to hostIndex -- the card list needs every question's
  // own answers at once (for the completed-card correct-count summary), not
  // just whichever one is currently displayed.
  const answersForQuestionIndex = (index) => answers.filter((answer) => Number(answer.questionIndex) === index && activeTeamIds.has(answer.playerId));
  const selectedIntroKey = introRoundKey || currentRound?.key || rounds[0]?.key || "";
  const chooseIntroRound = (roundKey) => {
    setIntroRoundKey(roundKey);
    if (presentMode === "categories") releaseMode("categories", roundKey);
  };
  return <div className={`${embedded ? "h-full" : "min-h-screen"} bg-[#09090B] text-white`} data-testid="host-session-page" style={brandStyle}>
    {displayedQuestion?.audioUrl && <audio ref={audioRef} src={displayedQuestion.audioUrl} preload="none" onPlay={() => setIsPlayingAudio(true)} onPause={() => setIsPlayingAudio(false)} onEnded={() => setIsPlayingAudio(false)} className="hidden" />}
    {isTestRun && !focusMode && <div className="bg-amber-400/15 border-b border-amber-400/30 text-amber-200 text-sm px-4 py-2 flex items-center justify-center gap-2" data-testid="test-mode-banner">
      <AlertTriangle size={14} />
      Test Run -- teams and scores here won't count. Starting the real event clears this out automatically.
    </div>}
    {!focusMode && <TopBar navigate={navigate} id={id} embedded={embedded} sessionName={sessionName} venueName={session?.venue_name || session?.venueName || session?.venue || ""} questions={questions} players={players} currentIndex={currentIndex} liveStatus={liveStatus} openPresentation={openPresentation} setFocusMode={setFocusMode} progress={progress} branding={branding} customizeOpen={customizeOpen} setCustomizeOpen={setCustomizeOpen} endSession={endSession} collapsed={topbarCollapsed} onToggleCollapse={() => setTopbarCollapsed((value) => !value)} answersPaused={answersPaused} onToggleAnswersPaused={toggleAnswersPaused} />}
    <div className={`max-w-[1680px] mx-auto p-4 lg:p-8 ${focusMode ? `${embedded ? "h-full" : "min-h-screen"} flex flex-col` : ""}`}>
      {focusMode && <div className="flex justify-between items-center mb-4"><Badge className="bg-zinc-800 text-zinc-300">{currentRound?.name || "Round"} - {currentIndex + 1} / {questions.length}</Badge><Button variant="outline" onClick={() => setFocusMode(false)} className="border-white/10 text-zinc-300 hover:text-white">Exit Focus</Button></div>}
      <div className={focusMode ? "flex-1 flex items-center" : "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-7"}>
        <main className="w-full min-w-0">
          {!focusMode && customizeOpen && <HostCustomizePanel branding={branding} defaultBranding={readDefaultBranding()} onSave={saveBranding} onSaveDefault={saveBrandingAsDefault} onUseDefault={useDefaultBranding} onClose={() => setCustomizeOpen(false)} />}
          <QuestionListView
            rounds={rounds}
            questions={questions}
            currentIndex={currentIndex}
            hostIndex={hostIndex}
            isReviewing={isReviewing}
            gameStarted={gameStarted}
            eventOpen={eventOpen}
            displayedQuestion={displayedQuestion}
            goToQuestion={goToQuestion}
            reviewQuestion={reviewQuestion}
            onBackToLive={returnToLiveQuestion}
            onGoLiveWithThis={releaseReviewedQuestion}
            answersForQuestionIndex={answersForQuestionIndex}
            gradedAnswers={gradedAnswers}
            players={playingPlayers}
            hostAnswers={activeCurrentAnswers}
            fairPlayStats={fairPlayStats}
            showAnswer={isReviewing ? true : showAnswer}
            showFunFact={isReviewing ? false : showFunFact}
            timeRemaining={isReviewing ? null : timeRemaining}
            viewPointsPerQuestion={viewPointsPerQuestion}
            viewTimerSeconds={viewTimerSeconds}
            viewWagerMode={viewWagerMode}
            viewWagerLimit={viewWagerLimit}
            viewWagerTiming={viewWagerTiming}
            onUpdateSettings={isReviewing ? () => {} : updateQuestionSettings}
            renameRound={renameRound}
            describeRound={describeRound}
            setEmptyRoundSettings={setEmptyRoundSettings}
            moveRound={moveRound}
            deleteRound={deleteRound}
            createRound={createRound}
            createEmptyRound={createEmptyRound}
            moveQuestionToRound={moveQuestionToRound}
            duplicateQuestion={duplicateQuestion}
            discardQuestion={discardQuestion}
            addQuestionToRound={addQuestionToRound}
            addLibraryQuestionToRound={addLibraryQuestionToRound}
            updateQuestionContent={updateQuestionContent}
            editQuestionWithAi={editQuestionWithAi}
            askCoHost={askCoHost}
            saveQuestionToLibrary={saveQuestionToLibrary}
            approvedCategories={approvedCategories}
            addGeneratedQuestionsToRound={addGeneratedQuestionsToRound}
            venueId={session?.venue_id}
            branding={branding}
            markAnswer={markAnswer}
            addManualAnswer={addManualAnswer}
            editWager={editWager}
            releaseMode={releaseMode}
            hasRevealExtra={hasRevealExtra}
            hasFunFact={Boolean(displayedQuestion.funFact)}
            hasAudio={Boolean(displayedQuestion.audioUrl)}
            isPlayingAudio={isPlayingAudio}
            onToggleAudio={toggleAudioPlayback}
            onRevealAnswer={toggleAnswer}
            onShowFunFact={toggleFunFact}
            startTimer={startTimer}
            resetTimer={isReviewing ? () => {} : resetTimer}
            resetQuestion={() => resetQuestion(hostIndex)}
            resetQuestionAt={resetQuestion}
          />
        </main>
        {!focusMode && <PlaybackRail eventOpen={eventOpen} onOpenEvent={openEvent} mode={presentMode} setMode={releaseMode} rounds={rounds} introRoundKey={selectedIntroKey} chooseIntroRound={chooseIntroRound} onOpenDrawer={setActiveDrawer} flaggedTeamCount={flaggedTeamCount} />}
      </div>
    </div>
    {scoreModal && <ScoreAdjustModal modal={scoreModal} setModal={setScoreModal} adjustScore={adjustScore} setScore={setScore} />}
    {!focusMode && <ToolDrawer activeDrawer={activeDrawer} onClose={() => setActiveDrawer(null)}>
      {activeDrawer === "teams" && <LeaderboardPanel leaderboard={leaderboard} teamName={teamName} teamScore={teamScore} setTeamName={setTeamName} setTeamScore={setTeamScore} addTeam={addTeam} openScoreModal={openScoreModal} removeTeam={removeTeam} toggleTeamDropped={toggleTeamDropped} showLeaderboard={() => releaseMode("leaderboard")} fairPlayStats={fairPlayStats} attendance={attendance} updateNonPlayers={updateNonPlayers} updateTeamHeadcount={updateTeamHeadcount} />}
      {activeDrawer === "run" && <div className="space-y-4"><RunSheet rounds={rounds} currentIndex={hostIndex} goToQuestion={reviewQuestion} answers={answers} players={activePlayers} gradedAnswers={gradedAnswers} editBuild={() => (onEditBuild ? onEditBuild() : navigate(`/build/${id}`))} /><AnswerHistoryPanel rounds={rounds} players={activePlayers} leaderboard={leaderboard} answers={answers} gradedAnswers={gradedAnswers} currentIndex={hostIndex} markAnswer={markAnswer} openScoreModal={openScoreModal} reviewQuestion={reviewQuestion} /></div>}
      {activeDrawer === "fairplay" && <FairPlayPanel leaderboard={leaderboard} fairPlayStats={fairPlayStats} openScoreModal={openScoreModal} />}
      {activeDrawer === "disputes" && <DisputesPanel currentIndex={currentIndex} addDisputeNote={addDisputeNote} disputeNotes={disputeNotes} />}
      {activeDrawer === "emergency" && <EmergencyPanel currentIndex={currentIndex} emergencyOverride={emergencyOverride} generatedEmergency={generatedEmergency} emergencyLoading={emergencyLoading} generateEmergencyQuestion={generateEmergencyQuestion} activateEmergencyQuestion={activateEmergencyQuestion} clearEmergency={() => setEmergencyOverride(null)} startTimerOnly={startTimerOnly} goToQuestion={goToQuestion} editBuild={() => (onEditBuild ? onEditBuild() : navigate(`/build/${id}`))} />}
      {activeDrawer === "feedback" && <LiveSignalsPanel feedback={feedback} categoryFeedback={categoryFeedback} ideas={playerIdeas} currentIndex={currentIndex} displayedQuestion={displayedQuestion} />}
    </ToolDrawer>}
  </div>;
};

// The unified card list: replaces the old QuestionNavStrip pill-bar +
// single-question QuestionStage split. Every question in every round renders
// as its own card at all times -- whichever one is "displayed" (the live
// question, or whatever's being reviewed) renders via QuestionStage's full
// interactive body (unchanged, including AnswerRows/Manual Answer); every
// other question renders as a compact summary card with Ask Question/Review
// actions. The true live question always gets a small pulsing indicator even
// while a different one is being reviewed, mirroring the old nav strip's
// "LIVE: Q#" badge.
// Existing rounds only -- no "+ Add Round" here yet (that's a later merge
// phase, since creating a round with zero questions doesn't survive a
// reload today; see flattenSession/makeRounds above). Rename/describe never
// touch round_order, so they can't shift which question is live or being
// reviewed; move does, and compensates for it in moveRound itself.
const emptyRoundDraft = { question_type: "written", category: "", question_text: "", correct_answer: "", incorrect_answers: ["", "", ""], fun_fact: "", points: "", timer_seconds: 30, image_url: "", image_timing: "initial", audio_url: "" };

// Shared by AddRoundModal and WriteQuestionModal -- a cut-down manual
// question form. Mirrors BuildSession's FreeWriteForm AI-assist button
// (Sparkles icon in the corner of the Question field): with no question
// text yet, it drafts a whole new one from the category via
// /api/generate-session-candidates; with a question already typed, it fills
// in only whatever's missing (answer/wrong answers/fun fact) via
// host-assistant.js's "question_edit" mode, explicitly told to leave the
// question wording (and the answer, if already filled in) untouched. Media
// attachment stays on the standalone builder page for now.
const QuestionDraftFields = ({ draft, setDraft, approvedCategories = [], categoryUsage = null }) => {
  const [assisting, setAssisting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const updateWrong = (index, value) => setDraft((prev) => ({ ...prev, incorrect_answers: prev.incorrect_answers.map((answer, i) => (i === index ? value : answer)) }));

  // Captured once, on first render, so Clear restores the round-inherited
  // defaults (question_type/points/timer_seconds) this draft actually
  // started with instead of a hardcoded blank -- "clear the AI question if I
  // don't like it" without also losing those.
  const initialDraftRef = useRef(draft);
  const clearContent = () => {
    setDraft((prev) => ({ ...initialDraftRef.current, question_type: prev.question_type, points: prev.points, timer_seconds: prev.timer_seconds }));
    toast.success("Cleared");
  };

  // Mirrors BuildSession's randomizeCategory -- picks a different one each
  // click rather than the same first-in-list every time. Prefers a category
  // not already used in this round (the host's stated preference); only
  // falls back to repeating one if every approved category is already
  // spoken for, so cycling never just gets stuck doing nothing.
  const cycleCategory = () => {
    const pool = approvedCategories.filter(Boolean);
    if (!pool.length) return toast.error("No approved categories yet -- approve some from the Categories page");
    const notCurrent = pool.filter((category) => category !== draft.category);
    const unused = notCurrent.filter((category) => !categoryUsage?.inRound?.has(categoryKey(category)));
    const options = unused.length ? unused : (notCurrent.length ? notCurrent : pool);
    setDraft((prev) => ({ ...prev, category: options[Math.floor(Math.random() * options.length)] }));
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadQuestionMedia(file);
      setDraft((prev) => ({ ...prev, image_url: url }));
    } catch (error) {
      console.error("Upload image error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioFile = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      const url = await uploadQuestionAudio(file);
      setDraft((prev) => ({ ...prev, audio_url: url }));
    } catch (error) {
      console.error("Upload audio error:", error);
      toast.error(error.message || "Failed to upload audio");
    } finally {
      setUploadingAudio(false);
    }
  };

  const runAssist = async () => {
    setAssisting(true);
    try {
      if (draft.question_text.trim()) {
        const missing = [];
        if (!draft.correct_answer.trim()) missing.push("the correct answer");
        if (draft.question_type === "multiple_choice" && draft.incorrect_answers.filter((answer) => answer.trim()).length < 2) missing.push("three plausible, comparable wrong answers");
        if (!draft.fun_fact.trim()) missing.push("a short fun fact");
        if (!missing.length) { toast.info("This question already has everything filled in."); return; }
        const keepUnchanged = ["the exact question wording"];
        if (draft.correct_answer.trim()) keepUnchanged.push("the correct answer");
        const response = await fetch("/api/host-assistant", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mode: "question_edit",
            request: `Do not rewrite or replace ${keepUnchanged.join(" or ")} -- keep them exactly as given. Add only: ${missing.join("; ")}.`,
            context: {
              questions: [{
                category: draft.category,
                question_text: draft.question_text,
                correct_answer: draft.correct_answer,
                question_type: draft.question_type,
                incorrect_answers: draft.incorrect_answers.filter(Boolean).join("; "),
                fun_fact: draft.fun_fact,
              }],
              hostStyleProfile: readHostStyleProfile(),
            },
          }),
        });
        const data = await response.json();
        if (!response.ok || !data?.candidate) throw new Error(data?.error || "No AI draft came back");
        const candidate = data.candidate;
        setDraft((prev) => ({
          ...prev,
          correct_answer: prev.correct_answer.trim() || candidate.correct_answer || prev.correct_answer,
          incorrect_answers: prev.question_type === "multiple_choice" && prev.incorrect_answers.filter((answer) => answer.trim()).length < 2 ? [...(candidate.incorrect_answers || []), "", "", ""].slice(0, 3) : prev.incorrect_answers,
          fun_fact: prev.fun_fact.trim() || candidate.fun_fact || prev.fun_fact,
        }));
        toast.success("Filled in the missing pieces");
      } else {
        // Only tell the AI to avoid this round's categories when it's the
        // one picking the category (no theme forcing a specific one) --
        // if the host already typed/cycled to a category themselves, that's
        // a deliberate choice (the CategoryUsageHint below already told them
        // if it repeats), not something to fight.
        const response = await fetch("/api/generate-session-candidates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: `write-${Date.now()}`,
            questionType: draft.question_type,
            count: 1,
            theme: draft.category ? `Use category: ${draft.category}` : "",
            excludeCategories: draft.category ? [] : [...(categoryUsage?.inRound?.values() || [])],
            excludeUsed: true,
            avoidDuplicates: true,
            hostStyleProfile: readHostStyleProfile(),
          }),
        });
        const data = await response.json();
        const candidate = Array.isArray(data?.candidates) ? data.candidates[0] : null;
        if (!response.ok || !candidate) throw new Error(data?.error || "No AI draft came back. Try adding a category.");
        setDraft((prev) => ({
          ...prev,
          category: candidate.category || prev.category,
          question_text: candidate.question_text || prev.question_text,
          correct_answer: candidate.correct_answer || prev.correct_answer,
          incorrect_answers: prev.question_type === "multiple_choice" ? [...(candidate.incorrect_answers || []), "", "", ""].slice(0, 3) : prev.incorrect_answers,
          fun_fact: candidate.fun_fact || prev.fun_fact,
        }));
        toast.success("AI draft added");
      }
    } catch (error) {
      console.error("Question write AI assist error:", error);
      toast.error(error.message || "Failed to draft with AI");
    } finally {
      setAssisting(false);
    }
  };

  return <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3">
      <select value={draft.question_type} onChange={(event) => setDraft((prev) => ({ ...prev, question_type: event.target.value }))} className="h-10 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white">
        <option value="written">Free Response</option>
        <option value="true_false">True/False</option>
        <option value="multiple_choice">Multiple Choice</option>
      </select>
      <div>
        <div className="flex gap-2">
          <input value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))} placeholder="Category" className="h-10 min-w-0 flex-1 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
          <button type="button" onClick={cycleCategory} title="Cycle through your approved categories" aria-label="Cycle approved categories" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 bg-zinc-950/50 text-zinc-300 hover:bg-zinc-800 hover:text-white"><RefreshCw size={16} /></button>
        </div>
        <CategoryUsageHint category={draft.category} usage={categoryUsage} />
      </div>
    </div>
    <div className="relative">
      <textarea value={draft.question_text} onChange={(event) => setDraft((prev) => ({ ...prev, question_text: event.target.value }))} placeholder="Question" className="min-h-[86px] w-full resize-none rounded-md border border-white/10 bg-zinc-950/50 px-3 py-2 pr-20 text-white outline-none focus:border-[#71E0DC]/60" />
      {(draft.question_text.trim() || draft.correct_answer.trim() || draft.fun_fact.trim()) && <button type="button" title="Clear the question, answer, wrong answers, and fun fact" aria-label="Clear question" onClick={clearContent} className="absolute right-11 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-zinc-950/70 text-zinc-300 hover:bg-zinc-800 hover:text-white"><X size={15} /></button>}
      <button type="button" title={draft.question_text.trim() ? "AI fill in the missing answer, wrong answers, or fun fact -- keeps your question as-is" : "AI draft a question from your category"} aria-label="AI assist" onClick={runAssist} disabled={assisting} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-zinc-950/70 text-zinc-300 hover:bg-zinc-800 hover:text-white disabled:opacity-60">
        {assisting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
      </button>
    </div>
    <input value={draft.correct_answer} onChange={(event) => setDraft((prev) => ({ ...prev, correct_answer: event.target.value }))} placeholder="Correct answer" className="h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
    {draft.question_type === "multiple_choice" && <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {draft.incorrect_answers.map((answer, index) => <input key={index} value={answer} onChange={(event) => updateWrong(index, event.target.value)} placeholder={`Wrong answer ${index + 1}`} className="h-10 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />)}
    </div>}
    <input value={draft.fun_fact} onChange={(event) => setDraft((prev) => ({ ...prev, fun_fact: event.target.value }))} placeholder="Fun fact (optional)" className="h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
    <div className="rounded-md border border-white/10 bg-zinc-950/30 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Media (optional)</p>
        {draft.image_url && <div className="inline-flex rounded-md border border-white/10 bg-zinc-950/70 p-0.5">
          <button type="button" onClick={() => setDraft((prev) => ({ ...prev, image_timing: "initial" }))} className={`rounded px-2 py-1 text-[11px] font-semibold ${draft.image_timing !== "after_answer" ? "bg-[#71E0DC]/15 text-[#71E0DC]" : "text-zinc-400 hover:text-white"}`}>With question</button>
          <button type="button" onClick={() => setDraft((prev) => ({ ...prev, image_timing: "after_answer" }))} className={`rounded px-2 py-1 text-[11px] font-semibold ${draft.image_timing === "after_answer" ? "bg-[#71E0DC]/15 text-[#71E0DC]" : "text-zinc-400 hover:text-white"}`}>After answer</button>
        </div>}
      </div>
      <label className="flex h-16 cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/15 bg-zinc-950/40 px-3 hover:border-[#71E0DC]/40">
        {uploadingImage ? <Loader2 size={18} className="animate-spin text-[#71E0DC]" /> : draft.image_url ? <img src={draft.image_url} alt="Question media preview" className="h-12 w-12 rounded object-cover border border-white/10" /> : <Upload size={16} className="text-zinc-400" />}
        <span className="text-xs text-zinc-400">{draft.image_url ? "Click to replace the image" : "Click to upload an image, or paste a URL below"}</span>
        <input type="file" accept="image/*" className="hidden" disabled={uploadingImage} onChange={(event) => handleImageFile(event.target.files?.[0])} />
      </label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Link className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input value={draft.image_url} onChange={(event) => setDraft((prev) => ({ ...prev, image_url: event.target.value }))} placeholder="Image URL" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 pl-9 pr-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
        </div>
        {draft.image_url && <button type="button" onClick={() => setDraft((prev) => ({ ...prev, image_url: "" }))} className="shrink-0 text-xs text-zinc-500 hover:text-rose-300">Remove</button>}
      </div>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Music className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input value={draft.audio_url} onChange={(event) => setDraft((prev) => ({ ...prev, audio_url: event.target.value }))} placeholder="Audio URL (optional)" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 pl-9 pr-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
        </div>
        <label className={`flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-white/10 bg-zinc-950/50 px-3 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white ${uploadingAudio ? "opacity-60" : ""}`}>
          {uploadingAudio ? <Loader2 size={14} className="animate-spin" /> : "Upload"}
          <input type="file" accept="audio/*" className="hidden" disabled={uploadingAudio} onChange={(event) => handleAudioFile(event.target.files?.[0])} />
        </label>
        {draft.audio_url && <button type="button" onClick={() => setDraft((prev) => ({ ...prev, audio_url: "" }))} className="shrink-0 text-xs text-zinc-500 hover:text-rose-300">Remove</button>}
      </div>
    </div>
  </div>;
};

// Two steps, not one combined form: naming the round is round-specific and
// belongs on its own screen; writing its first question is a separate
// concern that happens to be required (a round only becomes real once it
// has a question -- see flattenSession/makeRounds) but shouldn't visually
// read as "the round's fields" alongside the name field.
const AddRoundModal = ({ onCreate, onCreateEmpty, onSaveToLibrary, approvedCategories, categoryUsage, onClose }) => {
  const [step, setStep] = useState("name");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [questionType, setQuestionType] = useState("mixed");
  const [points, setPoints] = useState("");
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [draft, setDraft] = useState(emptyRoundDraft);
  const [saving, setSaving] = useState(false);
  const [creatingEmpty, setCreatingEmpty] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  const goToQuestionStep = () => {
    if (!name.trim()) return;
    setDraft((prev) => ({ ...prev, question_type: questionType === "mixed" ? prev.question_type : questionType, points, timer_seconds: timerSeconds }));
    setStep("question");
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const ok = await onCreate(name, draft, description);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEmpty = async () => {
    if (!name.trim()) return;
    setCreatingEmpty(true);
    try {
      const ok = await onCreateEmpty(name, description, { questionType, points, timerSeconds });
      if (ok) onClose();
    } finally {
      setCreatingEmpty(false);
    }
  };

  // Same as WriteQuestionModal's version -- save the drafted first question
  // straight to the library without creating the round at all.
  const handleSaveToLibrary = async () => {
    if (!draft.question_text.trim() || !draft.correct_answer.trim()) return toast.error("Question and answer can't be empty");
    setSavingToLibrary(true);
    try {
      await onSaveToLibrary({
        question_text: draft.question_text.trim(),
        correct_answer: draft.correct_answer.trim(),
        question_type: draft.question_type,
        category: draft.category.trim() || "General",
        incorrect_answers: draft.question_type === "multiple_choice" ? draft.incorrect_answers.map((answer) => answer.trim()).filter(Boolean) : [],
        fun_fact: draft.fun_fact.trim(),
        image_url: draft.image_url.trim(),
      });
    } finally {
      setSavingToLibrary(false);
    }
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-2xl rounded-xl bg-[#17181c] border border-white/10 shadow-2xl shadow-black/60 p-5 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      {step === "name" ? <>
        <h2 className="mb-1 text-xl font-bold text-white">Add Round</h2>
        <p className="mb-5 text-sm text-zinc-500">Name the round and set its defaults -- these apply to the questions you add to it.</p>
        <div className="space-y-3">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Round name"
            className="h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60"
            autoFocus
          />
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Description (optional) -- read out before this round starts"
            className="min-h-16 w-full resize-none rounded-md border border-white/10 bg-zinc-950/50 px-3 py-2 text-sm text-white outline-none focus:border-[#71E0DC]/60"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-xs text-zinc-500">Question type
              <select value={questionType} onChange={(event) => setQuestionType(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60">
                <option value="mixed">Mix</option>
                <option value="true_false">True/False</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="written">Written</option>
              </select>
            </label>
            <label className="block text-xs text-zinc-500">Points
              <input type="number" min="0" step="5" value={points} onChange={(event) => setPoints(event.target.value)} placeholder="Default for type" className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
            </label>
            <label className="block text-xs text-zinc-500">Timer (seconds)
              <input type="number" min="1" step="5" value={timerSeconds} onChange={(event) => setTimerSeconds(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
            </label>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
          <Button type="button" variant="outline" onClick={handleCreateEmpty} disabled={!name.trim() || creatingEmpty} className="border-white/10 text-zinc-300 hover:text-white">{creatingEmpty ? "Creating..." : "Create Round"}</Button>
          <Button type="button" onClick={goToQuestionStep} disabled={!name.trim()} className="gradient-btn">Next: Write Question</Button>
        </div>
      </> : <>
        <h2 className="mb-1 text-xl font-bold text-white">Write Question</h2>
        <p className="mb-5 text-sm text-zinc-500">First question for <span className="text-zinc-300 font-semibold">{name}</span> (optional -- "Create Round" on the previous step skips this and adds questions later).</p>
        <QuestionDraftFields draft={draft} setDraft={setDraft} approvedCategories={approvedCategories} categoryUsage={categoryUsage} />
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setStep("name")} className="border-white/10 text-zinc-300 hover:text-white">Back</Button>
          <Button type="button" variant="outline" onClick={handleSaveToLibrary} disabled={savingToLibrary} className="border-white/10 text-zinc-300 hover:text-white"><List size={14} className="mr-1.5" />{savingToLibrary ? "Saving..." : "Save to Library"}</Button>
          <Button type="button" onClick={handleCreate} disabled={saving} className="gradient-btn">{saving ? "Creating..." : "Create Round"}</Button>
        </div>
      </>}
    </div>
  </div>;
};

// Adds one question to an existing round -- the generalized form of
// AddRoundModal's first-question step, reachable from any round once it
// already exists.
const WriteQuestionModal = ({ round, onCreate, onSaveToLibrary, approvedCategories, categoryUsage, onClose }) => {
  // Prefills from the round's own first question, or (for a round created
  // without one yet -- see createEmptyRound) from the defaults saved with it
  // at creation -- either way, the closest thing this app has to a stored
  // "round default" (rounds aren't first-class entities, see
  // flattenSession/makeRounds), so a round's points/timer/type carry forward
  // to every question added to it, while staying fully overridable here.
  const roundDefaults = round.questions[0] || { type: round.questionType !== "mixed" ? round.questionType : null, points: round.points, timerSeconds: round.timerSeconds };
  const [draft, setDraft] = useState(() => ({
    ...emptyRoundDraft,
    question_type: roundDefaults?.type || emptyRoundDraft.question_type,
    points: roundDefaults?.points ?? "",
    timer_seconds: roundDefaults?.timerSeconds ?? 30,
  }));
  const [saving, setSaving] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const ok = await onCreate(draft);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  // "I like this question but don't want to use it this round" -- saves the
  // draft straight to the library without ever adding it to this round, so
  // there's no need to add it to the session first just to then save it.
  const handleSaveToLibrary = async () => {
    if (!draft.question_text.trim() || !draft.correct_answer.trim()) return toast.error("Question and answer can't be empty");
    setSavingToLibrary(true);
    try {
      await onSaveToLibrary({
        question_text: draft.question_text.trim(),
        correct_answer: draft.correct_answer.trim(),
        question_type: draft.question_type,
        category: draft.category.trim() || "General",
        incorrect_answers: draft.question_type === "multiple_choice" ? draft.incorrect_answers.map((answer) => answer.trim()).filter(Boolean) : [],
        fun_fact: draft.fun_fact.trim(),
        image_url: draft.image_url.trim(),
      });
    } finally {
      setSavingToLibrary(false);
    }
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-2xl rounded-xl bg-[#17181c] border border-white/10 shadow-2xl shadow-black/60 p-5 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      <h2 className="mb-1 text-xl font-bold text-white">Write Question</h2>
      <p className="mb-5 text-sm text-zinc-500">Added to the end of <span className="text-zinc-300 font-semibold">{round.name}</span>.</p>
      <QuestionDraftFields draft={draft} setDraft={setDraft} approvedCategories={approvedCategories} categoryUsage={categoryUsage} />
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
        <Button type="button" variant="outline" onClick={handleSaveToLibrary} disabled={savingToLibrary} className="border-white/10 text-zinc-300 hover:text-white"><List size={14} className="mr-1.5" />{savingToLibrary ? "Saving..." : "Save to Library"}</Button>
        <Button type="button" onClick={handleCreate} disabled={saving} className="gradient-btn">{saving ? "Adding..." : "Add Question"}</Button>
      </div>
    </div>
  </div>;
};

// First AI feature ported into the merged workspace (workspace-merge Phase
// 6) -- reuses host-assistant.js's "question_edit" mode exactly as
// BuildSession's handleChatEditQuestion already does. Never auto-commits:
// the AI's rewrite is only a preview until the host clicks Apply, which maps
// it onto updateQuestionContent (content fields only, same as manual Edit).
// If the AI answers with a different question_type than the original, the
// answer/wrong-answers aren't applied -- converting a question between
// true_false/multiple_choice/written means moving it to a different array
// entirely, which is a separate, riskier feature this doesn't attempt yet.
const AiEditQuestionModal = ({ question, onEdit, onApply, onClose }) => {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async () => {
    if (!instruction.trim() || loading) return;
    setLoading(true);
    try {
      const outcome = await onEdit(question, instruction);
      if (outcome) setResult(outcome);
    } finally {
      setLoading(false);
    }
  };

  const typeMatches = result?.candidate && result.candidate.question_type === question.type;
  const apply = async () => {
    if (!result?.candidate) return;
    setApplying(true);
    try {
      const patch = { category: result.candidate.category, questionText: result.candidate.question_text, funFact: result.candidate.fun_fact };
      if (typeMatches) {
        patch.answer = result.candidate.correct_answer;
        if (question.type === "multiple_choice") patch.incorrectAnswers = result.candidate.incorrect_answers.join("; ");
      }
      const ok = await onApply(patch);
      if (ok) onClose();
    } finally {
      setApplying(false);
    }
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-xl rounded-xl bg-[#17181c] border border-white/10 shadow-2xl shadow-black/60 p-5 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      <h2 className="mb-1 flex items-center gap-2 text-xl font-bold text-white"><Sparkles size={17} className="text-[#71E0DC]" />AI Edit</h2>
      <p className="mb-4 text-sm text-zinc-500">Tell the AI what to change about this question.</p>
      <div className="mb-3 rounded-lg border border-white/10 bg-zinc-950/50 p-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Current</p>
        <p className="mt-1 text-sm font-semibold text-white">{question.questionText}</p>
        <p className="mt-1 text-sm text-[#71E0DC]">Answer: {question.answer}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") ask(); }}
          placeholder="e.g. make it harder, fix the fun fact, tighten the wording..."
          className="h-10 flex-1 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60"
          autoFocus
        />
        <Button type="button" onClick={ask} disabled={loading || !instruction.trim()} className="h-10 gradient-btn">{loading ? <Loader2 size={15} className="animate-spin" /> : "Ask AI"}</Button>
      </div>
      {result && <div className="mt-4 rounded-lg border border-[#71E0DC]/20 bg-[#71E0DC]/5 p-3">
        <p className="text-sm text-zinc-200">{result.answer}</p>
        {result.candidate && <div className="mt-3 rounded-md border border-white/10 bg-zinc-950/60 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">{result.candidate.category}</p>
          <p className="mt-1 text-sm font-semibold text-white">{result.candidate.question_text}</p>
          {typeMatches && <p className="mt-1 text-sm text-[#71E0DC]">Answer: {result.candidate.correct_answer}</p>}
          {typeMatches && result.candidate.question_type === "multiple_choice" && result.candidate.incorrect_answers.length > 0 && <p className="mt-1 text-xs text-zinc-500">Wrong answers: {result.candidate.incorrect_answers.join(", ")}</p>}
          {result.candidate.fun_fact && <p className="mt-2 text-xs text-zinc-400">{result.candidate.fun_fact}</p>}
          {!typeMatches && <p className="mt-2 text-xs text-amber-300">AI suggested making this a {typeMeta[result.candidate.question_type]?.label || result.candidate.question_type} question -- changing a question's type isn't supported here yet, so only the category/question/fun fact would be applied, not the answer.</p>}
        </div>}
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setResult(null)} className="border-white/10 text-zinc-300 hover:text-white">Try Again</Button>
          {result.candidate && <Button type="button" onClick={apply} disabled={applying} className="gradient-btn">{applying ? "Applying..." : "Apply"}</Button>}
        </div>
      </div>}
    </div>
  </div>;
};

const GENERATE_DIFFICULTIES = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "host_hard", label: "Host Hard" },
];

// Second piece of AI tooling ported in (per-question AI edit was the
// first) -- reuses /api/generate-session-candidates unchanged, the same
// endpoint BuildSession's requestGeneratedQuestions calls, just with a
// leaner request (no approved/rejected-category memory or venue history
// yet -- those stay a BuildSession-only refinement for now). Candidates are
// a review step, not an auto-add: the host picks which ones to keep, then
// "Add to Round" commits the selection in one batch via
// addGeneratedQuestionsToRound.
const GenerateRoundModal = ({ round, venueId, existingQuestionTexts, onAddAll, onSaveToLibrary, onClose }) => {
  // "I don't like to use the same category more than once in a round" --
  // this round's own current categories, sent as excludeCategories below so
  // the API's real filter (not just a prompt hint) rejects any candidate
  // that repeats one, rather than leaving it to the model's judgment.
  const roundCategories = [...new Set(round.questions.map((question) => question.category).filter(Boolean))];
  const [questionType, setQuestionType] = useState(round.questions[0]?.type || (round.questionType && round.questionType !== "mixed" ? round.questionType : "written"));
  const [difficulty, setDifficulty] = useState("medium");
  const [count, setCount] = useState(5);
  const [theme, setTheme] = useState("");
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [adding, setAdding] = useState(false);

  const saveCandidate = async (candidate) => {
    const ok = await onSaveToLibrary(candidate);
    if (ok) setSavedIds((prev) => new Set(prev).add(candidate._id));
  };

  const generate = async () => {
    setLoading(true);
    setCandidates([]);
    try {
      const response = await fetch("/api/generate-session-candidates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: `round-${round.key}`,
          questionType,
          count: Math.max(1, Math.min(12, Number(count) || 5)),
          difficulty,
          theme: theme.trim(),
          excludeCategories: roundCategories,
          excludeUsed: true,
          avoidDuplicates: true,
          rejectedQuestions: [...existingQuestionTexts],
          hostStyleProfile: readHostStyleProfile(),
          venueId: venueId || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Failed to generate questions");
      const generated = (data.candidates || []).map((candidate, index) => ({ ...candidate, _id: `${Date.now()}-${index}` }));
      setCandidates(generated);
      setSelectedIds(new Set(generated.map((item) => item._id)));
      if (!generated.length) toast.error("No questions came back -- try a different theme or type");
    } catch (error) {
      console.error("Generate round error:", error);
      toast.error(error.message || "Failed to generate questions");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (candidateId) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(candidateId)) next.delete(candidateId); else next.add(candidateId);
    return next;
  });

  const addSelected = async () => {
    const selected = candidates.filter((item) => selectedIds.has(item._id));
    if (!selected.length) return;
    setAdding(true);
    try {
      const ok = await onAddAll(selected);
      if (ok) onClose();
    } finally {
      setAdding(false);
    }
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-2xl rounded-xl bg-[#17181c] border border-white/10 shadow-2xl shadow-black/60 p-5 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      <h2 className="mb-1 flex items-center gap-2 text-xl font-bold text-white"><Sparkles size={18} className="text-[#71E0DC]" />Generate Questions</h2>
      <div className="mb-4">
        <p className="text-sm text-zinc-500">AI-write a batch of questions for <span className="text-zinc-300 font-semibold">{round.name}</span>, then pick which ones to keep.</p>
        {roundCategories.length > 0 && <p className="mt-1 text-xs text-zinc-500">Won't repeat: {roundCategories.join(", ")}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="block text-xs text-zinc-500">Type
          <select value={questionType} onChange={(event) => setQuestionType(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-2 text-white outline-none focus:border-[#71E0DC]/60">
            <option value="true_false">True/False</option>
            <option value="multiple_choice">Multiple Choice</option>
            <option value="written">Written</option>
          </select>
        </label>
        <label className="block text-xs text-zinc-500">Difficulty
          <select value={difficulty} onChange={(event) => setDifficulty(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-2 text-white outline-none focus:border-[#71E0DC]/60">
            {GENERATE_DIFFICULTIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <label className="block text-xs text-zinc-500">Count
          <input type="number" min="1" max="12" value={count} onChange={(event) => setCount(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-2 text-white outline-none focus:border-[#71E0DC]/60" />
        </label>
        <label className="col-span-2 block text-xs text-zinc-500 md:col-span-1">Theme (optional)
          <input value={theme} onChange={(event) => setTheme(event.target.value)} placeholder="e.g. 90s movies" className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 px-2 text-white outline-none focus:border-[#71E0DC]/60" />
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <Button type="button" onClick={generate} disabled={loading} className="gradient-btn">{loading ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}{loading ? "Generating..." : "Generate"}</Button>
      </div>
      {candidates.length > 0 && <div className="mt-4 max-h-[360px] space-y-2 overflow-y-auto pr-1">
        {candidates.map((candidate) => {
          const selected = selectedIds.has(candidate._id);
          const saved = savedIds.has(candidate._id);
          return <div key={candidate._id} role="button" tabIndex={0} onClick={() => toggle(candidate._id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") toggle(candidate._id); }} className={`block w-full cursor-pointer rounded-lg border p-3 text-left transition-colors ${selected ? "border-[#71E0DC]/40 bg-[#71E0DC]/5" : "border-white/10 bg-zinc-950/40 hover:border-white/20"}`}>
            <div className="flex items-start gap-2">
              <div className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? "border-[#71E0DC] bg-[#71E0DC]" : "border-white/20"}`}>{selected && <Check size={12} className="text-zinc-950" />}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">{candidate.category}</p>
                  <button type="button" disabled={saved} onClick={(event) => { event.stopPropagation(); saveCandidate(candidate); }} className={`shrink-0 text-[11px] font-semibold ${saved ? "text-zinc-600" : "text-zinc-400 hover:text-[#71E0DC]"}`}>{saved ? "Saved" : "Save to Library"}</button>
                </div>
                <p className="mt-0.5 text-sm font-semibold text-white">{candidate.question_text}</p>
                <p className="mt-1 text-sm text-[#71E0DC]">Answer: {candidate.correct_answer}</p>
                {candidate.fun_fact && <p className="mt-1 text-xs text-zinc-400">{candidate.fun_fact}</p>}
              </div>
            </div>
          </div>;
        })}
      </div>}
      {candidates.length > 0 && <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-zinc-500">{selectedIds.size} of {candidates.length} selected</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
          <Button type="button" onClick={addSelected} disabled={adding || !selectedIds.size} className="gradient-btn">{adding ? "Adding..." : `Add ${selectedIds.size || ""} to Round`}</Button>
        </div>
      </div>}
    </div>
  </div>;
};

// Third AI feature ported in (after per-question AI edit and one-shot
// Generate) -- a multi-turn chat via host-assistant.js's "build_cohost"
// mode. Unlike Generate (one request, one batch of candidates), this keeps
// a conversation so the host can push back and refine ("no repeat
// categories", "make these harder") instead of starting over each time.
// Every reply's candidates stay addable individually and never disappear
// when a new message comes in, so an earlier answer's suggestions aren't
// lost by continuing the conversation.
const CoHostChatModal = ({ round, onAsk, onAddCandidate, onSaveToLibrary, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [candidatesByMessage, setCandidatesByMessage] = useState([]);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [savedIds, setSavedIds] = useState(() => new Set());
  // Drafts the host X'd out of the list -- kept for the life of this chat
  // (not just removed from view) so every later ask sends them back as
  // rejectedQuestions, and the co-host can't just resuggest the same thing
  // next message.
  const rejectedRef = useRef([]);

  const send = async () => {
    const request = prompt.trim();
    if (!request || loading) return;
    const nextMessages = [...messages, { role: "user", content: request }].slice(-10);
    setMessages(nextMessages);
    setPrompt("");
    setLoading(true);
    try {
      const outcome = await onAsk(round, nextMessages, request, rejectedRef.current);
      if (!outcome) return;
      setMessages((prev) => [...prev, { role: "assistant", content: outcome.answer }].slice(-12));
      if (outcome.candidates.length) {
        setCandidatesByMessage((prev) => [...prev, ...outcome.candidates.map((candidate, index) => ({ ...candidate, _id: `${Date.now()}-${index}` }))]);
      }
    } finally {
      setLoading(false);
    }
  };

  const addCandidate = async (candidate) => {
    const ok = await onAddCandidate(candidate);
    if (ok) setAddedIds((prev) => new Set(prev).add(candidate._id));
  };

  const saveCandidate = async (candidate) => {
    const ok = await onSaveToLibrary(candidate);
    if (ok) setSavedIds((prev) => new Set(prev).add(candidate._id));
  };

  // "I don't like this question" -- drops it from the list and teaches two
  // things: rememberStyleFeedback persists it as an avoid-this-style example
  // that shapes every future AI draft across the app (not just this chat),
  // while rejectedRef makes sure THIS chat's very next message can't just
  // hand the exact same question back.
  const dismissCandidate = (candidate) => {
    setCandidatesByMessage((prev) => prev.filter((item) => item._id !== candidate._id));
    rejectedRef.current = [...rejectedRef.current, candidate].slice(-40);
    rememberStyleFeedback(candidate, "not_my_style", "Dismissed from AI Co-Host drafts").catch((error) => console.warn("Style feedback save unavailable:", error));
    toast.success("Won't suggest that one again");
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-4xl rounded-xl bg-[#17181c] border border-white/10 shadow-2xl shadow-black/60 p-5 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      <h2 className="mb-1 flex items-center gap-2 text-xl font-bold text-white"><Bot size={18} className="text-[#71E0DC]" />AI Co-Host</h2>
      <p className="mb-4 text-sm text-zinc-500">Chat about <span className="text-zinc-300 font-semibold">{round.name}</span> -- ask for a vibe, push back, and add only what you like.</p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="flex min-h-[420px] flex-col rounded-xl border border-white/10 bg-zinc-950/40 p-3">
          <div className="flex-1 space-y-3 overflow-y-auto pr-1">
            {messages.length ? messages.map((message, index) => <div key={`${message.role}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${message.role === "user" ? "border border-[#71E0DC]/25 bg-[#71E0DC]/15 text-white" : "border border-white/10 bg-zinc-900/90 text-zinc-200"}`}>{message.content}</div>
            </div>) : <div className="flex h-full min-h-[260px] items-center justify-center text-center text-zinc-500">
              <div>
                <Bot className="mx-auto mb-3 h-7 w-7 text-[#71E0DC]" />
                <p className="font-semibold text-zinc-300">Tell the co-host what you need.</p>
                <p className="mt-1 text-sm">e.g. "give me 3 harder written questions, no repeat categories"</p>
              </div>
            </div>}
            {loading && <div className="flex justify-start"><div className="rounded-2xl border border-white/10 bg-zinc-900/90 px-4 py-2.5 text-sm text-zinc-400"><Loader2 size={14} className="mr-2 inline animate-spin text-[#71E0DC]" />Thinking...</div></div>}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") send(); }}
              placeholder="Ask the co-host..."
              className="h-10 flex-1 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60"
              autoFocus
            />
            <Button type="button" onClick={send} disabled={loading || !prompt.trim()} className="h-10 gradient-btn">{loading ? <Loader2 size={15} className="animate-spin" /> : "Send"}</Button>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/30 p-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-white">Usable Drafts</p>
            <Badge className="bg-zinc-800 text-zinc-300">{candidatesByMessage.length}</Badge>
          </div>
          <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {candidatesByMessage.length ? candidatesByMessage.map((candidate) => {
              const added = addedIds.has(candidate._id);
              const saved = savedIds.has(candidate._id);
              return <div key={candidate._id} className="rounded-lg border border-white/10 bg-zinc-950/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">{candidate.category}</p>
                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" disabled={saved} onClick={() => saveCandidate(candidate)} className={`text-[11px] font-semibold ${saved ? "text-zinc-600" : "text-zinc-400 hover:text-[#71E0DC]"}`}>{saved ? "Saved" : "Save to Library"}</button>
                    <button type="button" onClick={() => dismissCandidate(candidate)} title="Don't suggest this one again" aria-label="Dismiss this draft" className="text-zinc-500 hover:text-rose-300"><X size={14} /></button>
                  </div>
                </div>
                <p className="mt-1 text-sm font-semibold text-white">{candidate.question_text}</p>
                <p className="mt-1 text-sm text-[#71E0DC]">Answer: {candidate.correct_answer}</p>
                {candidate.fun_fact && <p className="mt-1 text-xs text-zinc-400">{candidate.fun_fact}</p>}
                <Button type="button" size="sm" disabled={added} onClick={() => addCandidate(candidate)} className={`mt-2 h-8 w-full ${added ? "bg-zinc-800 text-zinc-500" : "gradient-btn"}`}>{added ? "Added" : "Add to Round"}</Button>
              </div>;
            }) : <div className="rounded-lg border border-dashed border-white/10 bg-zinc-950/30 p-6 text-center text-xs text-zinc-500">When the co-host writes usable questions, they'll appear here.</div>}
          </div>
        </div>
      </div>
    </div>
  </div>;
};

// Browses the user's saved question library (the `questions` table) and
// inserts a pick straight into `round`. Fetched lazily and cached in
// QuestionListView's state across round-picker opens for one page visit.
// Deliberately narrower than BuildSession's LibraryModal for now: no
// cross-session "used elsewhere" fingerprinting or question-memory
// blocking -- just hides anything whose question text is already somewhere
// in *this* session, which is the case that actually matters for avoiding
// an accidental duplicate mid-event. The fuller usage-aware picker is a
// possible later refinement.
const LibraryPickerModal = ({ round, libraryQuestions, loading, existingTexts, onInsert, onClose }) => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [addedIds, setAddedIds] = useState(() => new Set());

  const filtered = (libraryQuestions || []).filter((question) => {
    const type = normalizeType(question);
    if (typeFilter !== "all" && type !== typeFilter) return false;
    const text = String(question.question_text || question.question || "").trim();
    if (!text) return false;
    if (existingTexts.has(text.toLowerCase()) && !addedIds.has(String(question.id))) return false;
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [question.question_text, question.correct_answer, question.category].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
  });

  const handlePick = async (question) => {
    const ok = await onInsert(question);
    if (ok) setAddedIds((prev) => new Set(prev).add(String(question.id)));
  };

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="flex w-full max-w-3xl max-h-[85vh] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#17181c] p-5 shadow-2xl shadow-black/60 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
      <h2 className="mb-1 text-xl font-bold text-white">Add from Library</h2>
      <p className="mb-4 text-sm text-zinc-500">Adding to <span className="font-semibold text-zinc-300">{round.name}</span>. Questions already used in this event are hidden.</p>
      <div className="mb-3 flex flex-col gap-2 md:flex-row">
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="h-10 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white">
          <option value="all">All types</option>
          <option value="written">Free Response</option>
          <option value="true_false">True/False</option>
          <option value="multiple_choice">Multiple Choice</option>
        </select>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search your library..." className="h-10 flex-1 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-white outline-none focus:border-[#71E0DC]/60" />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto pr-1">
        {loading && <p className="py-10 text-center text-sm text-zinc-500">Loading your library...</p>}
        {!loading && filtered.length === 0 && <p className="py-10 text-center text-sm text-zinc-500">No matching questions.</p>}
        {!loading && filtered.map((question) => {
          const added = addedIds.has(String(question.id));
          const meta = typeMeta[normalizeType(question)] || typeMeta.written;
          return <button key={question.id} type="button" onClick={() => !added && handlePick(question)} disabled={added} className={`w-full rounded-lg border p-3 text-left transition ${added ? "border-emerald-400/30 bg-emerald-400/5 opacity-60" : "border-white/10 bg-zinc-950/40 hover:border-[#71E0DC]/40"}`}>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-zinc-700 text-xs text-zinc-400">{question.category || "General"}</Badge>
              <Badge className="bg-zinc-800 text-xs text-zinc-300">{meta.short}</Badge>
              {added && <Badge className="bg-emerald-500/15 text-xs text-emerald-300">Added</Badge>}
            </div>
            <p className="text-sm text-white">{question.question_text || question.question}</p>
          </button>;
        })}
      </div>
    </div>
  </div>;
};

// Only the active round's bar renders here now -- see QuestionListView.
// Renaming/reordering/deleting moved into RoundManagerModal (opened via the
// RoundSwitcher dropdown's "Manage Rounds" entry): with only one round
// visible at a time, seeing it next to its siblings is what makes reorder
// and delete-with-guard legible, the same reason BuildSession's original
// round dropdown paired with a separate management surface.
const RoundHeader = ({ rounds, activeRound, activeIndex, onSelectRound, onManageRounds, onDescribe, onWriteQuestion, onAddFromLibrary, onGenerate, onCoHost, onNextRound, hasNextRound }) => {
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState(activeRound.description || "");

  const commitDescription = () => {
    onDescribe(descriptionDraft.trim());
    setEditingDescription(false);
  };

  return <div className="sticky top-0 z-[5] mb-3 rounded-lg border border-white/10 bg-zinc-950/95 backdrop-blur">
    <div className="flex items-center gap-2 px-3 py-2">
      <RoundSwitcher rounds={rounds} activeRound={activeRound} activeIndex={activeIndex} onSelect={onSelectRound} onManage={onManageRounds} />
      <span className="text-xs text-zinc-500">{activeRound.questions.length} question{activeRound.questions.length === 1 ? "" : "s"}</span>
      <div className="ml-auto flex items-center gap-1">
        <button type="button" onClick={onNextRound} disabled={!hasNextRound} className="flex h-7 items-center gap-1 rounded px-2 text-xs font-medium text-zinc-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-zinc-400" title={hasNextRound ? "Go to next round" : "This is the last round"} aria-label="Next round">Next Round<ChevronRight size={14} /></button>
        <div className="mx-0.5 h-4 w-px bg-white/10" />
        <button type="button" onClick={() => setEditingDescription((value) => !value)} className={`flex h-7 w-7 items-center justify-center rounded ${activeRound.description ? "text-[#71E0DC]" : "text-zinc-500"} hover:text-white`} title={activeRound.description ? "Edit round note" : "Add round note"} aria-label="Round note"><MessageSquare size={14} /></button>
        <button type="button" onClick={onCoHost} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white" title="Chat with the AI co-host" aria-label="AI co-host"><Bot size={14} /></button>
        <button type="button" onClick={onGenerate} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white" title="Generate questions with AI" aria-label="Generate questions"><Sparkles size={14} /></button>
        <button type="button" onClick={onWriteQuestion} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white" title="Write a question for this round" aria-label="Write question"><Pencil size={14} /></button>
        <button type="button" onClick={onAddFromLibrary} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white" title="Add from library" aria-label="Add from library"><List size={14} /></button>
      </div>
    </div>
    {editingDescription && <div className="flex items-start gap-2 border-t border-white/5 px-3 py-2">
      <textarea
        autoFocus
        value={descriptionDraft}
        onChange={(event) => setDescriptionDraft(event.target.value)}
        placeholder="Optional note read out before this round starts..."
        className="min-h-16 flex-1 resize-none rounded-md border border-white/10 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-200 outline-none focus:border-[#71E0DC]/60"
      />
      <div className="flex flex-col gap-1.5 pt-1">
        <button type="button" onClick={commitDescription} className="text-emerald-300 hover:text-emerald-200" aria-label="Save round note"><Check size={15} /></button>
        <button type="button" onClick={() => { setDescriptionDraft(activeRound.description || ""); setEditingDescription(false); }} className="text-zinc-500 hover:text-white" aria-label="Cancel note"><X size={15} /></button>
      </div>
    </div>}
  </div>;
};

// Trigger + dropdown: "R{n}: {name}", a list of every round (name, position,
// question count) to jump to, and a "Manage Rounds" entry for structural
// changes. Matches the reference's round picker instead of stacking every
// round's full question list on one page.
const RoundSwitcher = ({ rounds, activeRound, activeIndex, onSelect, onManage }) => {
  const [open, setOpen] = useState(false);
  return <div className="relative">
    <button type="button" onClick={() => setOpen((value) => !value)} className="flex h-7 items-center gap-1.5 rounded-md border border-[#71E0DC]/30 bg-[#71E0DC]/10 px-2.5 text-sm font-bold text-[#71E0DC]">
      R{activeIndex + 1}: {activeRound.name}<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <>
      <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
      <div className="absolute left-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-lg border border-white/10 bg-zinc-950 shadow-xl shadow-black/60">
        <p className="px-3 pb-1 pt-3 text-xs font-bold uppercase tracking-wide text-zinc-500">Rounds</p>
        <div className="max-h-72 overflow-y-auto py-1">
          {rounds.map((round, index) => <button key={round.key} type="button" onClick={() => { onSelect(round.key); setOpen(false); }} className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-zinc-900 ${round.key === activeRound.key ? "bg-zinc-900/70" : ""}`}>
            <span>
              <span className={`block text-sm font-semibold ${round.key === activeRound.key ? "text-[#71E0DC]" : "text-white"}`}>{round.name}</span>
              <span className="block text-xs text-zinc-500">Round {index + 1}</span>
            </span>
            <span className="text-xs text-zinc-500">({round.questions.length})</span>
          </button>)}
        </div>
        <button type="button" onClick={() => { onManage(); setOpen(false); }} className="flex w-full items-center gap-2 border-t border-white/10 px-3 py-2.5 text-left hover:bg-zinc-900">
          <Pencil size={14} className="text-zinc-400" />
          <span>
            <span className="block text-sm font-semibold text-white">Manage Rounds</span>
            <span className="block text-xs text-zinc-500">Manage the rounds in the event.</span>
          </span>
        </button>
      </div>
    </>}
  </div>;
};

// The structural surface for rounds -- rename/reorder/delete/add, all in one
// place since seeing every round next to its siblings is what makes those
// operations legible when only one round shows on the main screen at a time.
const RoundManagerModal = ({ rounds, onRename, onDescribe, onSetRoundSettings, onMoveRound, onDelete, onAddRound, onClose }) => <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
  <div className="w-full max-w-xl rounded-xl border border-white/10 bg-[#17181c] p-5 shadow-2xl shadow-black/60 relative">
    <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close"><X size={18} /></button>
    <h2 className="mb-4 text-xl font-bold text-white">Manage Rounds</h2>
    <div className="space-y-2">
      {rounds.map((round, index) => <RoundManagerRow
        key={round.key}
        round={round}
        canMoveUp={index > 0}
        canMoveDown={index < rounds.length - 1}
        canDelete={rounds.length > 1}
        onRename={(name) => onRename(round, name)}
        onDescribe={(description) => onDescribe(round, description)}
        onSetSettings={(settings) => onSetRoundSettings(round, settings)}
        onMoveUp={() => onMoveRound(round, -1)}
        onMoveDown={() => onMoveRound(round, 1)}
        onDelete={() => onDelete(round)}
      />)}
    </div>
    <Button type="button" variant="outline" onClick={onAddRound} className="mt-4 w-full border-dashed border-white/15 text-zinc-300 hover:text-white"><Plus size={14} className="mr-1.5" />Add Round</Button>
  </div>
</div>;

const RoundManagerRow = ({ round, canMoveUp, canMoveDown, canDelete, onRename, onDescribe, onSetSettings, onMoveUp, onMoveDown, onDelete }) => {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(round.name);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState(round.description || "");
  const [editingSettings, setEditingSettings] = useState(false);
  const roundPoints = round.questions[0]?.points ?? round.points;
  const roundTimerSeconds = round.questions[0]?.timerSeconds ?? round.timerSeconds;
  const [pointsDraft, setPointsDraft] = useState(roundPoints ?? "");
  const [timerDraft, setTimerDraft] = useState(roundTimerSeconds ?? 30);
  const [typeDraft, setTypeDraft] = useState(round.questionType || "mixed");

  const commitName = () => { onRename(nameDraft); setEditingName(false); };
  const commitDescription = () => { onDescribe(descriptionDraft.trim()); setEditingDescription(false); };
  const commitSettings = () => {
    onSetSettings({ points: pointsDraft === "" ? null : Number(pointsDraft) || 0, timerSeconds: Number(timerDraft) || 30, questionType: round.isEmpty ? typeDraft : undefined });
    setEditingSettings(false);
  };

  return <div className="rounded-lg border border-white/10 bg-zinc-950/40 p-2.5">
    <div className="flex items-center gap-2">
      {editingName ? <div className="flex flex-1 items-center gap-1.5">
        <input autoFocus value={nameDraft} onChange={(event) => setNameDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") commitName(); if (event.key === "Escape") { setNameDraft(round.name); setEditingName(false); } }} className="h-8 flex-1 rounded border border-[#71E0DC]/40 bg-zinc-900 px-2 text-sm font-bold text-white outline-none" />
        <button type="button" onClick={commitName} className="text-emerald-300 hover:text-emerald-200" aria-label="Save round name"><Check size={15} /></button>
        <button type="button" onClick={() => { setNameDraft(round.name); setEditingName(false); }} className="text-zinc-500 hover:text-white" aria-label="Cancel rename"><X size={15} /></button>
      </div> : <button type="button" onClick={() => { setNameDraft(round.name); setEditingName(true); }} className="flex flex-1 items-center gap-1.5 text-left text-sm font-bold text-white hover:text-[#71E0DC]" title="Rename round">
        {round.name}<Pencil size={11} className="shrink-0 text-zinc-500" />
        <span className="ml-1 text-xs font-normal text-zinc-500">{round.questions.length} question{round.questions.length === 1 ? "" : "s"}</span>
      </button>}
      <div className="flex shrink-0 items-center gap-1">
        <button type="button" onClick={() => { setPointsDraft(roundPoints ?? ""); setTimerDraft(roundTimerSeconds ?? 30); setTypeDraft(round.questionType || "mixed"); setEditingSettings((value) => !value); }} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white" title="Points &amp; timer for this round" aria-label="Round points and timer"><SlidersHorizontal size={14} /></button>
        <button type="button" onClick={() => setEditingDescription((value) => !value)} className={`flex h-7 w-7 items-center justify-center rounded ${round.description ? "text-[#71E0DC]" : "text-zinc-500"} hover:text-white`} title={round.description ? "Edit round note" : "Add round note"} aria-label="Round note"><MessageSquare size={14} /></button>
        <button type="button" onClick={onMoveUp} disabled={!canMoveUp} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white disabled:opacity-30" aria-label="Move round up"><ChevronUp size={15} /></button>
        <button type="button" onClick={onMoveDown} disabled={!canMoveDown} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white disabled:opacity-30" aria-label="Move round down"><ChevronDown size={15} /></button>
        <button type="button" onClick={onDelete} disabled={!canDelete} title={canDelete ? "Delete round" : "Keep at least one round"} className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-rose-300 disabled:opacity-30" aria-label="Delete round"><Trash2 size={14} /></button>
      </div>
    </div>
    {editingSettings && <div className="mt-2 flex items-end gap-2 border-t border-white/5 pt-2">
      {round.isEmpty && <label className="block flex-1 text-xs text-zinc-500">Question type
        <select value={typeDraft} onChange={(event) => setTypeDraft(event.target.value)} className="mt-1 h-8 w-full rounded border border-white/10 bg-zinc-900 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60">
          <option value="mixed">Mix</option>
          <option value="true_false">True/False</option>
          <option value="multiple_choice">Multiple Choice</option>
          <option value="written">Written</option>
        </select>
      </label>}
      <label className="block flex-1 text-xs text-zinc-500">Points (whole round)
        <input type="number" min="0" step="5" value={pointsDraft} onChange={(event) => setPointsDraft(event.target.value)} placeholder="Default for type" className="mt-1 h-8 w-full rounded border border-white/10 bg-zinc-900 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
      </label>
      <label className="block flex-1 text-xs text-zinc-500">Timer (seconds)
        <input type="number" min="1" step="5" value={timerDraft} onChange={(event) => setTimerDraft(event.target.value)} className="mt-1 h-8 w-full rounded border border-white/10 bg-zinc-900 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
      </label>
      <button type="button" onClick={commitSettings} className="mb-0.5 text-emerald-300 hover:text-emerald-200" aria-label="Save round points and timer"><Check size={15} /></button>
      <button type="button" onClick={() => setEditingSettings(false)} className="mb-0.5 text-zinc-500 hover:text-white" aria-label="Cancel points and timer edit"><X size={15} /></button>
    </div>}
    {editingDescription && <div className="mt-2 flex items-start gap-2 border-t border-white/5 pt-2">
      <textarea autoFocus value={descriptionDraft} onChange={(event) => setDescriptionDraft(event.target.value)} placeholder="Optional note read out before this round starts..." className="min-h-16 flex-1 resize-none rounded-md border border-white/10 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-200 outline-none focus:border-[#71E0DC]/60" />
      <div className="flex flex-col gap-1.5 pt-1">
        <button type="button" onClick={commitDescription} className="text-emerald-300 hover:text-emerald-200" aria-label="Save round note"><Check size={15} /></button>
        <button type="button" onClick={() => { setDescriptionDraft(round.description || ""); setEditingDescription(false); }} className="text-zinc-500 hover:text-white" aria-label="Cancel note"><X size={15} /></button>
      </div>
    </div>}
  </div>;
};

const QuestionListView = ({
  rounds, questions, currentIndex, hostIndex, isReviewing, gameStarted, eventOpen,
  displayedQuestion, goToQuestion, reviewQuestion, onBackToLive, onGoLiveWithThis,
  answersForQuestionIndex, gradedAnswers, players, hostAnswers, fairPlayStats,
  showAnswer, showFunFact, timeRemaining, viewPointsPerQuestion, viewTimerSeconds, viewWagerMode, viewWagerLimit, viewWagerTiming,
  onUpdateSettings, renameRound, describeRound, setEmptyRoundSettings, moveRound, deleteRound, createRound, createEmptyRound, moveQuestionToRound, duplicateQuestion, discardQuestion, addQuestionToRound, addLibraryQuestionToRound, addGeneratedQuestionsToRound, updateQuestionContent, editQuestionWithAi, askCoHost, saveQuestionToLibrary, venueId, approvedCategories, branding, markAnswer, addManualAnswer, editWager, releaseMode,
  hasRevealExtra, hasFunFact, hasAudio, isPlayingAudio, onToggleAudio, onRevealAnswer, onShowFunFact, startTimer, resetTimer, resetQuestion, resetQuestionAt,
}) => {
  // Only one round is shown at a time (see RoundHeader/RoundSwitcher) --
  // defaults to whichever round the live/reviewed question is in, and
  // re-syncs to that whenever hostIndex moves (Ask Question / Review), so
  // the view doesn't sit stale on a round nothing is happening in. A host
  // who deliberately picks a different round via the switcher stays there
  // until hostIndex itself changes next.
  const [activeRoundKey, setActiveRoundKey] = useState(() => {
    const liveRound = rounds.find((round) => hostIndex >= round.startIndex && hostIndex < round.startIndex + round.questions.length);
    return (liveRound || rounds[0])?.key || null;
  });
  useEffect(() => {
    const liveRound = rounds.find((round) => hostIndex >= round.startIndex && hostIndex < round.startIndex + round.questions.length);
    if (liveRound) setActiveRoundKey(liveRound.key);
    // Only hostIndex should trigger this -- re-running on every `rounds`
    // change would fight the round-created effect below and the switcher's
    // own manual selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostIndex]);
  // A freshly created round always lands last (see createRound) -- jump to
  // it so the host sees the round they just named/wrote a question for.
  const prevRoundCountRef = useRef(rounds.length);
  useEffect(() => {
    if (rounds.length > prevRoundCountRef.current) setActiveRoundKey(rounds[rounds.length - 1].key);
    prevRoundCountRef.current = rounds.length;
  }, [rounds]);
  const activeRound = rounds.find((round) => round.key === activeRoundKey) || rounds[0];
  const activeIndex = rounds.findIndex((round) => round.key === activeRound.key);

  const [manageRoundsOpen, setManageRoundsOpen] = useState(false);
  const [addRoundOpen, setAddRoundOpen] = useState(false);
  const [aiEditQuestionId, setAiEditQuestionId] = useState(null);
  const aiEditQuestion = questions.find((item) => item.id === aiEditQuestionId) || null;
  const [writeQuestionRoundKey, setWriteQuestionRoundKey] = useState(null);
  const writeQuestionRound = rounds.find((round) => round.key === writeQuestionRoundKey) || null;
  const [generateRoundKey, setGenerateRoundKey] = useState(null);
  const generateRound = rounds.find((round) => round.key === generateRoundKey) || null;
  const [coHostRoundKey, setCoHostRoundKey] = useState(null);
  const coHostRound = rounds.find((round) => round.key === coHostRoundKey) || null;
  const [libraryRoundKey, setLibraryRoundKey] = useState(null);
  const libraryRound = rounds.find((round) => round.key === libraryRoundKey) || null;
  const [libraryQuestions, setLibraryQuestions] = useState(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const openLibrary = (roundKey) => {
    setLibraryRoundKey(roundKey);
    if (libraryQuestions !== null || libraryLoading) return;
    setLibraryLoading(true);
    supabase.from("questions").select("*").order("created_at", { ascending: false }).then(({ data, error }) => {
      if (error) {
        console.warn("Library fetch unavailable:", error);
        toast.error("Couldn't load your question library");
        setLibraryQuestions([]);
      } else {
        setLibraryQuestions(data || []);
      }
      setLibraryLoading(false);
    });
  };
  const existingQuestionTexts = new Set(questions.map((question) => String(question.questionText || "").trim().toLowerCase()).filter(Boolean));
  return <div className="space-y-3">
    <section>
      <RoundHeader
        rounds={rounds}
        activeRound={activeRound}
        activeIndex={activeIndex}
        onSelectRound={setActiveRoundKey}
        onManageRounds={() => setManageRoundsOpen(true)}
        onDescribe={(description) => describeRound(activeRound, description)}
        onWriteQuestion={() => setWriteQuestionRoundKey(activeRound.key)}
        onAddFromLibrary={() => openLibrary(activeRound.key)}
        onGenerate={() => setGenerateRoundKey(activeRound.key)}
        onCoHost={() => setCoHostRoundKey(activeRound.key)}
        hasNextRound={activeIndex < rounds.length - 1}
        onNextRound={() => { if (activeIndex < rounds.length - 1) setActiveRoundKey(rounds[activeIndex + 1].key); }}
      />
      <div className="space-y-3">
        {activeRound.questions.map((question, localIndex) => {
          const index = activeRound.startIndex + localIndex;
          if (index === hostIndex && (isReviewing || gameStarted)) {
            return <QuestionStage key={question.id} question={displayedQuestion} index={hostIndex} total={questions.length} showAnswer={showAnswer} showFunFact={showFunFact} pointsPerQuestion={viewPointsPerQuestion} timerSeconds={viewTimerSeconds} timeRemaining={timeRemaining} wagerMode={viewWagerMode} wagerLimit={viewWagerLimit} wagerTiming={viewWagerTiming} onUpdateSettings={onUpdateSettings} onUpdateContent={(patch) => updateQuestionContent(displayedQuestion, patch)} onDuplicate={() => duplicateQuestion(displayedQuestion)} onAiEdit={() => setAiEditQuestionId(displayedQuestion.id)} onSaveToLibrary={saveQuestionToLibrary} branding={branding} players={players} answers={hostAnswers} fairPlayStats={fairPlayStats} gradedAnswers={gradedAnswers} markAnswer={markAnswer} addManualAnswer={addManualAnswer} editWager={editWager} setMode={releaseMode} isReviewing={isReviewing} hasRevealExtra={hasRevealExtra} hasFunFact={hasFunFact} hasAudio={hasAudio} isPlayingAudio={isPlayingAudio} onToggleAudio={onToggleAudio} onRevealAnswer={onRevealAnswer} onShowFunFact={onShowFunFact} startTimer={startTimer} resetTimer={resetTimer} resetQuestion={resetQuestion} onBackToLive={onBackToLive} onGoLiveWithThis={onGoLiveWithThis} categoryUsage={buildCategoryUsage(questions, { roundKey: activeRound.key, excludeQuestionId: displayedQuestion.id })} />;
          }
          const isLiveElsewhere = index === currentIndex && isReviewing;
          const state = isLiveElsewhere ? "live" : index < currentIndex ? "completed" : "upcoming";
          const questionAnswers = answersForQuestionIndex(index);
          const graded = questionAnswers.map((answer) => gradedAnswers[answerKey(answer)]).filter(Boolean);
          const correctCount = graded.filter((item) => item.status === "correct").length;
          return <CollapsedQuestionCard
            key={question.id}
            question={question}
            index={index}
            state={state}
            submittedCount={questionAnswers.length}
            playerCount={players.length}
            correctCount={correctCount}
            eventOpen={eventOpen}
            onAsk={() => goToQuestion(index, { startTimer: true })}
            onReview={isLiveElsewhere ? onBackToLive : () => reviewQuestion(index)}
            currentRoundName={activeRound.name}
            otherRounds={rounds.filter((item) => item.key !== activeRound.key)}
            categoryUsage={buildCategoryUsage(questions, { roundKey: activeRound.key, excludeQuestionId: question.id })}
            onMoveToRound={(targetRound) => moveQuestionToRound(question, targetRound)}
            onUpdateContent={(patch) => updateQuestionContent(question, patch)}
            onDuplicate={() => duplicateQuestion(question)}
            onDiscard={() => discardQuestion(question)}
            onResetQuestion={() => resetQuestionAt(index)}
            onAiEdit={() => setAiEditQuestionId(question.id)}
            onSaveToLibrary={saveQuestionToLibrary}
          />;
        })}
        {!activeRound.questions.length && <div className="rounded-lg border border-dashed border-white/15 bg-zinc-950/40 p-6 text-center">
          <p className="text-sm text-zinc-400">This round doesn't have any questions yet.</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Button type="button" size="sm" onClick={() => setGenerateRoundKey(activeRound.key)} className="gradient-btn"><Sparkles size={13} className="mr-1.5" />Generate</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setCoHostRoundKey(activeRound.key)} className="border-white/10 text-zinc-300 hover:text-white"><Bot size={13} className="mr-1.5" />AI Co-Host</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setWriteQuestionRoundKey(activeRound.key)} className="border-white/10 text-zinc-300 hover:text-white"><Pencil size={13} className="mr-1.5" />Write Question</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => openLibrary(activeRound.key)} className="border-white/10 text-zinc-300 hover:text-white"><List size={13} className="mr-1.5" />Add from Library</Button>
          </div>
        </div>}
      </div>
    </section>
    {manageRoundsOpen && <RoundManagerModal
      rounds={rounds}
      onRename={renameRound}
      onDescribe={describeRound}
      onSetRoundSettings={(round, settings) => (round.isEmpty ? setEmptyRoundSettings(round, settings) : onUpdateSettings({ points: settings.points, timerSeconds: settings.timerSeconds }, "round", round.questions[0]))}
      onMoveRound={moveRound}
      onDelete={deleteRound}
      onAddRound={() => setAddRoundOpen(true)}
      onClose={() => setManageRoundsOpen(false)}
    />}
    {addRoundOpen && <AddRoundModal onCreate={createRound} onCreateEmpty={createEmptyRound} onSaveToLibrary={saveQuestionToLibrary} approvedCategories={approvedCategories} categoryUsage={buildCategoryUsage(questions)} onClose={() => setAddRoundOpen(false)} />}
    {writeQuestionRound && <WriteQuestionModal round={writeQuestionRound} onCreate={(draft) => addQuestionToRound(writeQuestionRound, draft)} onSaveToLibrary={saveQuestionToLibrary} approvedCategories={approvedCategories} categoryUsage={buildCategoryUsage(questions, { roundKey: writeQuestionRound.key })} onClose={() => setWriteQuestionRoundKey(null)} />}
    {libraryRound && <LibraryPickerModal round={libraryRound} libraryQuestions={libraryQuestions} loading={libraryLoading} existingTexts={existingQuestionTexts} onInsert={(question) => addLibraryQuestionToRound(libraryRound, question)} onClose={() => setLibraryRoundKey(null)} />}
    {aiEditQuestion && <AiEditQuestionModal question={aiEditQuestion} onEdit={editQuestionWithAi} onApply={(patch) => updateQuestionContent(aiEditQuestion, patch)} onClose={() => setAiEditQuestionId(null)} />}
    {generateRound && <GenerateRoundModal round={generateRound} venueId={venueId} existingQuestionTexts={existingQuestionTexts} onAddAll={(candidates) => addGeneratedQuestionsToRound(generateRound, candidates)} onSaveToLibrary={saveQuestionToLibrary} onClose={() => setGenerateRoundKey(null)} />}
    {coHostRound && <CoHostChatModal round={coHostRound} onAsk={askCoHost} onAddCandidate={(candidate) => addGeneratedQuestionsToRound(coHostRound, [candidate])} onSaveToLibrary={saveQuestionToLibrary} onClose={() => setCoHostRoundKey(null)} />}
  </div>;
};

// Shared by CollapsedQuestionCard and QuestionStage -- inline editing of a
// question's actual content, right on the card it's hosted from, instead of
// forcing a trip to the standalone builder for something this small. Type
// conversion stays out of scope here (moving a question to a different
// array is a bigger operation, see moveQuestionToRound/addLibraryQuestionToRound
// for the established pattern) -- this edits the fields that don't require it.
const QuestionEditFields = ({ type, draft, setDraft, categoryUsage = null }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const updateWrong = (index, value) => setDraft((prev) => ({ ...prev, incorrectAnswers: prev.incorrectAnswers.map((answer, i) => (i === index ? value : answer)) }));

  const handleImageFile = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadQuestionMedia(file);
      setDraft((prev) => ({ ...prev, imageUrl: url }));
    } catch (error) {
      console.error("Upload image error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioFile = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      const url = await uploadQuestionAudio(file);
      setDraft((prev) => ({ ...prev, audioUrl: url }));
    } catch (error) {
      console.error("Upload audio error:", error);
      toast.error(error.message || "Failed to upload audio");
    } finally {
      setUploadingAudio(false);
    }
  };

  return <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
    <div>
      <input value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))} placeholder="Category" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
      <CategoryUsageHint category={draft.category} usage={categoryUsage} />
    </div>
    <textarea value={draft.questionText} onChange={(event) => setDraft((prev) => ({ ...prev, questionText: event.target.value }))} placeholder="Question" className="min-h-[70px] w-full resize-none rounded-md border border-white/10 bg-zinc-950/50 px-3 py-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
    {type === "true_false" ? <select value={draft.answer} onChange={(event) => setDraft((prev) => ({ ...prev, answer: event.target.value }))} className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-sm text-white">
      <option value="True">True</option>
      <option value="False">False</option>
    </select> : <input value={draft.answer} onChange={(event) => setDraft((prev) => ({ ...prev, answer: event.target.value }))} placeholder="Correct answer" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />}
    {type === "multiple_choice" && <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      {draft.incorrectAnswers.map((answer, index) => <input key={index} value={answer} onChange={(event) => updateWrong(index, event.target.value)} placeholder={`Wrong answer ${index + 1}`} className="h-9 rounded-md border border-white/10 bg-zinc-950/50 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />)}
    </div>}
    <input value={draft.funFact} onChange={(event) => setDraft((prev) => ({ ...prev, funFact: event.target.value }))} placeholder="Fun fact (optional)" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
    <div className="rounded-md border border-white/10 bg-zinc-950/30 p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Media (optional)</p>
        {draft.imageUrl && <div className="inline-flex rounded-md border border-white/10 bg-zinc-950/70 p-0.5">
          <button type="button" onClick={() => setDraft((prev) => ({ ...prev, imageTiming: "initial" }))} className={`rounded px-2 py-1 text-[11px] font-semibold ${draft.imageTiming !== "after_answer" ? "bg-[#71E0DC]/15 text-[#71E0DC]" : "text-zinc-400 hover:text-white"}`}>With question</button>
          <button type="button" onClick={() => setDraft((prev) => ({ ...prev, imageTiming: "after_answer" }))} className={`rounded px-2 py-1 text-[11px] font-semibold ${draft.imageTiming === "after_answer" ? "bg-[#71E0DC]/15 text-[#71E0DC]" : "text-zinc-400 hover:text-white"}`}>After answer</button>
        </div>}
      </div>
      <label className="flex h-14 cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/15 bg-zinc-950/40 px-3 hover:border-[#71E0DC]/40">
        {uploadingImage ? <Loader2 size={16} className="animate-spin text-[#71E0DC]" /> : draft.imageUrl ? <img src={draft.imageUrl} alt="Question media preview" className="h-10 w-10 rounded object-cover border border-white/10" /> : <Upload size={14} className="text-zinc-400" />}
        <span className="text-xs text-zinc-400">{draft.imageUrl ? "Click to replace the image" : "Click to upload an image, or paste a URL below"}</span>
        <input type="file" accept="image/*" className="hidden" disabled={uploadingImage} onChange={(event) => handleImageFile(event.target.files?.[0])} />
      </label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Link className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input value={draft.imageUrl} onChange={(event) => setDraft((prev) => ({ ...prev, imageUrl: event.target.value }))} placeholder="Image URL" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 pl-9 pr-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
        </div>
        {draft.imageUrl && <button type="button" onClick={() => setDraft((prev) => ({ ...prev, imageUrl: "" }))} className="shrink-0 text-xs text-zinc-500 hover:text-rose-300">Remove</button>}
      </div>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Music className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input value={draft.audioUrl} onChange={(event) => setDraft((prev) => ({ ...prev, audioUrl: event.target.value }))} placeholder="Audio URL (optional)" className="h-9 w-full rounded-md border border-white/10 bg-zinc-950/50 pl-9 pr-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
        </div>
        <label className={`flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-white/10 bg-zinc-950/50 px-3 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white ${uploadingAudio ? "opacity-60" : ""}`}>
          {uploadingAudio ? <Loader2 size={14} className="animate-spin" /> : "Upload"}
          <input type="file" accept="audio/*" className="hidden" disabled={uploadingAudio} onChange={(event) => handleAudioFile(event.target.files?.[0])} />
        </label>
        {draft.audioUrl && <button type="button" onClick={() => setDraft((prev) => ({ ...prev, audioUrl: "" }))} className="shrink-0 text-xs text-zinc-500 hover:text-rose-300">Remove</button>}
      </div>
    </div>
  </div>;
};

const draftFromQuestion = (question) => ({
  category: question.category || "",
  questionText: question.questionText || "",
  answer: question.answer || "",
  incorrectAnswers: [0, 1, 2].map((index) => (question.options || []).filter((option) => option !== question.answer)[index] || ""),
  funFact: question.funFact || "",
  imageUrl: question.imageUrl || "",
  imageTiming: normalizeImageTiming(question.imageTiming),
  audioUrl: question.audioUrl || "",
});

// Static, non-interactive preview of a question's answer options -- every
// card shows this at all times (see CollapsedQuestionCard), not just the
// live one. True/False is always green/red regardless of which is actually
// correct (a fixed convention, not a hint); multiple choice options are
// neutral since which one's correct only matters once the question is live.
const AnswerOptionPreview = ({ question }) => {
  if (question.type === "true_false") return <div className="mt-2.5 flex gap-2">
    <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300"><CheckCircle size={12} />True</span>
    <span className="flex items-center gap-1 rounded-full bg-rose-500/15 px-3 py-1 text-xs font-bold text-rose-300"><XCircle size={12} />False</span>
  </div>;
  if (question.type === "multiple_choice" && question.options?.length) return <div className="mt-2.5 flex flex-wrap gap-1.5">
    {question.options.map((option, i) => <span key={i} className="rounded-full bg-zinc-800 px-2.5 py-1 text-xs text-zinc-300">{option}</span>)}
  </div>;
  return null;
};

const CollapsedQuestionCard = ({ question, index, state, submittedCount, playerCount, correctCount, eventOpen, onAsk, onReview, currentRoundName, otherRounds, onMoveToRound, onUpdateContent, onDuplicate, onDiscard, onResetQuestion, onAiEdit, onSaveToLibrary, categoryUsage }) => {
  const meta = typeMeta[question.type] || typeMeta.written;
  const points = getQuestionPoints(question);
  const wagerLimit = Number(question.wagerLimit || 0);
  const timerSeconds = Number(question.timerSeconds || 30);
  const imageUrl = buildStorageUrl(question.imageUrl);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFromQuestion(question));
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setDraft(draftFromQuestion(question)); setEditing(true); };
  const handleSave = async () => {
    if (!draft.questionText.trim() || !draft.answer.trim()) return toast.error("Question and answer can't be empty");
    setSaving(true);
    const ok = await onUpdateContent({
      category: draft.category.trim() || "General",
      questionText: draft.questionText.trim(),
      answer: draft.answer.trim(),
      incorrectAnswers: question.type === "multiple_choice" ? draft.incorrectAnswers.map((item) => item.trim()).filter(Boolean).join("; ") : undefined,
      funFact: draft.funFact.trim(),
      imageUrl: draft.imageUrl.trim(),
      imageTiming: draft.imageTiming,
      audioUrl: draft.audioUrl.trim(),
    });
    setSaving(false);
    if (ok) setEditing(false);
  };

  const correctPct = state === "completed" && submittedCount > 0 ? Math.round((correctCount / submittedCount) * 100) : null;

  if (editing) {
    return <Card className="glass-card">
      <CardContent className="p-3.5">
        <QuestionEditFields type={question.type} draft={draft} setDraft={setDraft} categoryUsage={categoryUsage} />
        <div className="mt-2 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-8 border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 gradient-btn">{saving ? "Saving..." : "Save"}</Button>
        </div>
      </CardContent>
    </Card>;
  }

  return <Card className={`glass-card ${state === "live" ? "border-rose-400/40" : ""}`}>
    <CardContent className="p-3.5">
      <div className="flex items-start gap-3">
        {imageUrl && <img src={imageUrl} alt="Question media" className="h-16 w-16 shrink-0 rounded-lg border border-white/10 object-cover" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-zinc-500">{meta.label}{isBonusQuestion(question) && <span className="ml-1.5 text-amber-300" title="Bonus question">&#9733;</span>}<br />#{index + 1} {question.category}</p>
            <div className="flex shrink-0 items-center gap-2">
              {correctPct !== null && <span className="text-xs font-bold text-zinc-400">{correctPct}%</span>}
              {state === "live" && <span className="flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[11px] font-bold text-rose-200"><span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-pulse" />LIVE</span>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" title="More actions" aria-label="More actions" className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white"><MoreVertical size={15} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 border-white/10 bg-zinc-950 text-zinc-100">
                  <div className="px-2 py-1.5"><span className="inline-flex items-center rounded-md bg-[#71E0DC]/15 px-2 py-1 text-xs font-bold text-[#71E0DC]">{currentRoundName}</span></div>
                  {state !== "live" && otherRounds?.map((round) => <DropdownMenuItem key={round.key} onClick={() => onMoveToRound(round)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><ArrowRightLeft size={14} className="mr-2" />Move to {round.name}</DropdownMenuItem>)}
                  <div className="my-1 h-px bg-white/10" />
                  <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500">Options</p>
                  <DropdownMenuItem onClick={startEdit} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Pencil size={14} className="mr-2" />Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={onAiEdit} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Sparkles size={14} className="mr-2" />AI Edit</DropdownMenuItem>
                  {state === "completed" && <DropdownMenuItem onClick={onResetQuestion} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><RotateCcw size={14} className="mr-2" />Reset Question</DropdownMenuItem>}
                  {state === "completed" && <DropdownMenuItem onClick={onAsk} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><RefreshCw size={14} className="mr-2" />Reactivate Question</DropdownMenuItem>}
                  <DropdownMenuItem onClick={onDuplicate} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Copy size={14} className="mr-2" />Clone</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSaveToLibrary(question)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><List size={14} className="mr-2" />Save to Library</DropdownMenuItem>
                  {state !== "live" && <DropdownMenuItem onClick={onDiscard} className="cursor-pointer text-rose-300 focus:bg-zinc-900 focus:text-rose-200"><Trash2 size={14} className="mr-2" />Discard</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <p className="mt-1.5 text-sm font-semibold text-white">{question.questionText}</p>
          <AnswerOptionPreview question={question} />
          {state === "completed" && question.funFact && <p className="mt-2 rounded-md border border-dashed border-white/10 bg-white/5 p-2 text-xs text-zinc-400"><b className="text-zinc-300">Fun fact:</b> {question.funFact}</p>}
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-2.5">
        {state === "upcoming" && <Button size="sm" onClick={onAsk} disabled={!eventOpen} title={!eventOpen ? "Open the event to start asking questions" : undefined} className="h-8 gradient-btn text-xs disabled:opacity-40"><Play size={12} className="mr-1" />Ask Question</Button>}
        {state === "completed" && <Button size="sm" variant="outline" onClick={onReview} className="h-8 border-white/10 text-xs text-zinc-300 hover:text-white"><Eye size={12} className="mr-1" />Review</Button>}
        {state === "live" && <Button size="sm" variant="outline" onClick={onReview} className="h-8 border-rose-300/30 text-xs text-rose-200 hover:text-white">View Live</Button>}
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          {state === "completed" && <span>{correctCount > 0 ? `${correctCount} correct` : "Asked"} &middot; {submittedCount}/{playerCount || 0}</span>}
          <span className="flex items-center gap-1"><Tags size={12} />{points}pts</span>
          <span className="flex items-center gap-1"><Clock size={12} />{timerSeconds}s</span>
          {wagerLimit > 0 && <span className="text-purple-300">Wager</span>}
        </div>
      </div>
    </CardContent>
  </Card>;
};

const PLAYBACK_TOOLS = [
  { key: "teams", label: "Teams", icon: Trophy },
  { key: "run", label: "Run Sheet", icon: MonitorPlay },
  { key: "fairplay", label: "Fair Play", icon: AlertTriangle },
  { key: "disputes", label: "Disputes", icon: Clock },
  { key: "emergency", label: "Emergency", icon: Zap },
  { key: "feedback", label: "Feedback", icon: ThumbsUp },
];

// Per-question controls (timer, reveal, audio) moved into QuestionStage's own
// card -- see the unified card list above. What's left here is genuinely
// global: the drawer-tool row, the viewer-screen scene switcher (gated behind
// eventOpen -- none of those scenes have a real audience until the event is
// open, since ensureLiveGame/persistLiveState don't run before then either),
// and the Open Event action itself.
const PlaybackRail = ({ eventOpen, onOpenEvent, mode, setMode, rounds, introRoundKey, chooseIntroRound, onOpenDrawer, flaggedTeamCount }) => {
  const sceneActive = (key) => mode === key;
  const sceneButtonClass = (active) => `h-14 rounded-lg text-[11px] font-bold flex flex-col items-center justify-center gap-1 transition ${active ? "text-zinc-950" : "border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"}`;
  const sceneButtonStyle = (active) => active ? { background: "linear-gradient(90deg, var(--host-primary), var(--host-accent))" } : undefined;

  return <aside className="space-y-4">
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Playback</p>
          {eventOpen && <p className="text-[11px] text-zinc-600">event is open</p>}
        </div>
        <div className="flex flex-wrap gap-2 border-b border-white/10 px-3 py-3">
          {PLAYBACK_TOOLS.map(({ key, label, icon: Icon }) => <button key={key} type="button" onClick={() => onOpenDrawer(key)} className="relative flex items-center gap-1.5 rounded-md border border-white/10 bg-zinc-950/60 px-2.5 py-1.5 text-xs font-bold text-zinc-300 hover:border-white/25 hover:text-white">
            <Icon size={13} />{label}
            {key === "fairplay" && flaggedTeamCount > 0 && <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-400 text-[9px] font-black text-zinc-950">{flaggedTeamCount}</span>}
          </button>)}
        </div>
        <div className="p-3.5">
          {eventOpen ? <p className="rounded-lg border border-white/10 bg-zinc-950/60 px-3 py-4 text-center text-xs text-zinc-500">Use each question card&apos;s <b className="text-zinc-300">Ask Question</b> button to go live, reveal the answer, and grade.</p>
            : <Button onClick={onOpenEvent} className="w-full h-11 gradient-btn text-sm font-bold"><Play size={16} className="mr-2" />Open Event</Button>}
        </div>
      </CardContent>
    </Card>

    {eventOpen && <Card className="glass-card">
      <CardContent className="p-3.5">
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Viewer Screen</p>
          <select aria-label="Round intro target" value={introRoundKey} onChange={(event) => chooseIntroRound(event.target.value)} className="h-7 min-w-[110px] rounded-md bg-zinc-950 border border-white/10 px-2 text-[11px] text-zinc-300 outline-none focus:border-[#71E0DC]/60">
            {rounds.map((round) => <option key={round.key} value={round.key}>{round.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={() => setMode("qr")} className={sceneButtonClass(sceneActive("qr"))} style={sceneButtonStyle(sceneActive("qr"))}><QrCode size={16} /><span>Lobby / QR</span></button>
          <button type="button" onClick={() => setMode("categories")} className={sceneButtonClass(sceneActive("categories"))} style={sceneButtonStyle(sceneActive("categories"))}><Tags size={16} /><span>Round Intro</span></button>
          <button type="button" onClick={() => setMode("question")} className={sceneButtonClass(sceneActive("question"))} style={sceneButtonStyle(sceneActive("question"))}><MonitorPlay size={16} /><span>Question</span></button>
          <button type="button" onClick={() => setMode("leaderboard")} className={sceneButtonClass(sceneActive("leaderboard"))} style={sceneButtonStyle(sceneActive("leaderboard"))}><Trophy size={16} /><span>Leaderboard</span></button>
          <button type="button" onClick={() => setMode("winners")} className={sceneButtonClass(sceneActive("winners"))} style={sceneButtonStyle(sceneActive("winners"))}><Sparkles size={16} /><span>Winners</span></button>
          <button type="button" onClick={() => setMode("feedback")} className={sceneButtonClass(sceneActive("feedback"))} style={sceneButtonStyle(sceneActive("feedback"))}><MessageSquare size={16} /><span>Feedback</span></button>
        </div>
      </CardContent>
    </Card>}
  </aside>;
};

const TopBar = ({ navigate, id, embedded, sessionName, venueName, questions, players, currentIndex, liveStatus, openPresentation, setFocusMode, progress, branding, customizeOpen, setCustomizeOpen, endSession, collapsed, onToggleCollapse, answersPaused, onToggleAnswersPaused }) => <div className="border-b border-white/10 bg-zinc-950/85 sticky top-0 z-20 backdrop-blur"><div className={`max-w-[1680px] mx-auto px-4 lg:px-6 flex items-center gap-3 transition-[padding] ${collapsed ? "py-1.5" : "py-3"}`}>{!collapsed && !embedded && <Button variant="ghost" onClick={() => navigate(`/session/${id}`)} className="text-zinc-400 hover:text-white h-9 w-9 p-0 shrink-0" aria-label="Back to session"><ArrowLeft size={18} /></Button>}{!collapsed && branding?.logoUrl && <img src={branding.logoUrl} alt={branding.name || "Host logo"} className="h-10 w-10 rounded-md bg-white object-contain p-1 shrink-0" />}<div className="min-w-0 flex-1">{!collapsed && <p className="text-xs font-bold uppercase tracking-wide truncate" style={{ color: "var(--host-primary)" }}>{venueName || branding?.name || "Live Trivia"}</p>}<h1 className={`font-bold truncate ${collapsed ? "text-sm" : ""}`}>{sessionName}</h1>{!collapsed && <p className="text-xs text-zinc-500">{players.length} player{players.length === 1 ? "" : "s"} connected</p>}</div>{!collapsed && <div className="flex items-center gap-2 flex-wrap justify-end"><Badge className={liveStatus === "live" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20" : "bg-zinc-800 text-zinc-300"}><Wifi size={13} className="mr-1" />{liveStatus === "live" ? "Live" : "Connecting"}</Badge><Badge className="bg-zinc-800 text-zinc-300">Question {currentIndex + 1} of {questions.length}</Badge><Button onClick={onToggleAnswersPaused} title="Session-wide -- stays on or off across every question until you flip it back. Join, feedback, and idea submission on phones are never affected." className={answersPaused ? "bg-amber-400/15 text-amber-300 border border-amber-400/30 hover:bg-amber-400/20" : "bg-transparent border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"}>{answersPaused ? <Unlock size={16} className="mr-2" /> : <Lock size={16} className="mr-2" />}Phone Answers: {answersPaused ? "Paused" : "On"}</Button><Button onClick={endSession} className="bg-amber-300 text-zinc-950 hover:bg-amber-200"><Save size={16} className="mr-2" />End Session</Button><DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" className="h-10 w-10 border-white/10 p-0 text-zinc-300 hover:text-white" aria-label="Host screen options"><MoreHorizontal size={18} /></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="border-white/10 bg-zinc-950 text-zinc-100"><DropdownMenuItem onClick={() => setCustomizeOpen((value) => !value)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Palette size={15} className="mr-2" />{customizeOpen ? "Hide Customize" : "Customize"}</DropdownMenuItem><DropdownMenuItem onClick={() => setFocusMode(true)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Maximize2 size={15} className="mr-2" />Focus Mode</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>}<Button variant="outline" onClick={onToggleCollapse} className="h-8 w-8 shrink-0 border-white/10 p-0 text-zinc-400 hover:text-white" aria-label={collapsed ? "Expand toolbar" : "Minimize toolbar"} title={collapsed ? "Expand toolbar" : "Minimize toolbar"}><ChevronLeft size={15} className={`transition-transform ${collapsed ? "-rotate-90" : "rotate-90"}`} /></Button><div className="w-px h-7 bg-white/10 shrink-0" /><Button onClick={openPresentation} className="shrink-0 text-zinc-950 font-bold shadow-lg shadow-[#71E0DC]/20" style={{ background: "linear-gradient(90deg, var(--host-primary), var(--host-accent))" }} title="Opens the audience screen in a new tab, for a TV or projector"><MonitorPlay size={16} className="mr-2" />Present<ExternalLink size={13} className="ml-2 opacity-70" /></Button></div>{!collapsed && <div className="h-1 bg-zinc-900"><div className="h-1 transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg, var(--host-primary), var(--host-accent))" }} /></div>}</div>;

const PresentationControls = ({ mode, setMode, rounds, currentIndex, currentRound, introRoundKey, setIntroRoundKey }) => {
  const nextRound = rounds.find((round) => round.startIndex > currentIndex);
  const suggestedRound = currentRound && currentIndex >= currentRound.startIndex + currentRound.questions.length - 1 && nextRound ? nextRound : currentRound;
  const selectedIntroKey = introRoundKey || suggestedRound?.key || currentRound?.key || "";
  const chooseIntroRound = (roundKey) => {
    setIntroRoundKey(roundKey);
    if (mode === "categories") setMode("categories", roundKey);
  };

  return <Card className="glass-card mb-5"><CardContent className="p-4 lg:p-5"><div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"><div><p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Presentation</p><p className="text-sm text-zinc-300 mt-1">Choose what the audience screen is showing.</p></div><div className="flex items-center gap-2 flex-wrap"><Button size="sm" onClick={() => setMode("qr")} className={mode === "qr" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><QrCode size={15} className="mr-2" />QR Code</Button><Button size="sm" onClick={() => setMode("categories", selectedIntroKey)} className={mode === "categories" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><Tags size={15} className="mr-2" />Round Intro</Button><select aria-label="Round intro target" value={selectedIntroKey} onChange={(event) => chooseIntroRound(event.target.value)} className="h-9 min-w-[150px] rounded-md bg-zinc-950 border border-white/10 px-3 text-sm text-zinc-200">{rounds.map((round) => <option key={round.key} value={round.key}>{round.name}</option>)}</select><Button size="sm" onClick={() => setMode("question")} className={mode === "question" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><MonitorPlay size={15} className="mr-2" />Question</Button><Button size="sm" onClick={() => setMode("leaderboard")} className={mode === "leaderboard" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><Trophy size={15} className="mr-2" />Leaderboard</Button><Button size="sm" onClick={() => setMode("winners")} className={mode === "winners" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><Sparkles size={15} className="mr-2" />Winners</Button><Button size="sm" onClick={() => setMode("feedback")} className={mode === "feedback" ? "gradient-btn" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}><MessageSquare size={15} className="mr-2" />Feedback</Button></div></div></CardContent></Card>;
};

const HostCustomizePanel = ({ branding, defaultBranding, onSave, onSaveDefault, onUseDefault, onClose }) => {
  const [form, setForm] = useState(() => normalizeBranding(branding));
  useEffect(() => setForm(normalizeBranding(branding)), [branding]);
  const safeForm = normalizeBranding(form);
  const safeDefault = normalizeBranding(defaultBranding);
  const update = (key, value) => setForm((current) => ({ ...normalizeBranding(current), [key]: value }));
  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file for the logo");
    try {
      update("logoUrl", await uploadQuestionMedia(file));
    } catch (error) {
      console.error("Upload logo error:", error);
      toast.error(error.message || "Failed to upload logo");
    }
  };
  return <Card className="glass-card mb-4"><CardContent className="p-4"><div className="flex items-center justify-between gap-3 mb-4"><div><h2 className="text-lg font-bold text-white flex items-center gap-2"><Palette size={18} style={{ color: "var(--host-primary)" }} />Customize Host Screen</h2><p className="text-xs text-zinc-500">Default branding is used for new host sessions. This session can still be customized.</p></div><Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white">Close</Button></div><div className="grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-4"><div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3 flex flex-col items-center justify-center min-h-36">{safeForm.logoUrl ? <img src={safeForm.logoUrl} alt="Logo preview" className="max-h-24 max-w-full rounded-md bg-white object-contain p-2" /> : <div className="h-24 w-24 rounded-md border border-white/10 bg-zinc-900 flex items-center justify-center text-zinc-500"><Image size={28} /></div>}<p className="mt-3 text-sm font-bold text-white text-center">{safeForm.name || "Host Name"}</p></div><div className="space-y-3"><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><label className="text-xs text-zinc-400">Host name<input value={safeForm.name || ""} onChange={(event) => update("name", event.target.value)} className="mt-1 h-10 w-full rounded-md bg-zinc-950 border border-white/10 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label><label className="text-xs text-zinc-400">Logo URL<input value={safeForm.logoUrl || ""} onChange={(event) => update("logoUrl", event.target.value)} placeholder="https://..." className="mt-1 h-10 w-full rounded-md bg-zinc-950 border border-white/10 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label></div><div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"><label className="text-xs text-zinc-400">Upload logo<span className="mt-1 h-10 rounded-md border border-white/10 bg-zinc-950 px-3 text-zinc-200 flex items-center gap-2 cursor-pointer hover:border-[#71E0DC]/50"><Upload size={15} />Choose file<input type="file" accept="image/*" onChange={uploadLogo} className="hidden" /></span></label><ColorField label="Primary color" value={safeForm.primaryColor} onChange={(value) => update("primaryColor", value)} /><ColorField label="Accent color" value={safeForm.accentColor} onChange={(value) => update("accentColor", value)} /></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end"><div><div className="mb-1 flex items-center justify-between"><span className="text-xs text-zinc-400">Correct answer color (present screen)</span>{safeForm.correctColor && <button type="button" onClick={() => update("correctColor", "")} className="text-[11px] font-semibold text-[#71E0DC] hover:underline">Match brand primary</button>}</div><div className="flex h-10 rounded-md border border-white/10 bg-zinc-950 overflow-hidden focus-within:border-[#71E0DC]/60"><input type="color" value={sanitizeHexColor(safeForm.correctColor, safeForm.primaryColor)} onChange={(event) => update("correctColor", event.target.value)} className="h-10 w-12 border-0 bg-transparent p-1" /><input value={safeForm.correctColor} onChange={(event) => update("correctColor", event.target.value)} placeholder={`Auto (${safeForm.primaryColor})`} className="min-w-0 flex-1 bg-transparent px-2 text-white outline-none placeholder:text-zinc-600" /></div></div><ColorField label="Answer option color (present screen)" value={safeForm.optionColor} onChange={(value) => update("optionColor", value)} /></div><label className="block text-xs text-zinc-400">Lobby tagline (present screen)<input value={safeForm.lobbyTagline} onChange={(event) => update("lobbyTagline", event.target.value)} placeholder={DEFAULT_BRANDING.lobbyTagline} className="mt-1 h-10 w-full rounded-md bg-zinc-950 border border-white/10 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label><label className="block text-xs text-zinc-400">Paused-answers message (player phones)<input value={safeForm.pausedMessage} onChange={(event) => update("pausedMessage", event.target.value)} placeholder={DEFAULT_BRANDING.pausedMessage} className="mt-1 h-10 w-full rounded-md bg-zinc-950 border border-white/10 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label><div className="rounded-md border border-white/10 bg-zinc-950/50 p-3"><div className="flex items-center justify-between gap-3 flex-wrap"><div><p className="text-sm font-semibold text-white">Current default</p><p className="text-xs text-zinc-500">{safeDefault.name} · {safeDefault.primaryColor} / {safeDefault.accentColor}</p></div><Button size="sm" variant="outline" onClick={() => setForm(normalizeBranding(onUseDefault()))} className="border-white/10 text-zinc-300 hover:text-white">Use Default</Button></div></div><div className="flex justify-end gap-2 pt-1 flex-wrap"><Button variant="outline" onClick={() => setForm(DEFAULT_BRANDING)} className="border-white/10 text-zinc-300 hover:text-white">Reset</Button><Button variant="outline" onClick={() => onSaveDefault(safeForm)} className="border-white/10 text-zinc-300 hover:text-white">Save as Default</Button><Button onClick={() => onSave(safeForm)} className="text-zinc-950 font-semibold hover:opacity-90" style={{ background: `linear-gradient(90deg, ${safeForm.primaryColor}, ${safeForm.accentColor})` }}><Save size={16} className="mr-2" />Save This Session</Button></div></div></div></CardContent></Card>;
};

const ColorField = ({ label, value, onChange }) => { const safeValue = sanitizeHexColor(value, DEFAULT_BRANDING.primaryColor); return <label className="text-xs text-zinc-400">{label}<div className="mt-1 flex h-10 rounded-md border border-white/10 bg-zinc-950 overflow-hidden focus-within:border-[#71E0DC]/60"><input type="color" value={safeValue} onChange={(event) => onChange(event.target.value)} className="h-10 w-12 border-0 bg-transparent p-1" /><input value={value || ""} onChange={(event) => onChange(event.target.value)} className="min-w-0 flex-1 bg-transparent px-2 text-white outline-none" /></div></label>; };

const sentimentCounts = (items) => ({
  like: items.filter((item) => item.sentiment === "like").length,
  dislike: items.filter((item) => item.sentiment === "dislike").length,
});

const LiveFeedbackPanel = ({ feedback, categoryFeedback, currentIndex, displayedQuestion }) => {
  const currentFeedback = feedback.filter((item) => Number(item.questionIndex) === Number(currentIndex));
  const questionCounts = sentimentCounts(currentFeedback);
  const currentCategoryFeedback = categoryFeedback.filter((item) => item.roundName === displayedQuestion?.roundName || item.category === displayedQuestion?.category || Number(item.questionIndex) === Number(currentIndex));
  const categoryCounts = sentimentCounts(currentCategoryFeedback);
  const recentVotes = [...feedback.map((item) => ({ ...item, kind: "Question" })), ...categoryFeedback.map((item) => ({ ...item, kind: "Category" }))].sort((a, b) => String(b.submittedAt || "").localeCompare(String(a.submittedAt || ""))).slice(0, 14);

  return <Card className="glass-card"><CardContent className="p-4 space-y-4"><div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2 text-white font-semibold"><ThumbsUp size={18} className="text-[#71E0DC]" />Live Feedback</div><Badge className="bg-zinc-800 text-zinc-300">{feedback.length + categoryFeedback.length}</Badge></div><div className="grid grid-cols-2 gap-3"><FeedbackMetric title="This Question" likes={questionCounts.like} dislikes={questionCounts.dislike} /><FeedbackMetric title="Round/Categories" likes={categoryCounts.like} dislikes={categoryCounts.dislike} /></div><div><p className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-500">Recent responses</p><div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">{recentVotes.map((vote, index) => <div key={`${vote.kind}-${vote.playerId}-${vote.submittedAt}-${index}`} className="rounded-md border border-white/10 bg-zinc-950/60 p-3"><div className="flex items-center justify-between gap-2"><span className="text-sm font-bold text-white truncate">{vote.playerName || "Team"}</span><Badge className={vote.sentiment === "like" ? "bg-[#71E0DC]/15 text-[#71E0DC] border border-[#71E0DC]/20" : "bg-red-400/15 text-red-300 border border-red-400/20"}>{vote.sentiment === "like" ? <ThumbsUp size={12} className="mr-1" /> : <ThumbsDown size={12} className="mr-1" />}{vote.sentiment === "like" ? "Liked" : "Disliked"}</Badge></div><p className="mt-1 text-xs text-zinc-500">{vote.kind}: {vote.kind === "Category" ? vote.category : (vote.questionText || `Question ${Number(vote.questionIndex || 0) + 1}`)}</p></div>)}{!recentVotes.length && <p className="rounded-lg border border-white/10 bg-zinc-950/50 p-4 text-center text-xs text-zinc-500">Thumbs-up and thumbs-down responses will appear here live.</p>}</div></div></CardContent></Card>;
};

const FeedbackMetric = ({ title, likes, dislikes }) => <div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><p className="text-xs font-bold uppercase tracking-wide text-zinc-500">{title}</p><div className="mt-3 flex items-center gap-3"><span className="inline-flex items-center gap-1.5 rounded-full bg-[#71E0DC]/15 px-2.5 py-1 text-sm font-bold text-[#71E0DC]"><ThumbsUp size={15} />{likes}</span><span className="inline-flex items-center gap-1.5 rounded-full bg-red-400/15 px-2.5 py-1 text-sm font-bold text-red-300"><ThumbsDown size={15} />{dislikes}</span></div></div>;

const LiveSignalsPanel = ({ feedback, categoryFeedback, ideas, currentIndex, displayedQuestion }) => {
  const [dismissed, setDismissed] = useState([]);
  const dismiss = (key) => setDismissed((current) => [...new Set([...current, key])]);
  const isDismissed = (key) => dismissed.includes(key);
  const currentFeedback = feedback.filter((item) => Number(item.questionIndex) === Number(currentIndex));
  const questionCounts = sentimentCounts(currentFeedback);
  const categoryRows = Object.values(categoryFeedback.reduce((groups, item) => {
    const label = item.category || item.roundName || "Category";
    if (!groups[label]) groups[label] = { key: `category-${label}`, label, likes: 0, dislikes: 0, likedBy: [], dislikedBy: [] };
    const teamName = item.playerName || "Team";
    if (item.sentiment === "like") { groups[label].likes += 1; groups[label].likedBy.push(teamName); }
    if (item.sentiment === "dislike") { groups[label].dislikes += 1; groups[label].dislikedBy.push(teamName); }
    return groups;
  }, {})).sort((a, b) => (b.likes + b.dislikes) - (a.likes + a.dislikes)).slice(0, 4);
  const recentIdeas = [...ideas].sort((a, b) => String(b.submittedAt || "").localeCompare(String(a.submittedAt || ""))).slice(0, 4);
  const currentQuestionFlag = questionCounts.dislike > questionCounts.like && questionCounts.dislike > 0 ? { key: `flag-${currentIndex}`, label: `Question ${Number(currentIndex) + 1} flagged confusing`, detail: displayedQuestion?.questionText || "" } : null;
  const signals = [
    ...categoryRows.map((row) => ({ ...row, type: "category" })),
    ...(currentQuestionFlag ? [currentQuestionFlag] : []),
    ...recentIdeas.map((idea, index) => ({ key: `idea-${idea.playerId || "player"}-${idea.submittedAt || index}`, type: "idea", label: idea.category || idea.question || "Player idea", detail: idea.question || idea.category || "", playerName: idea.playerName || "Team" })),
  ].filter((signal) => !isDismissed(signal.key)).slice(0, 8);
  const saveIdea = (signal) => toast.success(signal.type === "idea" ? "Idea saved for review" : "Signal saved");
  const markReview = () => toast.success(`Question ${Number(currentIndex) + 1} marked for review`);
  const favoriteCategory = (label) => toast.success(`${label} saved as a favorite category`);

  return <Card className="glass-card border-[#71E0DC]/10"><CardContent className="p-4"><div className="mb-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2 text-white font-semibold"><Sparkles size={18} className="text-[#71E0DC]" />Live Signals</div><Badge className="bg-zinc-800 text-zinc-300">{feedback.length + categoryFeedback.length + ideas.length}</Badge></div><div className="mb-3 grid grid-cols-2 gap-2"><FeedbackMetric title="Question" likes={questionCounts.like} dislikes={questionCounts.dislike} /><div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Ideas</p><p className="mt-2 text-2xl font-black text-[#AEB2EF]">{ideas.length}</p></div></div><div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">{signals.map((signal) => <div key={signal.key} className="rounded-lg border border-white/10 bg-zinc-950/65 p-3"><div className="flex items-start justify-between gap-3"><div className="min-w-0">{signal.type === "category" ? <div className="min-w-0"><div className="flex min-w-0 flex-wrap items-center gap-2"><span className="truncate text-sm font-bold text-white">{signal.label}</span><span className="inline-flex items-center gap-1 rounded-full bg-[#71E0DC]/15 px-2 py-0.5 text-xs font-bold text-[#71E0DC]"><ThumbsUp size={12} />{signal.likes}</span><span className="inline-flex items-center gap-1 rounded-full bg-red-400/15 px-2 py-0.5 text-xs font-bold text-red-300"><ThumbsDown size={12} />{signal.dislikes}</span></div>{Boolean(signal.likedBy?.length) && <p className="mt-1 truncate text-xs text-[#71E0DC]">Liked: {signal.likedBy.join(", ")}</p>}{Boolean(signal.dislikedBy?.length) && <p className="mt-0.5 truncate text-xs text-red-300">Disliked: {signal.dislikedBy.join(", ")}</p>}</div> : signal.type === "idea" ? <><p className="text-sm font-bold text-white line-clamp-2">"{signal.label}"</p><p className="mt-1 text-xs text-zinc-500">{signal.playerName}</p></> : <><p className="text-sm font-bold text-red-200">{signal.label}</p><p className="mt-1 line-clamp-2 text-xs text-zinc-500">{signal.detail}</p></>}</div><button type="button" onClick={() => dismiss(signal.key)} className="shrink-0 text-zinc-600 hover:text-zinc-300" aria-label="Dismiss signal">x</button></div><div className="mt-2 flex flex-wrap gap-1.5">{signal.type === "category" && <Button size="sm" variant="outline" onClick={() => favoriteCategory(signal.label)} className="h-7 border-white/10 px-2 text-xs text-zinc-300 hover:text-white">Favorite</Button>}{signal.type === "idea" && <Button size="sm" variant="outline" onClick={() => saveIdea(signal)} className="h-7 border-white/10 px-2 text-xs text-zinc-300 hover:text-white">Save Idea</Button>}{signal.type !== "idea" && <Button size="sm" variant="outline" onClick={markReview} className="h-7 border-white/10 px-2 text-xs text-zinc-300 hover:text-white">Review Q</Button>}</div></div>)}{!signals.length && <p className="rounded-lg border border-white/10 bg-zinc-950/60 p-4 text-center text-xs text-zinc-500">Live category reactions, question flags, and player ideas will appear here without covering answers.</p>}</div></CardContent></Card>;
};

const AnswerHistoryPanel = ({ rounds, players, leaderboard, answers, gradedAnswers, currentIndex, markAnswer, openScoreModal, reviewQuestion }) => {
  const [selectedIndex, setSelectedIndex] = useState(Number(currentIndex || 0));
  useEffect(() => setSelectedIndex(Number(currentIndex || 0)), [currentIndex]);
  const questions = rounds.flatMap((round) => (round.questions || []).map((question, localIndex) => ({ ...question, roundName: question.roundName || round.name, absoluteIndex: round.startIndex + localIndex })));
  const selectedQuestion = questions.find((question) => Number(question.absoluteIndex) === Number(selectedIndex)) || questions[0];
  const selectedAnswers = answers.filter((answer) => Number(answer.questionIndex) === Number(selectedQuestion?.absoluteIndex));
  const answerByTeam = new Map(selectedAnswers.map((answer) => [answer.playerId, answer]));
  const teamMap = new Map();
  leaderboard.forEach((team) => teamMap.set(team.id, { id: team.id, name: team.name || "Team", score: Number(team.score || 0) }));
  players.forEach((player) => teamMap.set(player.id, { id: player.id, name: player.name || "Team", score: teamMap.get(player.id)?.score || 0 }));
  answers.forEach((answer) => {
    if (!teamMap.has(answer.playerId)) teamMap.set(answer.playerId, { id: answer.playerId, name: answer.playerName || "Team", score: 0 });
  });
  const teams = [...teamMap.values()].sort((a, b) => a.name.localeCompare(b.name));
  const answeredCount = teams.filter((team) => answerByTeam.has(team.id)).length;
  const correctCount = selectedAnswers.filter((answer) => gradedAnswers[answerKey(answer)]?.status === "correct").length;
  const statusBadge = (answer) => {
    if (!answer) return <Badge className="bg-zinc-800 text-zinc-400">Missing</Badge>;
    const status = gradedAnswers[answerKey(answer)]?.status;
    if (status === "correct") return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"><CheckCircle size={12} className="mr-1" />Correct</Badge>;
    if (status === "incorrect") return <Badge className="bg-red-500/15 text-red-300 border border-red-500/20"><XCircle size={12} className="mr-1" />Incorrect</Badge>;
    return <Badge className="bg-amber-500/15 text-amber-200 border border-amber-500/20">Ungraded</Badge>;
  };

  return <Card className="glass-card"><CardContent className="p-4 space-y-4"><div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2 text-white font-semibold"><List size={18} className="text-[#71E0DC]" />Full Game Answers</div><Badge className="bg-zinc-800 text-zinc-300">{answers.length} total</Badge></div><select value={selectedQuestion?.absoluteIndex ?? ""} onChange={(event) => setSelectedIndex(Number(event.target.value))} className="h-10 w-full rounded-md border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60">{questions.map((question) => <option key={question.absoluteIndex} value={question.absoluteIndex}>Q{question.absoluteIndex + 1} · {question.roundName || "Round"} · {question.category || "General"}</option>)}</select>{selectedQuestion && <div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><div className="mb-2 flex flex-wrap items-center gap-2"><Badge className="bg-[#71E0DC]/15 text-[#71E0DC]">Q{selectedQuestion.absoluteIndex + 1}</Badge><Badge className="bg-zinc-800 text-zinc-300">{answeredCount}/{teams.length} answered</Badge><Badge className="bg-emerald-500/15 text-emerald-300">{correctCount} correct</Badge></div><p className="text-sm font-bold text-white line-clamp-3">{selectedQuestion.questionText}</p><p className="mt-2 text-xs text-zinc-500">Answer: <span className="text-emerald-300">{selectedQuestion.answer || "Not set"}</span></p><Button size="sm" variant="outline" onClick={() => reviewQuestion(selectedQuestion.absoluteIndex)} className="mt-3 h-8 border-white/10 text-zinc-300 hover:text-white">Review This Question</Button></div>}<div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">{teams.map((team) => { const answer = answerByTeam.get(team.id); const grade = answer ? gradedAnswers[answerKey(answer)] : null; return <div key={team.id} className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><div className="mb-2 flex items-center justify-between gap-2"><span className="min-w-0 truncate text-sm font-bold text-white">{team.name}</span>{statusBadge(answer)}</div><div className="rounded-md bg-zinc-900/80 px-3 py-2 text-sm text-zinc-200 break-words">{answer?.answer || "No answer submitted"}</div>{answer && <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500"><span>{new Date(answer.submittedAt || Date.now()).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>{hasSubmittedWager(answer) && <span className="rounded-full bg-purple-400/15 px-2 py-0.5 font-bold text-purple-200">Wager {Number(answer.wagerAmount || 0)}</span>}<span className="rounded-full bg-zinc-800 px-2 py-0.5 font-bold text-zinc-300">Points {Number(grade?.points ?? answer.points ?? 0)}</span>{answer.manuallyEntered && <span className="rounded-full bg-[#71E0DC]/15 px-2 py-0.5 font-bold text-[#71E0DC]">Manual</span>}</div>}<div className="mt-3 flex flex-wrap gap-2">{answer ? <><Button size="sm" onClick={() => markAnswer(answer, "correct")} className="h-8 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"><CheckCircle size={13} className="mr-1" />Correct</Button><Button size="sm" onClick={() => markAnswer(answer, "incorrect")} className="h-8 bg-red-500/20 text-red-200 hover:bg-red-500/30"><XCircle size={13} className="mr-1" />Incorrect</Button><Button size="sm" variant="outline" onClick={() => openScoreModal(team.id, { answer })} className="h-8 border-white/10 text-zinc-300 hover:text-white">Adjust</Button></> : <Button size="sm" variant="outline" onClick={() => openScoreModal(team.id)} className="h-8 border-white/10 text-zinc-300 hover:text-white">Adjust Score</Button>}</div></div>; })}{!teams.length && <p className="rounded-lg border border-white/10 bg-zinc-950/50 p-4 text-center text-xs text-zinc-500">Teams will appear here once players join or you add them.</p>}</div></CardContent></Card>;
};

const EmergencyPanel = ({ currentIndex, emergencyOverride, generatedEmergency, emergencyLoading, generateEmergencyQuestion, activateEmergencyQuestion, clearEmergency, startTimerOnly, goToQuestion, editBuild }) => {
  return <Card className="glass-card"><CardContent className="p-4 space-y-4"><div><div className="flex items-center gap-2 text-white font-semibold"><Zap size={18} className="text-[#71E0DC]" />Emergency Host Tools</div><p className="text-xs text-zinc-500 mt-1">Quick fixes for a live hosting moment.</p></div>{emergencyOverride && <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-100"><p className="font-bold">Emergency question is live</p><p className="mt-1 line-clamp-2">{emergencyOverride.questionText}</p><Button size="sm" variant="outline" onClick={clearEmergency} className="mt-2 h-8 border-amber-300/30 text-amber-100 hover:text-white">Return to original</Button></div>}<div className="grid grid-cols-2 gap-2"><Button size="sm" onClick={() => generateEmergencyQuestion("replacement")} disabled={emergencyLoading} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100">{emergencyLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}Replacement</Button><Button size="sm" onClick={() => generateEmergencyQuestion("tiebreaker")} disabled={emergencyLoading} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100"><Trophy size={14} className="mr-1" />Tiebreaker</Button><Button size="sm" onClick={() => startTimerOnly(60)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100"><Timer size={14} className="mr-1" />60s Timer</Button><Button size="sm" onClick={() => goToQuestion(currentIndex + 1, { startTimer: true, pauseBeforeBonus: false })} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100"><ChevronRight size={14} className="mr-1" />Skip</Button></div>{generatedEmergency && <div className="rounded-lg border border-white/10 bg-zinc-950/70 p-3"><div className="flex items-center justify-between gap-2 mb-2"><Badge className="bg-[#71E0DC]/15 text-[#71E0DC] border border-[#71E0DC]/20">{generatedEmergency.category}</Badge><Button size="sm" onClick={() => activateEmergencyQuestion(generatedEmergency)} className="h-8 gradient-btn">Use Live</Button></div><p className="text-sm font-bold text-white">{generatedEmergency.questionText}</p><p className="mt-2 text-sm text-[#71E0DC]">Answer: {generatedEmergency.answer}</p>{generatedEmergency.funFact && <p className="mt-2 text-xs text-zinc-400">{generatedEmergency.funFact}</p>}</div>}<Button size="sm" variant="outline" onClick={editBuild} className="w-full border-white/10 text-zinc-300 hover:text-white"><Pencil size={14} className="mr-2" />Open Builder for Bigger Fixes</Button></CardContent></Card>;
};

const DisputesPanel = ({ currentIndex, addDisputeNote, disputeNotes }) => {
  const [note, setNote] = useState("");
  const saveNote = () => {
    addDisputeNote(note);
    setNote("");
  };
  return <Card className="glass-card"><CardContent className="p-4 space-y-4"><div><div className="flex items-center gap-2 text-white font-semibold"><Clock size={18} className="text-[#71E0DC]" />Disputes</div><p className="text-xs text-zinc-500 mt-1">Log a ruling so it's on record for after the game.</p></div><div className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">Dispute note for Q{Number(currentIndex) + 1}</p><textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Example: Team argued spelling, accepted after clarification." className="min-h-20 w-full resize-none rounded-md border border-white/10 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" /><Button size="sm" onClick={saveNote} disabled={!note.trim()} className="mt-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100">Save Note</Button></div><div className="space-y-2 max-h-96 overflow-y-auto">{disputeNotes.map((item) => <div key={item.id} className="rounded-md bg-zinc-950/60 p-2 text-xs text-zinc-400"><p className="text-zinc-200 line-clamp-1">Q{Number(item.questionIndex) + 1}: {item.questionText}</p><p>{item.note}</p></div>)}{!disputeNotes.length && <p className="text-xs text-zinc-600 text-center">Dispute notes stay here for post-game review.</p>}</div></CardContent></Card>;
};

const FairPlayPanel = ({ leaderboard, fairPlayStats, openScoreModal }) => {
  const flagged = leaderboard
    .map((team) => ({ team, stats: fairPlayStats.get(team.id) || {} }))
    .filter(({ stats }) => (stats.flags?.length || 0) > 0 || Number(stats.lateCorrect || 0) > 0 || Number(stats.correctStreak || 0) >= 3);
  return <Card className="glass-card"><CardContent className="p-4 space-y-4"><div><div className="flex items-center gap-2 text-white font-semibold"><AlertTriangle size={18} className="text-amber-300" />Fair Play</div><p className="text-xs text-zinc-500 mt-1">Streaks and late-correct answers worth a second look.</p></div><div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">{flagged.map(({ team, stats }) => <div key={team.id} className="rounded-lg border border-white/10 bg-zinc-950/60 p-3"><div className="mb-2 flex items-center justify-between gap-2"><span className="font-semibold text-sm truncate">{team.name}</span><span className="font-black text-[#71E0DC]">{Number(team.score || 0)}</span></div>{Boolean(stats.flags?.length) && <div className="mb-2 flex flex-wrap gap-1">{stats.flags.map((flag) => <span key={flag} className="rounded-full bg-red-400/15 px-2 py-0.5 text-[11px] font-bold text-red-200">{flag}</span>)}</div>}<div className="mb-2 grid grid-cols-2 gap-1 text-[11px] text-zinc-500"><span>Streak {stats.correctStreak || 0}</span><span>Late {stats.lateCorrect || 0}</span></div><Button size="sm" variant="outline" onClick={() => openScoreModal(team.id)} className="h-7 border-white/10 text-zinc-300 hover:text-white">Adjust Score</Button></div>)}{!flagged.length && <p className="text-xs text-zinc-500 text-center py-6">No fair-play flags yet. Streaks and late-correct answers will show up here as the game runs.</p>}</div></CardContent></Card>;
};

const optionToneClasses = {
  correct: "border-emerald-500/45 bg-emerald-500/10 text-emerald-300 font-semibold",
  incorrect: "border-rose-500/40 bg-rose-500/10 text-zinc-300",
  neutral: "border-white/10 bg-zinc-900/70 text-zinc-200 hover:border-white/25",
};

// The grading UI for a question's answers, rendered as the same rows that
// display the options -- there is no separate "answers submitted" section.
const AnswerRows = ({ question, players, answers, fairPlayStats, gradedAnswers, markAnswer, addManualAnswer, editWager, wagerMode, pointsPerQuestion, setMode, isReviewing, showAnswer }) => {
  const [manualTeamIds, setManualTeamIds] = useState([]);
  const [manualAnswer, setManualAnswer] = useState("");
  const [manualWager, setManualWager] = useState("");
  const [manualOpen, setManualOpen] = useState(false);

  const submittedPlayerIds = new Set(answers.map((answer) => answer.playerId));
  const submittedCount = submittedPlayerIds.size;
  const wageredCount = answers.filter(hasSubmittedWager).length;
  const playerCount = players.length;
  const allSubmitted = playerCount > 0 && submittedCount >= playerCount;
  const waitingPlayers = players.filter((player) => !submittedPlayerIds.has(player.id));
  const lateCorrectIds = new Set(answers.filter((answer) => gradedAnswers[answerKey(answer)]?.status === "correct" && isLateSubmission(answer)).map((answer) => answer.playerId));
  const patternIds = new Set([...fairPlayStats.entries()].filter(([, stats]) => stats.flags.length > 0).map(([teamId]) => teamId));
  const normalize = (value) => normalizeAnswerText(String(value || "").trim()) || String(value || "").trim().toLowerCase();
  const acceptedKey = normalize(question.answer);

  let rows;
  if (question.type === "multiple_choice" && question.options.length > 0) {
    rows = question.options.map((optionText, optionIndex) => ({
      key: normalize(optionText) || `option-${optionIndex}`,
      letter: String.fromCharCode(65 + optionIndex),
      label: optionText,
      answers: answers.filter((answer) => normalize(answer.answer) === normalize(optionText)),
    }));
  } else if (question.type === "true_false") {
    rows = ["True", "False"].map((label) => ({ key: normalize(label), letter: null, label, answers: answers.filter((answer) => normalize(answer.answer) === normalize(label)) }));
  } else {
    const groups = new Map();
    answers.forEach((answer) => {
      const label = String(answer.answer || "Blank").trim() || "Blank";
      const key = normalize(label) || label.toLowerCase();
      if (!groups.has(key)) groups.set(key, { key, letter: null, label, answers: [] });
      groups.get(key).answers.push(answer);
    });
    // Keep the accepted answer visible even if nobody submitted it, once revealed.
    if (showAnswer && acceptedKey && !groups.has(acceptedKey)) groups.set(acceptedKey, { key: acceptedKey, letter: null, label: question.answer, answers: [] });
    rows = [...groups.values()].sort((a, b) => b.answers.length - a.answers.length || a.label.localeCompare(b.label));
  }

  const toggleRow = (row) => {
    if (!row.answers.length) return;
    const allCorrect = row.answers.every((answer) => gradedAnswers[answerKey(answer)]?.status === "correct");
    row.answers.forEach((answer) => markAnswer(answer, allCorrect ? "incorrect" : "correct"));
  };

  const quickAnswers = question.type === "true_false" ? ["True", "False"] : question.type === "multiple_choice" ? question.options || [] : [];
  const toggleManualTeam = (teamId) => {
    setManualTeamIds((current) => current.includes(teamId) ? current.filter((id) => id !== teamId) : [...current, teamId]);
  };
  // Lets the host add the same answer for a whole cluster of teams at once
  // (e.g. four tables all shout the same wrong guess) instead of repeating
  // the team-answer-wager entry per team.
  const submitManual = (value = manualAnswer) => {
    if (!manualTeamIds.length) return toast.error("Select at least one team");
    manualTeamIds.forEach((playerId) => addManualAnswer({ playerId, answer: value, wagerAmount: manualWager, silent: true }));
    const firstTeam = players.find((player) => player.id === manualTeamIds[0]);
    toast.success(manualTeamIds.length === 1 ? `Added answer for ${firstTeam?.name || "team"}` : `Added answer for ${manualTeamIds.length} teams`);
    setManualAnswer("");
    setManualWager("");
    setManualTeamIds([]);
  };

  return <div>
    <div className="grid gap-2 mb-3">
      {rows.map((row) => {
        const hasAnswers = row.answers.length > 0;
        const allCorrect = hasAnswers && row.answers.every((answer) => gradedAnswers[answerKey(answer)]?.status === "correct");
        const anyIncorrect = hasAnswers && row.answers.some((answer) => gradedAnswers[answerKey(answer)]?.status === "incorrect");
        const matchesAccepted = Boolean(acceptedKey) && row.key === acceptedKey;
        // Once revealed, an option with no submissions still shows correct/incorrect
        // by comparing it to the accepted answer -- correctness never waits on someone
        // having picked it.
        const tone = hasAnswers ? (allCorrect ? "correct" : anyIncorrect ? "incorrect" : "neutral") : showAnswer && acceptedKey ? (matchesAccepted ? "correct" : "incorrect") : "neutral";
        const wagerTotal = row.answers.filter(hasSubmittedWager).reduce((sum, answer) => sum + Number(answer.wagerAmount || 0), 0);
        const dim = !hasAnswers && tone !== "correct";
        return <button key={row.key} type="button" disabled={!hasAnswers || isReviewing} onClick={() => toggleRow(row)} className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition disabled:cursor-default ${optionToneClasses[tone]} ${dim ? "opacity-60" : ""}`}>
          {row.letter && <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${tone === "correct" ? "bg-emerald-400/25 text-emerald-300" : tone === "incorrect" ? "bg-rose-400/20 text-rose-300" : "bg-white/10 text-zinc-400"}`}>{row.letter}</span>}
          <span className="min-w-0 flex-1 truncate">{row.label}</span>
          {wagerMode && wagerTotal > 0 && <span className="shrink-0 rounded-full bg-purple-400/15 px-2 py-0.5 text-[10px] font-bold text-purple-200">{wagerTotal} pts at stake</span>}
          <span className="flex shrink-0 -space-x-2">
            {row.answers.slice(0, 4).map((answer) => <span key={answer.playerId} title={`${answer.playerName || "Team"}${hasSubmittedWager(answer) ? ` - wager ${Number(answer.wagerAmount || 0)}` : ""}${answer.manuallyEntered ? " - manual" : ""}`} className={`flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-zinc-950 text-[9px] font-black ${answer.manuallyEntered ? "bg-[#71E0DC] text-zinc-950" : lateCorrectIds.has(answer.playerId) ? "bg-purple-200 text-zinc-950" : patternIds.has(answer.playerId) ? "bg-red-200 text-zinc-950" : "bg-zinc-700 text-zinc-100"}`}>{String(answer.playerName || "?").slice(0, 1).toUpperCase()}</span>)}
            {row.answers.length > 4 && <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-zinc-950 bg-zinc-700 text-[9px] font-black text-white">+{row.answers.length - 4}</span>}
          </span>
          <span className={`shrink-0 min-w-[16px] text-right text-[11px] font-bold ${tone === "correct" ? "text-emerald-300" : tone === "incorrect" ? "text-rose-300" : "text-zinc-500"}`}>{row.answers.length}</span>
          <span className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded ${tone === "correct" ? "bg-emerald-400/20 text-emerald-300" : tone === "incorrect" ? "bg-rose-400/20 text-rose-300" : "bg-white/5 text-zinc-600"}`}>{tone === "correct" ? <CheckCircle size={13} /> : tone === "incorrect" ? <XCircle size={13} /> : null}</span>
        </button>;
      })}
      {!rows.length && <p className="text-xs text-zinc-500 text-center py-4">Answers for the current question will appear here.</p>}
    </div>

    {manualOpen ? <div className="mb-3 rounded-lg border border-white/10 bg-zinc-950/60 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Manual answer</p>
        <Button size="sm" variant="ghost" onClick={() => setManualOpen(false)} className="h-7 px-2 text-xs text-zinc-400 hover:text-white">Collapse</Button>
      </div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-zinc-600">{manualTeamIds.length > 0 ? `Teams (${manualTeamIds.length} selected)` : "Teams (tap to select, pick more than one to bulk-add)"}</p>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {players.map((player) => {
          const selected = manualTeamIds.includes(player.id);
          return <button key={player.id} type="button" onClick={() => toggleManualTeam(player.id)} className={`rounded-full border px-2.5 py-1 text-xs font-bold transition ${selected ? "border-[#71E0DC]/60 bg-[#71E0DC]/20 text-[#71E0DC]" : "border-white/10 bg-zinc-900 text-zinc-400 hover:text-white"}`}>{player.name || "Team"}</button>;
        })}
        {!players.length && <p className="text-xs text-zinc-500">Add a team first.</p>}
      </div>
      <div className={`grid gap-2 ${wagerMode ? "grid-cols-[1fr_76px]" : "grid-cols-1"}`}>
        <input value={manualAnswer} onChange={(event) => setManualAnswer(event.target.value)} onKeyDown={(event) => event.key === "Enter" && submitManual()} placeholder="Answer" className="h-9 min-w-0 rounded-md border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" />
        {wagerMode && <input value={manualWager} onChange={(event) => setManualWager(event.target.value)} onKeyDown={(event) => event.key === "Enter" && submitManual()} placeholder="Wager" type="number" min="0" className="h-9 min-w-0 rounded-md border border-white/10 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" />}
      </div>
      {quickAnswers.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{quickAnswers.map((option) => <button key={option} type="button" onClick={() => { setManualAnswer(option); submitManual(option); }} className="rounded-full border border-white/10 bg-zinc-900 px-2.5 py-1 text-xs font-bold text-zinc-200 hover:border-[#71E0DC]/50">{option}</button>)}</div>}
      <Button size="sm" onClick={() => submitManual()} className="mt-2 h-8 w-full gradient-btn">{manualTeamIds.length > 1 ? `Add for ${manualTeamIds.length} Teams` : "Add Manual Answer"}</Button>
    </div> : <Button size="sm" variant="outline" onClick={() => setManualOpen(true)} className="mb-3 h-9 w-full border-white/10 text-zinc-300 hover:text-white"><Plus size={14} className="mr-2" />Manual Answer</Button>}

    {waitingPlayers.length > 0 && <p className="mb-2 text-[11px] text-zinc-500">Waiting on <b className="font-semibold text-amber-300">{waitingPlayers.map((player) => player.name || "Team").join(", ")}</b></p>}
    {wageredCount > 0 && <div className="mb-2 rounded-lg border border-purple-400/25 bg-purple-400/10 p-2.5"><p className="mb-1.5 text-xs font-bold text-purple-200">Wagers submitted: {wageredCount}/{submittedCount || 0}</p><div className="flex flex-wrap gap-1.5">{answers.filter(hasSubmittedWager).map((answer) => <WagerChip key={answer.playerId} answer={answer} disabled={isReviewing || !editWager} onSave={(value) => editWager(answer.playerId, answer.questionIndex, value)} />)}</div></div>}
    {lateCorrectIds.size > 0 && <div className="mb-2 rounded-lg border border-purple-400/30 bg-purple-400/10 p-2.5 text-xs font-bold text-purple-200">{lateCorrectIds.size} late correct answer{lateCorrectIds.size === 1 ? "" : "s"} flagged</div>}

    <div className="mt-1 flex items-center justify-between gap-2">
      <p className="text-[11px] text-zinc-600">{allSubmitted ? "All answers are in" : `${submittedCount}/${playerCount || 0} submitted`} &middot; {wagerMode ? "Correct adds wager, incorrect subtracts wager" : `Correct: +${pointsPerQuestion}`}</p>
      <Button size="sm" variant="outline" onClick={() => setMode("leaderboard")} className="h-7 border-white/10 text-zinc-300 hover:text-white"><Trophy size={13} className="mr-1.5" />Leaderboard</Button>
    </div>
  </div>;
};

const LeaderboardPanel = ({ leaderboard, teamName, teamScore, setTeamName, setTeamScore, addTeam, openScoreModal, removeTeam, toggleTeamDropped, showLeaderboard, fairPlayStats, attendance, updateNonPlayers, updateTeamHeadcount }) => {
  const sorted = [...leaderboard].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
  const teamHeadcounts = attendance?.teamHeadcounts || {};
  const nonPlayers = normalizeHeadcount(attendance?.nonPlayers);
  const playingTotals = sorted.reduce((sum, team) => { const hc = normalizeHeadcount(teamHeadcounts[team.id]); sum.adults += Number(hc.adults) || 0; sum.kids += Number(hc.kids) || 0; return sum; }, { adults: 0, kids: 0 });
  const barTotals = { adults: playingTotals.adults + (Number(nonPlayers.adults) || 0), kids: playingTotals.kids + (Number(nonPlayers.kids) || 0) };
  return <Card className="glass-card"><CardContent className="p-3"><div className="flex items-center justify-between gap-2 mb-3"><div className="flex items-center gap-2 text-white font-semibold"><Trophy size={18} className="text-amber-300" />Leaderboard</div><Button size="sm" variant="outline" onClick={showLeaderboard} className="h-8 border-white/10 text-zinc-300 hover:text-white">Show</Button></div><div className="mb-3 rounded-md border border-white/10 bg-zinc-950/60 px-3 py-2"><span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2"><Users size={13} />Not playing (bar only)</span><div className="grid grid-cols-2 gap-2 mb-2"><label className="block text-[11px] text-zinc-500">Adults<input type="number" min="0" value={nonPlayers.adults} onChange={(event) => updateNonPlayers("adults", event.target.value)} placeholder="0" className="mt-1 h-8 w-full rounded-md border border-white/10 bg-zinc-900 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" /></label><label className="block text-[11px] text-zinc-500">Kids<input type="number" min="0" value={nonPlayers.kids} onChange={(event) => updateNonPlayers("kids", event.target.value)} placeholder="0" className="mt-1 h-8 w-full rounded-md border border-white/10 bg-zinc-900 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" /></label></div><div className="flex items-center justify-between gap-2 rounded-md border border-[#71E0DC]/20 bg-[#71E0DC]/5 px-2 py-1.5"><span className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">Bar Total</span><span className="text-sm font-black text-[#71E0DC]">{barTotals.adults + barTotals.kids} <span className="text-[10px] font-semibold text-zinc-500">({barTotals.adults}A · {barTotals.kids}K)</span></span></div></div><div className="grid grid-cols-[1fr_76px_36px] gap-2 mb-3"><input value={teamName} onChange={(event) => setTeamName(event.target.value)} onKeyDown={(event) => event.key === "Enter" && addTeam()} placeholder="Team name" className="h-9 rounded-md bg-zinc-950 border border-white/10 px-3 text-sm text-white outline-none focus:border-[#71E0DC]/60" /><input value={teamScore} onChange={(event) => setTeamScore(event.target.value)} onKeyDown={(event) => event.key === "Enter" && addTeam()} placeholder="Score" type="number" className="h-9 rounded-md bg-zinc-950 border border-white/10 px-2 text-sm text-white outline-none focus:border-[#71E0DC]/60" /><Button onClick={addTeam} className="h-9 w-9 p-0 gradient-btn" aria-label="Add team"><Plus size={16} /></Button></div><div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">{sorted.map((team) => { const stats = fairPlayStats.get(team.id) || {}; const hc = normalizeHeadcount(teamHeadcounts[team.id]); return <div key={team.id} className={`rounded-md border border-white/10 bg-zinc-950/60 p-2 ${team.dropped ? "opacity-60" : ""}`}><div className="flex items-center justify-between gap-2 mb-2"><span className="flex min-w-0 items-center gap-1.5"><span className="font-semibold text-sm truncate">{team.name}</span>{team.dropped && <span className="shrink-0 rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] font-bold text-zinc-400">Dropped</span>}</span><span className="flex shrink-0 items-center gap-1.5"><input type="number" min="0" value={hc.adults} onChange={(event) => updateTeamHeadcount(team.id, "adults", event.target.value)} placeholder="0" title="Adults on this team" className="h-6 w-10 rounded border border-white/10 bg-zinc-900 px-1 text-center text-xs text-zinc-300 outline-none focus:border-[#71E0DC]/60" /><span className="text-[9px] text-zinc-600">A</span><input type="number" min="0" value={hc.kids} onChange={(event) => updateTeamHeadcount(team.id, "kids", event.target.value)} placeholder="0" title="Kids on this team" className="h-6 w-10 rounded border border-white/10 bg-zinc-900 px-1 text-center text-xs text-zinc-300 outline-none focus:border-[#71E0DC]/60" /><span className="text-[9px] text-zinc-600">K</span><span className="font-black text-[#71E0DC]">{Number(team.score || 0)}</span></span></div>{Boolean(stats.flags?.length) && <div className="mb-2 flex flex-wrap gap-1">{stats.flags.map((flag) => <span key={flag} className="rounded-full bg-red-400/15 px-2 py-0.5 text-[11px] font-bold text-red-200">{flag}</span>)}</div>}<div className="mb-2 grid grid-cols-2 gap-1 text-[11px] text-zinc-500"><span>Streak {stats.correctStreak || 0}</span><span>Late {stats.lateCorrect || 0}</span></div><div className="flex items-center justify-end gap-1"><Button size="sm" variant="outline" onClick={() => openScoreModal(team.id)} className="h-7 min-w-24 border-white/10 text-zinc-300 hover:text-white">Edit</Button><Button size="sm" variant="outline" onClick={() => toggleTeamDropped(team.id)} className="h-7 w-8 p-0 border-white/10 text-zinc-400 hover:text-amber-300" aria-label={team.dropped ? "Restore team" : "Mark team dropped"} title={team.dropped ? "Restore team" : "Mark team dropped"}>{team.dropped ? <RotateCcw size={13} /> : <UserX size={13} />}</Button><Button size="sm" variant="outline" onClick={() => removeTeam(team.id)} className="h-7 w-8 p-0 border-white/10 text-zinc-400 hover:text-red-300" aria-label="Remove team"><Trash2 size={13} /></Button></div></div>; })}{!sorted.length && <p className="text-xs text-zinc-500 text-center py-3">Teams will appear here when players join from their phones.</p>}</div></CardContent></Card>; };

const IdeasPanel = ({ ideas }) => <Card className="glass-card"><CardContent className="p-4"><div className="flex items-center justify-between gap-2 mb-4"><div className="flex items-center gap-2 text-white font-semibold"><Sparkles size={18} className="text-[#71E0DC]" />Player Ideas</div><Badge className="bg-zinc-800 text-zinc-300">{ideas.length}</Badge></div><div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">{ideas.map((idea, index) => <div key={`${idea.playerId}-${idea.submittedAt}-${index}`} className="rounded-md border border-white/10 bg-zinc-950/60 p-3"><div className="mb-2 flex items-center justify-between gap-2"><span className="text-sm font-bold text-white truncate">{idea.playerName || "Team"}</span><span className="text-[11px] text-zinc-600">{new Date(idea.submittedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span></div>{idea.category && <p className="text-sm text-[#71E0DC]"><span className="text-zinc-500">Category:</span> {idea.category}</p>}{idea.question && <p className="mt-2 text-sm text-zinc-200 whitespace-pre-wrap">{idea.question}</p>}</div>)}{!ideas.length && <p className="text-xs text-zinc-500 text-center py-6">Ideas will appear here after players submit them.</p>}</div></CardContent></Card>;

const ScoreAdjustModal = ({ modal, setModal, adjustScore, setScore }) => {
  const adjustment = Number(modal.adjustment || 0);
  const setTo = Number(modal.setTo || 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"><div className="w-full max-w-sm rounded-xl border border-white/10 bg-zinc-950 p-4 shadow-2xl"><div className="mb-4"><p className="text-xs font-bold uppercase tracking-wide text-[#71E0DC]">Manual Score</p><h3 className="text-xl font-black text-white">{modal.teamName}</h3><p className="text-sm text-zinc-500">Current score: {modal.currentScore}</p></div>{modal.answer && <div className="mb-4 rounded-md border border-white/10 bg-zinc-900/70 p-3"><p className="text-xs text-zinc-500 mb-1">Answer</p><p className="text-sm text-zinc-200 break-words">{modal.answer.answer}</p></div>}<div className="space-y-3"><label className="block text-xs text-zinc-400">Add or subtract points<input type="number" value={modal.adjustment} onChange={(event) => setModal((current) => ({ ...current, adjustment: event.target.value }))} placeholder="e.g. 25 or -10" className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-900 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label><label className="block text-xs text-zinc-400">Set total score<input type="number" value={modal.setTo} onChange={(event) => setModal((current) => ({ ...current, setTo: event.target.value }))} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-zinc-900 px-3 text-white outline-none focus:border-[#71E0DC]/60" /></label></div><div className="mt-5 flex flex-wrap justify-end gap-2"><Button variant="outline" onClick={() => setModal(null)} className="border-white/10 text-zinc-300 hover:text-white">Cancel</Button><Button onClick={() => { adjustScore(modal.teamId, adjustment); setModal(null); toast.success(`Adjusted ${modal.teamName} by ${adjustment}`); }} disabled={!adjustment} className="bg-zinc-800 text-white hover:bg-zinc-700">Apply Adjustment</Button><Button onClick={() => { setScore(modal.teamId, setTo); setModal(null); toast.success(`Set ${modal.teamName} to ${setTo}`); }} className="gradient-btn">Set Score</Button></div></div></div>;
};

const TOOL_DRAWER_TITLES = { teams: "Teams & Leaderboard", run: "Run Sheet", fairplay: "Fair Play", disputes: "Disputes", emergency: "Emergency Host Tools", feedback: "Live Feedback" };

const ToolDrawer = ({ activeDrawer, onClose, children }) => {
  const open = Boolean(activeDrawer);
  return <>
    <div onClick={onClose} className={`fixed inset-0 z-40 bg-black/55 transition-opacity ${open ? "opacity-100" : "pointer-events-none opacity-0"}`} />
    <div className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-white/10 bg-zinc-950 shadow-2xl transition-transform duration-200 ${open ? "translate-x-0" : "translate-x-full"}`}>
      <div className="flex items-center justify-between gap-3 border-b border-white/10 p-4">
        <h3 className="text-lg font-bold text-white">{TOOL_DRAWER_TITLES[activeDrawer] || "Tools"}</h3>
        <Button variant="ghost" onClick={onClose} className="h-9 w-9 p-0 text-zinc-400 hover:text-white" aria-label="Close"><XCircle size={20} /></Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{open && children}</div>
    </div>
  </>;
};

const RunSheet = ({ rounds, currentIndex, goToQuestion, answers, players, gradedAnswers, editBuild }) => <Card className="glass-card"><CardContent className="p-4"><div className="flex items-center justify-between gap-2 mb-4"><div className="flex items-center gap-2 text-white font-semibold"><MonitorPlay size={18} className="text-[#71E0DC]" />Run Sheet</div><Button size="sm" variant="outline" onClick={editBuild} className="h-8 border-white/10 text-zinc-300 hover:text-white"><Pencil size={14} className="mr-1" />Edit Build</Button></div><div className="space-y-4 max-h-[660px] overflow-y-auto pr-1">{rounds.map((round) => <section key={round.key}><button type="button" onClick={() => goToQuestion(round.startIndex)} className="w-full flex items-center justify-between text-left mb-2 px-2 py-1 rounded hover:bg-white/5"><span className="text-sm font-bold text-white">{round.name}</span><Badge className="bg-zinc-800 text-zinc-300">{round.questions.length}</Badge></button><div className="space-y-2">{round.questions.map((question, localIndex) => { const absoluteIndex = round.startIndex + localIndex; const active = absoluteIndex === currentIndex; const questionAnswers = answers.filter((answer) => Number(answer.questionIndex) === absoluteIndex); const graded = questionAnswers.map((answer) => gradedAnswers[answerKey(answer)]).filter(Boolean); const correctCount = graded.filter((item) => item.status === "correct").length; const incorrectCount = graded.filter((item) => item.status === "incorrect").length; return <button key={question.id} type="button" onClick={() => goToQuestion(absoluteIndex)} className={`w-full text-left rounded-md px-3 py-3 border transition-colors ${active ? "border-[#71E0DC]/50 bg-[#71E0DC]/10" : "border-white/5 bg-zinc-950/40 hover:bg-zinc-900"}`}><div className="flex items-start justify-between gap-3 mb-2"><div className="flex items-center gap-2 flex-wrap"><span className="text-xs text-zinc-500 font-mono">#{localIndex + 1}</span><QuestionBadge type={question.type} />{question.imageUrl && <Image size={12} className="text-amber-300" />}</div><div className="flex items-center gap-1 text-[11px]"><span className="rounded-full bg-zinc-800 px-2 py-0.5 text-zinc-300">{questionAnswers.length}/{players.length || 0}</span>{correctCount > 0 && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-300">+{correctCount}</span>}{incorrectCount > 0 && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-300">-{incorrectCount}</span>}</div></div><p className="text-xs text-zinc-300 line-clamp-2">{question.questionText}</p><p className="mt-1 text-[11px] text-zinc-600">{question.category}</p></button>; })}</div></section>)}</div></CardContent></Card>;

const QuestionBadge = ({ type }) => { const meta = typeMeta[type] || typeMeta.written; const Icon = meta.icon; return <Badge className="bg-zinc-800 text-zinc-300 text-[11px]"><Icon size={11} className={`mr-1 ${meta.color}`} />{meta.short}</Badge>; };

const EDIT_SCOPE_LABEL = { question: "this question", round: "the rest of this round", all: "all remaining questions" };

// Replaces the old window.prompt()-based editor. That one only ever wrote
// to the live pointsPerQuestion/timerSeconds state, which goToQuestion
// resets from the question's own stored value on every navigation -- so an
// edit "stuck" only until you left and came back. This calls onSave(value,
// scope), which persists into the question data itself.
// Uses the Radix-backed Popover (portal-rendered) rather than a plain
// absolutely-positioned div -- this badge lives inside a "glass-card" tile,
// and stacking a plain div's z-index doesn't actually escape a card's own
// stacking context (its backdrop-blur/opacity creates one), so a later
// sibling card in the list was painting over the popover no matter how
// high its z-index went. Rendering through a portal to the document body
// sidesteps that entirely, the same way the other dropdown menus already do.
const EditableStatBadge = ({ label, value, unit, suffix, tone, onSave, step = 5 }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [scope, setScope] = useState("question");
  useEffect(() => { if (open) { setDraft(value); setScope("question"); } }, [open, value]);
  const toneClass = {
    amber: "border-amber-400/20 bg-amber-400/15 text-amber-200 hover:border-amber-300/50",
    teal: "border-[#71E0DC]/25 bg-[#71E0DC]/12 text-[#71E0DC] hover:border-[#71E0DC]/60",
    periwinkle: "border-[#AEB2EF]/25 bg-[#AEB2EF]/15 text-[#C6C9FF] hover:border-[#AEB2EF]/60",
    purple: "border-purple-500/30 bg-purple-500/15 text-purple-200 hover:border-purple-400/60",
  }[tone] || "border-white/10 bg-zinc-900 text-zinc-300 hover:text-zinc-100";
  const save = () => {
    onSave(Math.max(0, Number(draft) || 0), scope);
    setOpen(false);
    toast.success(`${label} set to ${Math.max(0, Number(draft) || 0)}${suffix || ""} for ${EDIT_SCOPE_LABEL[scope]}`);
  };
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button type="button" className={`rounded-full border px-3 py-1.5 text-sm font-bold transition ${toneClass}`}>{label}: {value}{suffix || ""}</button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-64 border-white/10 bg-zinc-950 p-3 text-white shadow-2xl">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-zinc-500">{label}{unit ? ` (${unit})` : ""}</p>
      <div className="mb-2.5 flex items-center gap-2">
        <button type="button" onClick={() => setDraft((current) => Math.max(0, Number(current || 0) - step))} className="h-8 w-8 shrink-0 rounded-md border border-white/10 bg-zinc-900 text-zinc-300 hover:text-white">&minus;</button>
        <input type="number" value={draft} onChange={(event) => setDraft(event.target.value)} className="h-8 min-w-0 flex-1 rounded-md border border-white/10 bg-zinc-900 px-2 text-center font-mono text-white outline-none focus:border-[#71E0DC]/60" />
        <button type="button" onClick={() => setDraft((current) => Number(current || 0) + step)} className="h-8 w-8 shrink-0 rounded-md border border-white/10 bg-zinc-900 text-zinc-300 hover:text-white">+</button>
      </div>
      <select value={scope} onChange={(event) => setScope(event.target.value)} className="mb-2.5 h-8 w-full rounded-md border border-white/10 bg-zinc-900 px-2 text-xs text-zinc-300 outline-none focus:border-[#71E0DC]/60">
        <option value="question">This question only</option>
        <option value="round">Rest of this round</option>
        <option value="all">All remaining questions</option>
      </select>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-8 flex-1 border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
        <Button size="sm" onClick={save} className="h-8 flex-1 gradient-btn">Save</Button>
      </div>
    </PopoverContent>
  </Popover>;
};
// A per-team click-to-edit wager amount -- corrects a submitted wager
// (mis-heard over the mic, fat-fingered manual entry) without touching the
// team's answer text or resubmitting it as a fresh answer.
// Edits inline (swaps the badge for an input in place) rather than in an
// absolutely-positioned popover -- this sits inside QuestionStage's
// overflow-hidden card, low enough in the layout that a popover opening
// below the button was getting silently clipped, making the chip look
// unresponsive even though the click was registering.
const WagerChip = ({ answer, disabled, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(answer.wagerAmount);
  useEffect(() => { if (editing) setDraft(answer.wagerAmount); }, [editing, answer.wagerAmount]);
  const save = () => {
    onSave(Math.max(0, Number(draft) || 0));
    setEditing(false);
  };
  if (editing) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-purple-400/40 bg-zinc-950 py-1 pl-2.5 pr-1.5">
      <span className="text-[11px] font-bold text-purple-200">{answer.playerName || "Team"}</span>
      <input type="number" min="0" autoFocus value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => event.key === "Enter" && save()} className="h-6 w-16 rounded border border-white/10 bg-zinc-900 px-1 text-center text-xs text-white outline-none focus:border-[#71E0DC]/60" />
      <button type="button" onClick={save} aria-label="Save wager" className="text-emerald-300 hover:text-emerald-200"><CheckCircle size={14} /></button>
      <button type="button" onClick={() => setEditing(false)} aria-label="Cancel" className="text-zinc-500 hover:text-zinc-300"><XCircle size={14} /></button>
    </span>;
  }
  return <button type="button" disabled={disabled} onClick={() => setEditing(true)} className="rounded-full border border-purple-400/30 bg-purple-400/10 px-2.5 py-1 text-[11px] font-bold text-purple-200 transition disabled:cursor-default disabled:opacity-70 enabled:hover:border-purple-300/60">{answer.playerName || "Team"}: {Number(answer.wagerAmount || 0)}</button>;
};

const QuestionStage = ({ question, index, total, showAnswer, showFunFact, focusMode, pointsPerQuestion, timerSeconds, timeRemaining, wagerMode, wagerTiming, onUpdateSettings, onUpdateContent, onDuplicate, onAiEdit, onSaveToLibrary, branding, players, answers, fairPlayStats, gradedAnswers, markAnswer, addManualAnswer, editWager, setMode, isReviewing, hasRevealExtra, hasFunFact, hasAudio, isPlayingAudio, onToggleAudio, onRevealAnswer, onShowFunFact, startTimer, resetTimer, resetQuestion, onBackToLive, onGoLiveWithThis, categoryUsage }) => {
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFromQuestion(question));
  const [savingEdit, setSavingEdit] = useState(false);
  const meta = typeMeta[question.type] || typeMeta.written;
  const imageUrl = buildStorageUrl(question.imageUrl);
  const revealTiming = normalizeImageTiming(question.imageTiming || question.image_timing);
  const accentColor = branding?.accentColor || DEFAULT_BRANDING.accentColor;
  // This remounts (new `question.id` key) every time a different question
  // becomes live or gets reviewed -- Ask Question/Review otherwise leave the
  // scroll position wherever it was, so the card that just went live could
  // land off-screen, above or below the viewport, with no indication it
  // moved. Scroll it into view once on that mount instead of leaving the
  // host to go hunt for it.
  const cardRef = useRef(null);
  useEffect(() => { cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, []);

  const startEdit = () => { setDraft(draftFromQuestion(question)); setEditing(true); };
  const handleSaveEdit = async () => {
    if (!draft.questionText.trim() || !draft.answer.trim()) return toast.error("Question and answer can't be empty");
    setSavingEdit(true);
    const ok = await onUpdateContent({
      category: draft.category.trim() || "General",
      questionText: draft.questionText.trim(),
      answer: draft.answer.trim(),
      incorrectAnswers: question.type === "multiple_choice" ? draft.incorrectAnswers.map((item) => item.trim()).filter(Boolean).join("; ") : undefined,
      funFact: draft.funFact.trim(),
      imageUrl: draft.imageUrl.trim(),
      imageTiming: draft.imageTiming,
      audioUrl: draft.audioUrl.trim(),
    });
    setSavingEdit(false);
    if (ok) setEditing(false);
  };

  // Fun fact renders directly under the question now (not off in its own
  // block elsewhere) -- always visible to the host once a question has one,
  // dimmed until actually revealed to players/presentation.
  const funFactBox = question.funFact && <div className="mt-2 flex items-start gap-2 rounded-md border border-dashed p-2.5 text-xs leading-relaxed transition-opacity" style={{ borderColor: `${accentColor}59`, backgroundColor: `${accentColor}0F`, color: "#C7C9F5", opacity: showFunFact ? 1 : 0.6 }}><Sparkles size={13} className="mt-0.5 shrink-0" style={{ color: accentColor }} /><div><b style={{ color: accentColor }}>Fun fact:</b> {question.funFact}</div></div>;

  return <>
  <Card ref={cardRef} className={`glass-card ${isReviewing ? "border-amber-400/40" : "border-rose-400/40"} ${focusMode ? "w-full" : ""}`}>
    <CardContent className={focusMode ? "p-6 lg:p-8" : "p-3.5"}>
      {isReviewing && <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-100"><Eye size={13} className="shrink-0 text-amber-300" /><span className="flex-1">Reviewing this question &mdash; players and the presentation screen still see the live question.</span>{onBackToLive && <Button size="sm" variant="outline" onClick={onBackToLive} className="h-7 border-amber-300/30 text-amber-100 hover:text-white">Back to Live</Button>}{onGoLiveWithThis && <Button size="sm" onClick={onGoLiveWithThis} className="h-7 bg-amber-300 text-zinc-950 hover:bg-amber-200">Go Live With This Question</Button>}</div>}
      {editing ? <>
        <QuestionEditFields type={question.type} draft={draft} setDraft={setDraft} categoryUsage={categoryUsage} />
        <div className="mt-2 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-8 border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
          <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit} className="h-8 gradient-btn">{savingEdit ? "Saving..." : "Save"}</Button>
        </div>
      </> : <>
        <div className="flex items-start gap-3">
          {imageUrl && revealTiming !== "after_answer" && <img src={imageUrl} alt="Question media" className={`shrink-0 rounded-lg border border-white/10 object-cover ${focusMode ? "h-28 w-28" : "h-16 w-16"}`} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-zinc-500">{meta.label}{isBonusQuestion(question) && <span className="ml-1.5 text-amber-300" title="Bonus question">&#9733;</span>}<br />#{index + 1} / {total} &middot; {question.category}</p>
              <div className="flex shrink-0 items-center gap-2">
                {wagerMode && <button type="button" onClick={() => onUpdateSettings({ wagerTiming: wagerTiming === "after_answer" ? "before_answer" : "after_answer" }, "question", question)} className="rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 text-[11px] font-bold text-purple-200 hover:border-purple-400/60" title="Wager timing">{wagerTiming === "after_answer" ? "After Answer" : "Before Answer"}</button>}
                {!isReviewing && <span className="flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[11px] font-bold text-rose-200"><span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-pulse" />LIVE</span>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" title="More actions" aria-label="More actions" className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:text-white"><MoreVertical size={15} /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="border-white/10 bg-zinc-950 text-zinc-100">
                    <DropdownMenuItem onClick={startEdit} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Pencil size={14} className="mr-2" />Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={onAiEdit} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Sparkles size={14} className="mr-2" />AI Edit</DropdownMenuItem>
                    {!isReviewing && <DropdownMenuItem onClick={resetTimer} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><RotateCcw size={14} className="mr-2" />Clear Timer</DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => setMediaModalOpen(true)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Image size={14} className="mr-2" />Media</DropdownMenuItem>
                    <DropdownMenuItem onClick={onDuplicate} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><Copy size={14} className="mr-2" />Clone</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSaveToLibrary(question)} className="cursor-pointer focus:bg-zinc-900 focus:text-white"><List size={14} className="mr-2" />Save to Library</DropdownMenuItem>
                    {resetQuestion && <DropdownMenuItem onClick={resetQuestion} className="cursor-pointer text-amber-300 focus:bg-zinc-900 focus:text-amber-200"><RefreshCw size={14} className="mr-2" />Reset Question</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <h2 className={`mt-1.5 font-bold leading-snug text-white ${focusMode ? "text-2xl lg:text-4xl" : "text-sm"}`}>{question.questionText}</h2>
            <p className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500" title="Only visible on this screen -- players and the presentation screen don't see this until you reveal"><EyeOff size={11} />Answer: {question.answer || "Not set"}</p>
            <AnswerOptionPreview question={question} />
            {funFactBox}
          </div>
        </div>
        {imageUrl && revealTiming === "after_answer" && showFunFact && <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-zinc-950"><img src={imageUrl} alt="Reveal media" className="max-h-64 w-full object-contain" /></div>}
        <div className="mt-2.5 border-t border-white/5 pt-2.5">
          <AnswerRows question={question} players={players} answers={answers} fairPlayStats={fairPlayStats} gradedAnswers={gradedAnswers} markAnswer={markAnswer} addManualAnswer={addManualAnswer} editWager={editWager} wagerMode={wagerMode} pointsPerQuestion={Number(pointsPerQuestion) || getDefaultPoints(question)} setMode={setMode} isReviewing={isReviewing} showAnswer={showAnswer} />
        </div>
        {!isReviewing && <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-2.5">
          <div className="flex flex-wrap items-center gap-2">
            {timeRemaining !== null ? (
              <button type="button" onClick={startTimer} title="Restart timer" className="flex h-8 items-center gap-1.5 rounded-md border border-[#71E0DC]/40 bg-[#71E0DC]/15 px-3 text-xs font-bold text-[#71E0DC] hover:bg-[#71E0DC]/25"><Timer size={12} />{timeRemaining}s left</button>
            ) : (
              <Button size="sm" onClick={startTimer} className="h-8 gradient-btn text-xs"><Play size={12} className="mr-1" />Start Timer</Button>
            )}
            <Button size="sm" onClick={onRevealAnswer} className={`h-8 text-xs ${showAnswer ? "bg-zinc-800 text-white hover:bg-zinc-700" : "gradient-btn"}`}>{showAnswer ? <EyeOff size={12} className="mr-1" /> : <Eye size={12} className="mr-1" />}{showAnswer ? "Hide Answer" : "Reveal Answer"}</Button>
            <Button size="sm" variant="outline" onClick={onShowFunFact} disabled={!hasRevealExtra} className="h-7 border-white/10 text-[11px] text-zinc-300 hover:text-white disabled:opacity-40"><Sparkles size={11} className="mr-1" />{showFunFact ? "Hide" : hasFunFact ? "Fun Fact" : "Media"}</Button>
            {hasAudio && <Button size="sm" onClick={onToggleAudio} className={`h-8 text-xs ${isPlayingAudio ? "bg-purple-500/20 text-purple-200 hover:bg-purple-500/30" : "bg-zinc-800 text-white hover:bg-zinc-700"}`}>{isPlayingAudio ? <Pause size={12} className="mr-1" /> : <Music size={12} className="mr-1" />}{isPlayingAudio ? "Stop Audio" : "Play Audio"}</Button>}
          </div>
          <div className="flex items-center gap-2">
            <EditableStatBadge label="Points" value={Number(pointsPerQuestion) || getDefaultPoints(question)} tone="amber" step={5} onSave={(value, scope) => onUpdateSettings({ points: value }, scope, question)} />
            <button type="button" onClick={() => onUpdateSettings({ wagerLimit: wagerMode ? 0 : 1 }, "question", question)} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${wagerMode ? "border-purple-400/50 bg-purple-500/20 text-purple-100" : "border-purple-500/30 bg-purple-500/10 text-purple-200 hover:border-purple-400/60"}`} title="Teams can wager up to whatever points they currently have -- there's no separate host-set limit">{wagerMode ? "Wager: On" : "Wager: Off"}</button>
            <EditableStatBadge label="Timer" value={Number(timerSeconds) || 0} unit="seconds" suffix="s" tone="teal" step={5} onSave={(value, scope) => onUpdateSettings({ timerSeconds: value }, scope, question)} />
          </div>
        </div>}
      </>}
    </CardContent>
  </Card>
  {mediaModalOpen && <MediaEditModal question={question} onSave={(patch) => { onUpdateSettings(patch, "question", question); setMediaModalOpen(false); }} onClose={() => setMediaModalOpen(false)} />}
  </>;
};

// Full workspace merge Phase 5. A cut-down version of BuildSession's
// MediaModal -- image URL/upload + before/after timing, audio upload/
// remove -- without its AI image-generation panel (that's part of the AI
// surface a later merge phase ports in alongside per-question AI edit).
// Always commits with scope "question" only: unlike points/timer, "apply
// this image to the rest of the round" is never what a host wants.
const MediaEditModal = ({ question, onSave, onClose }) => {
  const [imageUrl, setImageUrl] = useState(question.imageUrl || "");
  const [imageTiming, setImageTiming] = useState(normalizeImageTiming(question.imageTiming));
  const [audioUrl, setAudioUrl] = useState(question.audioUrl || "");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const handleImageFile = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      setImageUrl(await uploadQuestionMedia(file));
    } catch (error) {
      console.error("Upload image error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAudioFile = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      setAudioUrl(await uploadQuestionAudio(file));
    } catch (error) {
      console.error("Upload audio error:", error);
      toast.error(error.message || "Failed to upload audio");
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSave = () => onSave({ imageUrl: imageUrl.trim(), imageTiming, audioUrl: audioUrl.trim() });

  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-8 md:pt-14 overflow-y-auto">
    <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#17181c] p-6 shadow-2xl shadow-black/60 relative">
      <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" aria-label="Close media"><X size={18} /></button>
      <h2 className="mb-5 text-xl font-bold text-white">Media</h2>
      <div className="mb-4 flex gap-2">
        <button type="button" onClick={() => setImageTiming("initial")} className={`h-9 flex-1 rounded-md border text-sm font-semibold ${imageTiming !== "after_answer" ? "border-[#71E0DC]/50 bg-[#71E0DC]/15 text-[#71E0DC]" : "border-white/10 text-zinc-400 hover:text-white"}`}>Show with question</button>
        <button type="button" onClick={() => setImageTiming("after_answer")} className={`h-9 flex-1 rounded-md border text-sm font-semibold ${imageTiming === "after_answer" ? "border-[#71E0DC]/50 bg-[#71E0DC]/15 text-[#71E0DC]" : "border-white/10 text-zinc-400 hover:text-white"}`}>Show after answer</button>
      </div>
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Link className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} placeholder="Image URL" className="h-10 w-full rounded-md border border-white/10 bg-zinc-950/50 pl-9 pr-3 text-white outline-none focus:border-[#71E0DC]/60" />
        </div>
      </div>
      <label className={`mb-2 flex h-40 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed transition-colors ${uploadingImage ? "border-zinc-600 bg-zinc-950/20" : "border-zinc-600 bg-zinc-950/20 hover:border-[#71E0DC]"}`}>
        {uploadingImage ? <Loader2 size={22} className="animate-spin text-[#71E0DC]" /> : imageUrl ? <img src={imageUrl} alt="Question media preview" className="max-h-32 max-w-full rounded object-contain" /> : <><Upload size={22} className="mb-2 text-white" /><p className="text-sm font-medium text-white">Drop an image or click to upload</p></>}
        <input type="file" accept="image/*" className="hidden" disabled={uploadingImage} onChange={(event) => handleImageFile(event.target.files?.[0])} />
      </label>
      {imageUrl && <Button type="button" variant="ghost" onClick={() => setImageUrl("")} className="mb-4 text-zinc-400 hover:text-white">Remove image</Button>}
      <div className="my-5 h-px bg-white/10" />
      <label className="mb-3 flex items-center gap-2 text-zinc-300"><Music size={16} />Audio clip</label>
      <label className={`flex h-16 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed transition-colors ${uploadingAudio ? "border-zinc-600 bg-zinc-950/20" : "border-zinc-600 bg-zinc-950/20 hover:border-[#71E0DC]"}`}>
        {uploadingAudio ? <Loader2 size={18} className="animate-spin text-[#71E0DC]" /> : <><Upload size={16} className="text-white" /><p className="text-sm font-medium text-white">{audioUrl ? "Replace audio clip" : "Click to upload an audio clip"}</p></>}
        <input type="file" accept="audio/*" className="hidden" disabled={uploadingAudio} onChange={(event) => handleAudioFile(event.target.files?.[0])} />
      </label>
      {audioUrl && <div className="mt-3"><audio controls src={audioUrl} className="h-10 w-full" /><Button type="button" variant="ghost" onClick={() => setAudioUrl("")} className="mt-2 text-zinc-400 hover:text-white">Remove audio</Button></div>}
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-zinc-300 hover:text-white">Cancel</Button>
        <Button type="button" onClick={handleSave} disabled={uploadingImage || uploadingAudio} className="gradient-btn">Save</Button>
      </div>
    </div>
  </div>;
};

export default HostSession;

